// AI_CONTEXT: {
//   "docs": [
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-024-prompt-resolution-skips-credits-step"
//   ],
//   "purpose": "Shopping cart model: cart state, checkout step requirements/progress, credits, payment intents and cart API calls",
//   "version": "1.0.0",
//   "priority": "core",
//   "stability": "stable"
// }
define("ShoppingCart", [`Model`, `module`, `lodash`], function (Super, Self, _) {
	const DUMMY_RECAPTCHA = `dummytext`;
	const STEPS = {
		// the potentially required steps of checkout
		token: true,
		add_ons: false,
		q_config: false,
		logged_in: false,
		required_forms: false,
		phone: false, // whether a phone number is requested (required or optional) to submit the order
		shipping_address: false, // whether a shipping address is required to submit the order
		payment_plan: false, // used for "force payment plan"
		order_protection: false, // whether opting in or out of insurance is required
		payment: false, // whether a payment method is required to submit the order
		payment_terminal: false, // whether payment terminal step is required
		address_match: false, // whether the billing and shipping addresses must match
		optional_forms: false,
		credits: false, // whether credits step is applicable. Note: CheckoutSteps has an extra check to make sure the user is logged in before progressing
	};
	const COMBINE_PRIMARY_WAITLIST_COUNTS = {
		combine: `COMBINE_PRIMARY_WAITLIST_COUNTS`,
		separate: `SEPARATE_PRIMARY_WAITLIST_COUNTS`,
	};
	const DEFAULT_CART = {
		// the default 'starting' cart; it will also be user to overwrite existing values when the cart is 'reset'
		agentCheckoutPaymentOptions: null, // to be populated by the "reserve shopping cart" response
		checkoutMode: null, // AGENT | GUEST | USER
		checkoutType: null, // PURCHASE | UPGRADE
		completed: false, // whether the purchase has been completed
		checkoutEnabled: true, // whether checkout is allowed; when false, checkout button is disabled
		features: {
			payment_plan: false, // enabling payment plan is allowed
			promo_code: false, // entering a promo is allowed
			will_call_substitution: false, // allow substituting 'will call' for international shipping orders
			payment_plan_optional: false, // used in cases where only a URL param is forcing PP
		},
		copy: {
			add_on_blurb: null,
			order_complete_blurb: null,
		},
		requirements: _.cloneDeep(STEPS), // a list of potential 'requirements' the user must complete to purchase
		progress: _.cloneDeep(STEPS), // a map of user progress, to compare to the requirements map
		eventId: null, // the associated event id
		currency: null, // to be populated based on the initiated event
		saleItems: [], // to be populated by the "reserve shopping cart" response
		sales: [], // to be populated with the list of available sales
		holds: [], // to be populated with the list of available holds
		products: [], // the full list of visible products
		subtotal: null, // the locally calculated sum of item prices
		total: null, // the total cost of the cart
		orderProtection: null, // cost of optional order protection (if opted-in)
		unlockedHolds: [], // updated when a user applies access codes
		saleUnlocks: [], // updated when a user applies access codes
		tenderTypeOptions: [`CREDIT_CARD`],
		tenderType: `NONE`, // NONE | CREDIT_CARD
		token: null, // the unique token provided by the server
		authenticated: null, // used to double-check the user is logged in when proceeding to checkout
		expiration: null, // a timestamp of when the cart expires
		queue_settings: null, // 	s any queue details, if the tickets are sold out
		promoCode: null, // an optional promo code applied to the order
		paymentSchedule: null, // a breakdown of what the user pays if using a payment plan
		usePaymentPlan: false, // whether the user has enabled payment plan
		paymentPlanFee: null, // an additional payment plan fee; to be displayed on checkout
		paymentPlanTotal: null, // the value to use for order total, if payment plan is enabled
		deliveryFee: null, // an additional delivery fee; to be displayed on checkout
		feesAndTaxes: null, // a value that's used for when all-in pricing is disabled
		fee_and_tax_breakdown: null, // a value that's used for when all-in pricing is disabled
		discount: null, // a discount applied to the current cart; displayed in checkout summary
		nonce: null, // used for submitting a new payment method
		paymentMethodToken: null, // used for submitting an existing payment method
		billingAddress: null, // used to hold the address for authorizing the payment
		shippingAddress: null, // used to hold a shipping address, if required
		savePaymentMethod: true, // whether to save a new payment method
		useAsDefault: true, // whether to use a new payment method as the new default
		orderId: null, // holds the returned orderId of a successful submission
		blurb: null, // used to hold any special blurb text, to be presented to the user on order completion
		additionalFees: null, // used to hold any additional ees to display to the user
		checkoutLabel: null, // used to dictate the label to show on the checkout and scroll-to buttons
		quantity: 0, // a quick reference to the current total cart quantity
		eventCategoryName: null, // pass-through from the event object
		isPaymentPlanEligible: false, // whether the current cart is eligible for payment plan
		privateLinkToken: null, // whether a plt was passed in the URL (single-use token to buy a private sale)
		paymentProcessorType: `STRIPE`,
		paymentIntent: null,
		representativesSelectedStorageKey: ``, // key for storing selected representatives in session storage
		requireSavedPayment: false,
		violations: [], // used for keeping track of broken rules when selecting seats
		maxTicketsAllowed: null, // used for tracking the overall max number of tickets the user can buy
		guestCheckoutBehavior: null, // ALL | AGENT_ONLY | GUEST | NONE
		guestCheckoutDetails: null, // an optional ability to pre-populate and lock guest email
		orderProtectionConfiguration: null, // optional order protection config
		orderProtectionAdded: null, // null | true | false
		paymentTerminalStatus: null, // null | PENDING | COMPLETED | CANCELED | FAILED
		paymentTerminalStatusMessage: null,
		skipUserForBoxOffice: false,
		agentCheckoutAutoSendPhoneE164: null, // SMS phone for current agent checkout only (not session-persisted)
		agentCheckoutAutoSendPhoneDisplay: null,
		agentCheckoutTerminalEnabled: null, // AGENT_CHECKOUT_TERMINAL group feature, passed from Studio via postMessage
		agentCheckoutSmsEnabled: null, // smsTicketDelivery + RECORDS_ORDERS RESEND, passed from Studio via postMessage
		stripeTerminalReader: null, // selected Stripe Terminal reader info
	};
	const upgradesFindBestBlockOptions = { key: `upgradesFindBest`, message: `Finding Best Available` };
	const stateOverrides = {};
	let buyRequestPlaced = false;
	let setUserPromise = null;
	let expirationTimerId;
	let cartDeferred;

	return Super.inherit({
		namespace: Self.id,
		singleton: true, // utilty class, only one instance
		requires: [
			`Agent`,
			`AgentPriceCode`,
			`Countdown`,
			`DOM`,
			`Events`,
			`Groups`,
			`Insights`,
			`Locale`,
			`Orders`,
			`ParentFrame`,
			`Payment`,
			`PriceLevels`,
			`Products`,
			`ReservedSeating`,
			`Storage`,
			`Time`,
			`Templates`,
			`Unlocks`,
			`User`,
		],
		constructor(setup) {
			const { Agent, App, Config, Locale, Storage, Time, Unlocks, User, data, promises } = this;
			const agentHandle = App.getCookie(`agentHandle`);

			this.assign(data, {
				agentHandle,
				cart: null, // keeps track of the cart state
				checkoutActive: false,
				checkoutMode: `USER`,
				custom_forms: [],
				enablePersistentCheckout: true,
				event: null, // cached reference to the specified event
				ignoreConsecutiveSeatingViolations: false, // used for cases where F.B.A. has returned split seats
				incomplete_promo: null,
			});

			Unlocks.get({
				fireImmediately: true,
				subscribe: (unlocks) => {
					// keep track of the access codes
					const { eventUnlocks } = unlocks;

					this.assign(data, { eventUnlocks });
					this.syncUnlocks();
				},
			});

			Locale.get({
				fireImmediately: true, // fire the below handler immedidately
				subscribe: (localeInfo) => {
					// keep track of the locale
					data.selectedLocale = localeInfo.selected;
					this.updateLocaleSetting();
				},
			});

			// Update if the price code changes
			this.AgentPriceCode.get({
				subscribe: async () => {
					const { data } = this;
					const { event: existingEvent } = data;

					// only sync if there is an existing event
					if (existingEvent) {
						await this.syncAgentPriceCodeProducts();
						await this.syncUnlocks(); // TODO: jlu-Not sure this is needed
					}
				},
			});

			cartDeferred = this.Deferred();
			const { promise } = cartDeferred;
			promises.cart = promise;

			this.when({
				promise,
				then: () => {
					// cart is ready, listen for messages from Studio (used for agent checkout)
					const { ENVIRONMENT } = Config;
					const isLocal = ENVIRONMENT === `dev`;
					const { location } = window;
					const { origin: siteOrigin } = location;

					window.addEventListener(
						`message`,
						({ data: evt, origin }) => {
							const { action, value } = evt;

							if (!isLocal) {
								const tixrDomain = origin.endsWith(`tixr.com`);
								const studioDomain = origin.indexOf(`studio`) > -1;

								if (!(tixrDomain && studioDomain)) {
									return; // not from Studio, ignore
								}
							} else if (origin === siteOrigin) {
								return; // not from Studio, ignore
							}

							// messages from Box Office opening this page via iframe
							switch (action) {
								case `clear-guest-account`:
									data.cart.guestCheckoutDetails = null;
									this.reconcile();
									break;

								case `close-agent-checkout`:
									Agent.clearSession();
									Storage.removeSessionStorage({ key: data.cart.representativesSelectedStorageKey });
									Storage.removeSessionStorage({ key: `tixr-agent-checkout-auto-send` });
									this.init();
									break;

								case `set-guest-account`: {
									const { email, firstName, lastName, id } = value;
									data.cart.guestCheckoutDetails = { email, firstName, lastName, id };
									this.reconcile();
									break;
								}

								case `agent-checkout-config`: {
									data.cart.agentCheckoutTerminalEnabled = value?.terminalEnabled === true;
									data.cart.agentCheckoutSmsEnabled = value?.smsEnabled === true;
									this.reconcile();
									break;
								}

								case `stripe-terminal-update`: {
									const { selectedReader } = value;
									data.cart.stripeTerminalReader = selectedReader;
									this.reconcile();
									break;
								}

								case `prefill-serial-numbers`: {
									const { serialNumbers } = value;
									data.cart.prefilledSerialNumbers = serialNumbers;
									this.reconcile();
									break;
								}

								case `check-items-in-cart`: {
									evt.value.itemsInCart = data.cart.quantity;
									evt.force = true;

									this.ParentFrame.postMessage(evt);
								}
							}
						},
						false
					);

					const { parent } = window;
					if (this.isObject(parent) && this.isFunction(parent.postMessage)) {
						// try to notify parent Studio frame that cart is ready
						try {
							const evt = { action: `shopping-cart-ready` };
							const targetOrigin = `*`; // TODO: introduce Studio domain variable
							parent.postMessage(evt, targetOrigin);
						} catch (err) {
							this.log(`Agent Checkout: Error communicating with parent frame.`);
						}
					}
				},
			});

			const cartToken = Storage.get({ key: `cart_token` });
			const cartExpirationString = Storage.get({ key: `cart_expiration` });
			const orderTokenParam = App.getUrlParam(`stripe_redirect_token`); // Event.js uses this for order completion on stripe redirect

			if (this.isString(orderTokenParam, 1)) {
				this.clearPersistentCheckout();
			} else if (this.isString(cartToken, 1) && this.isString(cartExpirationString, 1)) {
				const cartExpiration = this.parseInt(cartExpirationString);
				const currentTime = Time.getTime();

				if (cartExpiration > currentTime) {
					data.initialCartToken = cartToken;
				} else {
					this.clearPersistentCheckout();
				}
			}

			return User.get({
				subscribe: true,
				then: (user) => {
					const { cart, event } = data;

					data.user = user;

					if (this.isObject(cart) && this.isString(cart.token, 1) && !cart.completed) {
						const { requiredAgeRestriction } = event;
						let initializePayment = true;

						// check for an age restriction
						if (this.isInt(requiredAgeRestriction, 1)) {
							const { birthday } = user;

							if (this.isArray(birthday) && birthday.length === 3) {
								const date = new Date();
								const year = date.getFullYear() - requiredAgeRestriction;
								const month = date.getMonth();
								const day = date.getDate();
								const maxDate = new Date(year, month, day);
								const birthDate = new Date(birthday[0], birthday[1] - 1, birthday[2]);
								const maxDateString = Time.format(`%Y-%M-%D`, maxDate.getTime());
								const birthDateString = Time.format(`%Y-%M-%D`, birthDate.getTime());

								if (maxDateString < birthDateString) {
									initializePayment = false;
								}
							} else {
								initializePayment = false;
							}
						}

						if (user.paymentIntent) {
							cart.paymentIntent = user.paymentIntent;
						}

						// credits came from guest user login; a fan's UserProfile has none, so don't wipe the
						// set-user cart credits on a mid-checkout re-seed (CEF-024)
						if (`creditsAvailableAmount` in user) {
							cart.creditsAvailableAmount = user.creditsAvailableAmount;
							cart.creditsApplicability = user.creditsApplicability;
						}

						cart.requirements.credits =
							this.isFinite(cart.creditsAvailableAmount) &&
							cart.creditsAvailableAmount > 0 &&
							cart.requirements.payment;

						if (initializePayment) {
							// active checkout
							const { paymentIntent, paymentProcessorType, requirements } = cart;

							if (requirements.payment && paymentProcessorType === `STRIPE` && !this.isObject(paymentIntent)) {
								return this.getPaymentIntent(); // NOTE: eventually calls reconcile(), return and skip below
							}
						}
					}

					// Sync user entitlements when user logs in to unlock any entitled sales
					// Only sync if the checkout mode is USER
					if (cart?.checkoutMode === `USER`) {
						this.syncUserEntitlements().then(() => {
							// reconcile is called by syncUserEntitlements, but call again to be safe
							this.reconcile();
						});
					} else {
						// For agent & guest checkout, just reconcile without syncing entitlements
						this.reconcile();
					}
				},
				fail: () => {
					data.user = null;
					// Clear entitlement-based unlocks when user logs out
					this.clearUserEntitlements();
				},
			});
		},

		api: {
			init: {
				async: true,
				clone: true,
				public: true,
				defaults: {
					eventId: null,
					userId: null, // used in some agent flows
					clear: true, // whether to remove all items from the cart (e.g. true on completed checkout)
					confirm: false, // whether to ask the user before proceeding
					isUpgrades: false, // whether the cart is being initialized for upgrades
					isUpgradeChildOrder: false, // whether the cart is being initialized for a child order of an upgrade
					preserveSession: false, // whether to keep the existing reserved seating session, if available
					resumeCheckout: false, // whether to try resuming if a cart token is present
					skipDelete: false, // whether to skip the DELETE call when voiding an existing cart,
					groupSaleId: null, // id of the group sale
					groupSaleUrlParam: null, // url to return user to when exiting group sale flow
				},
				async value(context, options) {
					const { Unlocks, App, Countdown, Events, Payment, ReservedSeating, User, XHR, data } = this;
					const { agentHandle } = data;
					const { confirm, isUpgrades, isUpgradeChildOrder, preserveSession, resumeCheckout } = options;
					const checkoutType = isUpgrades ? `UPGRADE` : `PURCHASE`;
					const purchaseFlow = isUpgrades ? `Upgrade` : `Purchase`;
					let { cart } = data;
					const existingReader = cart?.stripeTerminalReader;
					const existingAgentCheckoutTerminalEnabled = cart?.agentCheckoutTerminalEnabled;
					const existingAgentCheckoutSmsEnabled = cart?.agentCheckoutSmsEnabled;
					let hasToken = this.isObject(cart) && this.isString(cart.token);
					let eventId;

					if (hasToken && resumeCheckout) {
						// for cases where the cart is already populated & active
						// e.g. navigating away from and returning to the event page
						const { completed, eventId, saleItems } = cart;
						const hasMatchingEvent = eventId === options.eventId; // same event as active checkout
						const hasItems = this.isArray(saleItems, 1); // items from the server in cart

						if (hasMatchingEvent && hasItems) {
							if (completed) {
								// cart is active but completed, show the order complete overlay
								App.loadOverlay({
									type: `OrderComplete`,
									setup: { theme: `LIGHT`, overlayMode: `modal`, style: `ux4` },
									replace: true,
								});
							} else {
								this.checkout();
							}

							return this.clone(cart);
						}
					}

					data.isUpgrades = isUpgrades;
					data.isUpgradeChildOrder = isUpgradeChildOrder;
					data.skipOrderProtection = false; // used to keep track of widget failure
					data.enablePersistentCheckout = true; // reset to default
					data.ignoreConsecutiveSeatingViolations = false; // reset to default
					data.purchaseFlow = purchaseFlow;
					data.groupSaleId = options.groupSaleId;
					data.groupSaleUrlParam = options.groupSaleUrlParam;

					if (confirm && hasToken) {
						const heading = `Exiting checkout will result in your shopping cart being cleared.`;
						const message = `Do you want to proceed?`;
						const confirmExit = await App.askUser({
							message,
							heading,
							overlayMode: `modal`,
							style: `ux4`,
							theme: `LIGHT`,
							addHead: false,
							size: `small`,
						});

						if (!confirmExit) {
							return; // user cancelled, skip the rest of the function
						}
					}

					Countdown.clearExpiration();

					const [OrderProtection] = await this.require({ deps: [`OrderProtection`] });
					OrderProtection.clearSession();

					// clear existing stripe elment information if it exists
					Payment.clearStripeElementsInfo();

					// reset any existing timer
					this.setExpiration(null);

					if (this.isInt(options.eventId)) {
						// event id was manually passed
						eventId = options.eventId;
					}

					if (this.isObject(cart)) {
						// there's an existing cart
						if (this.isUndefined(eventId) && this.isInt(cart.eventId)) {
							eventId = cart.eventId;
						}

						if (this.isString(cart.token, 1) && !cart.completed) {
							// current cart is incomplete

							if (!options.skipDelete) {
								// NOTE: don't thread into the promise chain, assume success
								XHR.ajax({
									// void the existing token
									url: `/api/events/${cart.eventId}/shoppingcarts/${cart.token}`,
									ignoreErrors: true,
									type: `DELETE`,
									block: false,
								});
							}

							this.leaveUpgradesMobileWebview();

							// NOTE: DELETE call above invalidates reserved seating session
							if (!preserveSession) {
								ReservedSeating.endSession();
							}
						} else {
							// NOTE: don't thread into the promise chain, assume success
							if (!preserveSession) {
								ReservedSeating.endSession();
							}
						}

						if (cart.checkoutMode !== `USER`) {
							// guest or agent checkout

							if (this.isObject(data.user)) {
								User.logOut();
							}
						}
					}

					if (this.isUndefined(eventId)) {
						throw new Error(`Missing event id`);
					}

					const event = await Events.get({
						by: `id`,
						id: eventId,
						isUpgradeChildOrder,
						force: isUpgrades,
						params: isUpgrades ? { mode: `UPGRADE` } : undefined, // mode=UPGRADE returns add-ons with sales
					});

					let preservedProducts = []; // will contain values to carry over after cloning DEFAULT_CART
					let saleUnlocks = [];
					let unlockedHolds = [];
					let privateLinkToken;
					let groupSaleToken;

					if (this.isObject(cart)) {
						const includedProducts = this.filter(cart.products, ({ quantity }) => quantity > 0);
						const reservedProduct = this.find(includedProducts, ({ isReserved }) => isReserved);

						if (this.isObject(reservedProduct)) {
							// a 'reserved' sale is in the cart
							// they'll be removed, clear all for consistent behavior
							options.clear = true;
						}

						// a previous cart exists
						if (!options.clear && cart.eventId === eventId) {
							// preserve some aspects
							preservedProducts = this.map(includedProducts, (product) => {
								const { id, productType, selections } = product;
								return { id, productType, selections };
							});

							saleUnlocks = cart.saleUnlocks;
							unlockedHolds = cart.unlockedHolds;
						}

						if (this.isString(cart.privateLinkToken, 1)) {
							// a token exists
							if (cart.eventId === eventId) {
								// the same event is being initialized
								privateLinkToken = cart.privateLinkToken;
							}
						}

						if (this.isString(cart.groupSaleToken, 1)) {
							// a token exists
							if (cart.eventId === eventId) {
								// the same event is being initialized
								groupSaleToken = cart.groupSaleToken;
							}
						}
					}

					if (this.isString(options.privateLinkToken, 1)) {
						// a token was passed, use it (overrides existing if present)
						privateLinkToken = options.privateLinkToken;
					}

					if (this.isString(options.groupSaleToken, 1)) {
						// a token was passed, use it (overrides existing if present)
						groupSaleToken = options.groupSaleToken;
					}

					cart = this.clone(DEFAULT_CART);

					cart.eventId = event.id;
					cart.unlockedHolds = unlockedHolds;
					cart.saleUnlocks = saleUnlocks;
					cart.currency = event.currency;
					cart.checkoutType = checkoutType;
					cart.representativesSelectedStorageKey = `${agentHandle}-${event.group.id}-${event.id}`;
					cart.stripeTerminalReader = existingReader;
					cart.agentCheckoutTerminalEnabled = existingAgentCheckoutTerminalEnabled;
					cart.agentCheckoutSmsEnabled = existingAgentCheckoutSmsEnabled;

					if (this.isString(privateLinkToken, 1)) {
						cart.privateLinkToken = privateLinkToken;
					}

					if (this.isString(groupSaleToken, 1)) {
						cart.groupSaleToken = groupSaleToken;
					}

					data.custom_forms = [];
					data.incomplete_promo = null;

					data.checkoutMode = this.isString(agentHandle, 1) ? `AGENT` : `USER`;

					if (this.isFinite(options.userId)) {
						data.user = { id: options.userId };
					}

					data.event = event;
					data.cart = cart;

					cart.paymentProcessorType = event.paymentProcessorType;
					cart.requirements.address_match = event.shippingSetting.billingShippingMatch;

					cart.requirements.representatives =
						data.checkoutMode === `AGENT` && !window.location.href.includes(`/exchange/`);
					cart.progress.representatives = false;

					if (this.isObject(event.queue_settings)) {
						data.queueBehavior = event.queue_settings.behavior;
					} else {
						data.queueBehavior = false;
					}

					cart.eventCategoryName = event.eventCategoryName;
					cart.checkoutEnabled = event.checkoutEnabled !== false; // defaults to true unless the event explicitly disables it
					cart.filterConfiguration = this.clone(event.filterConfiguration);
					cart.collectionConfiguration = this.clone(event.collectionConfiguration);
					cart.sales = this.clone(event.sales);
					cart.holds = this.clone(event.holds);

					this.chain(cart.sales)
						.concat(cart.holds)
						.clone()
						.each(this.initProduct)
						.sortBy((product) => product.ordinal)
						.tap((products) => (cart.products = products));

					cart.maxTicketsAllowed = event.maxTicketsAllowed; // NOTE: can be null

					cartDeferred.resolve(cart); // make sure the initial promise is resolved
					this.promises.cart = this.when({
						// keep the promise up-to-date with the current
						promise: cart,
					});

					await this.updateProducts({ products: preservedProducts });

					const { initialCartToken } = data;

					if (options.clear) {
						this.clearPersistentCheckout();
						data.initialCartToken = null;
					} else if (this.isString(initialCartToken, 1)) {
						try {
							// fetch the cart session from the server
							const response = await this.fetch({ eventId: options.eventId, token: initialCartToken });

							data.initialCartToken = null;

							// sync the cart with the server response
							await this.syncCart({ response });

							if (this.isArray(data.cart.saleItems, 1) && !data.cart.completed) {
								return this.checkout();
							}
						} catch (err) {
							/* swallow any errors */
						}
					}

					if (isUpgrades) {
						// upgrades/exchanges reinitializes the cart when an event is selected
						const { orderId, sourceEventId, sourceSaleOrderItemIds } = options;
						await this.initializeUpgradeCart({ eventId, orderId, sourceEventId, sourceSaleOrderItemIds });
					} else {
						this.reconcile();
					}

					if (this.isString(options.accessCode, 1)) {
						const accessCode = options.accessCode.toUpperCase();
						const scope = `event`;

						if (this.isString(options.groupSaleId)) {
							data.groupSaleAccessCode = accessCode;
						}

						Unlocks.tryAccessCode({ accessCode, eventId, scope });
					} else {
						this.syncUnlocks(); // sync anything already unlocked via access codes
					}

					// Sync user entitlements to unlock any sales based on user's entitlement access
					// Only sync if the checkout mode is USER
					if (cart?.checkoutMode === `USER`) {
						await this.syncUserEntitlements();
					}

					return this.clone(cart);
				},
			},

			/**
			 * Clears all contents from the shopping cart.
			 * @async
			 * @method clearContents
			 * @param {Object} [options] - Optional configuration
			 * @param {Boolean} [options.confirm=false] - If true, prompts user for confirmation before clearing
			 * @param {String} [options.message=null] - Custom confirmation prompt message
			 * @param {String} [options.warningMessage=null] - Warning message to show if user declines (only used if confirm is true)
			 * @returns {Promise<boolean>} Returns true if cart was cleared, false if user declined confirmation
			 */
			clearContents: {
				async: true,
				public: true,
				defaults: {
					confirm: false,
					message: null,
					warningMessage: null,
				},
				async value(context, options) {
					const { App, data } = this;
					const { cart } = data;
					const { confirm, message: optionsMessage, warningMessage } = options;

					if (confirm) {
						// Check if there are any actual items in the cart
						// Don't rely on cart.quantity which may be stale or incorrectly calculated
						const { products } = cart;
						let hasItems = false;

						if (this.isArray(products, 1)) {
							hasItems = this.find(products, (product) => {
								const { selections } = product;
								// Check if there are any selections that aren't being removed
								return this.find(selections, ({ state }) => state !== `REMOVING`);
							});
						}

						if (hasItems) {
							// There are tickets present in the standard cart
							const defaultMessage = `Your shopping cart contains items. Would you like to clear your cart and continue?`;
							const message = optionsMessage === null ? defaultMessage : optionsMessage;

							const response = await App.askUser({ message });

							if (!response) {
								// User declined to clear cart
								if (this.isString(warningMessage, 1)) {
									// Show warning notification if message was provided
									App.notifyUser({
										type: `warning`,
										message: warningMessage,
									});
								}
								return false;
							}
						}
					}

					const products = this.map(cart.products, (product) => {
						const { id, productType } = product;
						return { id, productType, selections: [] };
					});

					this.updateProducts({ products });
					return true;
				},
			},

			initProduct: {
				public: false,
				value(product) {
					const { Utils, data } = this;

					// TODO: handle elsewhere, avoid showing any resale UI for agents

					// const { agentHandle } = data;
					// if (this.isString(agentHandle, 1) && product.state === `RESALE`) {
					// 	// agent checkout is not supported for agents
					// 	product.state = `SOLD_OUT`;
					// }

					// if (productType === `HOLD`) {
					// 	// NOTE: regardless of how it made it into cart.products, should leave clues to represent
					// 	// being unlocked. e.g. CollectionPicker can tell if a thumbnail has unlocked products
					// 	product.state = `LOCKED`;
					// }

					const { id, productType, resaleState, state } = product;
					const originalResaleState = resaleState; // used for removing potential overrides
					const displayState = state; // overrides from the server, e.g. access code unlocks
					const localState = state; // overrides from user history / behavior, e.g. AT_LIMIT
					const selections = []; // used to track seat and price selection choices

					if (product.isAddOn && this.isObject(data.cart.addOnPacks)) {
						// indicate which add-on packs the add-on belongs to
						product.addOnPackIndexes = [];
						product.addOnPackMetadata = [];

						this.each(data.cart.addOnPacks, ({ addOns }, index) => {
							const addOn = addOns.find(({ id: addOnId }) => addOnId === product.id);

							if (this.isObject(addOn)) {
								// the product is part of this add-on pack
								product.addOnPackIndexes.push(index);
								product.addOnPackMetadata.push(addOn.metadataCollection);
							}
						});
					}

					// default ignorePurchasedQuantity to false
					if (product.ignorePurchasedQuantity !== true) {
						product.ignorePurchasedQuantity = false;
					}

					if (state === `OPEN` && productType === `SALE`) {
						// possible to add to cart, holds not supported
						const { isLevels, priceLevels, tiers } = product;
						const { id: tierId } = tiers[0];

						if (isLevels) {
							// price levels are enabled, check level for minBehavior
							this.each(priceLevels, (priceLevel) => {
								const { initialQuantity } = priceLevel;

								if (initialQuantity > 0) {
									// price level should be added to cart
									const indices = Utils.range(0, initialQuantity);
									const { value: priceLevelOptionId } = priceLevel;

									this.each(indices, () => selections.push({ priceLevelOptionId, saleId: id, tierId }));
								}
							});
						} else {
							// standard pricing, check sale for minBehavior
							const { initialQuantity } = product;

							if (product.initialQuantity > 0) {
								// product should be added to cart
								const indices = Utils.range(0, initialQuantity);

								this.each(indices, () => selections.push({ saleId: id, tierId }));
							}
						}
					}

					this.assign(product, { displayState, localState, originalResaleState, quantity: 0, selections: [] });

					// a default quantity is being inserted, defer before doing that
					if (selections.length > 0) {
						this.defer({
							then: () =>
								this.updateProducts({
									products: [{ id, productType, selections }],
								}),
						});
					}

					return product;
				},
			},

			setCheckoutMode: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { mode } = options;

					if (data.checkoutMode !== mode) {
						data.checkoutMode = mode;
						this.reconcile();
					}
				},
			},

			setExpiration: {
				value(expiration) {
					const { Countdown, ReservedSeating, Time, data } = this;
					const { cart } = data;
					const currentTime = Time.getTime(); // use the server-adjusted timestamp
					let timerId = null;

					window.clearTimeout(expirationTimerId);

					if (this.isInt(expiration)) {
						const timeLeft = expiration - currentTime;
						timerId = window.setTimeout(this.expireCart, timeLeft);
						ReservedSeating.setExpiration(expiration);
						Countdown.setExpiration(expiration);
					}

					expirationTimerId = timerId;

					if (this.isObject(cart)) {
						cart.expiration = expiration;
					}
				},
			},

			expireCart: {
				public: true,
				value(context, options) {
					const { App } = this;
					const expiration = null;

					// reset the ttimer
					this.setExpiration(expiration);

					if (!buyRequestPlaced) {
						// don't reset if purchase is already in process
						this.init();

						App.notifyUser({
							type: `error`,
							message: `Sorry, your ticket reservation has expired, please try again`,
						});
					} // keep holding out if the request has been placed
				},
			},

			syncCart: {
				async: true,
				value(options) {
					// ingests the different server responses
					const { App, Config, Events, Templates, Utils, XHR, data } = this;
					const { response } = options;
					const { custom_form_assignments, expiration, paymentStatus, saleItems } = response;
					const { cart, event, isUpgrades } = data;
					const urlParams = App.getQueryParams();
					const keys = [
						`add_ons_blurb`,
						`add_ons`,
						`addOnPacks`,
						`addOnsFilterConfiguration`,
						`additionalFees`,
						`agentCheckoutFanId`,
						`agentCheckoutPaymentOptions`,
						`agentCheckoutRequireCVC`,
						`blurb`,
						`checkoutConfig`,
						`creditsApplicability`,
						`creditsApplied`,
						`creditsAvailableAmount`,
						`currentPayment`,
						`customPaymentSchedule`,
						`discount`,
						`fee_and_tax_breakdown`,
						`feesAndTaxes`,
						`groupSaleToken`,
						`guestCheckoutBehavior`,
						`hasBatchSubmissions`,
						`isPaymentPlanEligible`,
						`orderProtection`,
						`orderProtectionConfiguration`,
						`paymentIntent`,
						`paymentPlanFee`,
						`paymentPlanTotal`,
						`paymentSchedule`,
						`privateLinkToken`,
						`promoCode`,
						`queueCartSettings`,
						`queue_settings`,
						`representativesSelected`,
						`seasonAutoRenewal`,
						`seasonAutoRenewalConfiguration`,
						`seasonPackage`,
						`shipping`,
						`shippingSetting`,
						`showAddOns`,
						`showWaitlist`,
						`skipUserInfo`,
						`token`,
						`total`,
						`totalDueNow`,
						`transactionMetadataConfiguration`,
						`upgradeDiscount`,
						`upgradeInfo`,
						`upgradeSubTotal`,
						`usePaymentPlan`,
						`useCustomPaymentSchedule`,
						`useSalesInvoice`,
						`violations`, // used locally for keeping track of seats.io violations
					];
					let q_required;
					let promise;

					this.each(keys, (key) => {
						if (!this.isUndefined(response[key])) {
							cart[key] = response[key];
						}
					});

					cart.deliveryFee = this.isFinite(response.deliveryFee) ? response.deliveryFee : null;

					if (paymentStatus === `completed`) {
						// payment intent has been used, mark checkout process as completed
						cart.completed = true;
					}

					if (this.isInt(expiration)) {
						this.setExpiration(expiration);
					}

					const {
						features,
						orderProtection,
						orderProtectionConfiguration,
						progress,
						requirements,
						seasonAutoRenewal,
						seasonAutoRenewalConfiguration,
						seasonPackage,
						shippingSetting,
						upgradeOrderProtectionFee,
					} = cart;

					q_required = this.isObject(cart.queue_settings);
					requirements.auto_renewal =
						!cart.skipUserForBoxOffice &&
						((this.isObject(seasonAutoRenewalConfiguration) &&
							seasonAutoRenewalConfiguration.autoRenewalStatus === `ACTIVE`) ||
							(this.isObject(seasonPackage) && seasonPackage.autoRenewalStatus === `ACTIVE`));

					if (!progress.auto_renewal) {
						progress.auto_renewal = requirements.auto_renewal && seasonAutoRenewal;
					}

					const shippingRequirement = shippingSetting?.requirement;
					const shippingAddress = shippingSetting?.address;
					if (shippingRequirement === `REQUIRED`) {
						// shipping address is required
						requirements.shipping_address = true;
						features.will_call_substitution = false;
					} else if (shippingRequirement === `ELIGIBLE_FOR_WILL_CALL`) {
						// shipping address required, but allow international to substitute will call
						requirements.shipping_address = true;
						features.will_call_substitution = false;
					} else {
						// shipping address is not required
						features.will_call_substitution = false;
						requirements.shipping_address = false;
					}

					// update internal values from the latest response, and notify subscribers
					if (this.isObject(shippingAddress)) {
						cart.shippingAddress = shippingAddress;
						progress.shipping_address = true;
					} else {
						const substitutedToWillCall = shippingSetting?.substitutedToWillCall === true;

						cart.shippingAddress = null;
						progress.shipping_address = substitutedToWillCall;
					}

					// phone number form step
					const hasNullPhoneNumber = data.user && data.user.phoneNumber === null;
					if (cart.checkoutMode === `USER` && event.group.phoneNumberCollection && hasNullPhoneNumber) {
						requirements.phone = true;
					}

					if (this.isFinite(cart.total)) {
						requirements.payment = cart.total > 0;
						cart.tenderType = `NONE`;
					}

					if (!this.isObject(response.paymentSchedule) && !this.isObject(cart.queue_settings)) {
						if (cart.usePaymentPlan) {
							App.notifyUser({
								type: `warning`,
								message: `Order total no longer meets the minimum amount required for payment plan use.`,
							});
						}

						cart.usePaymentPlan = false;

						if (this.isObject(cart.paymentSchedule)) {
							cart.paymentSchedule = null; // reset any previously stored schedule
						}
					}

					// this has been removed for exchanges v1, but will need to be handled in a future version
					if (this.isFinite(upgradeOrderProtectionFee) && upgradeOrderProtectionFee !== 0) {
						// upgrading an order that was protected, fee is required and listed already
						requirements.order_protection = false;
					} else if (isUpgrades) {
						// for now, upgrades/exchanges don't support order protection
						requirements.order_protection = false;
					} else if (requirements.payment) {
						// requires payment
						const { skipOrderProtection } = data;
						const available = this.isObject(orderProtectionConfiguration) && orderProtectionConfiguration.enabled;

						if (available && !skipOrderProtection) {
							// order protection plan is available and enabled
							requirements.order_protection = true;
						} else {
							// no order protection plan available
							requirements.order_protection = false;
						}
					} else {
						// e.g. free order
						requirements.order_protection = false;
						progress.order_protection = true; // important, so resetting to false via back button counts as a change
						progress.credits = true;
						progress.payment_terminal = true;
						cart.usePaymentPlan = false;

						if (this.isObject(orderProtection)) {
							// order protection was previously added, but not compatible with free orders
							// NOTE: return, since this function will be called again after removing order protection
							return this.removeOrderProtection();
						}
					}

					requirements.credits =
						this.isFinite(cart.creditsAvailableAmount) && cart.creditsAvailableAmount > 0 && requirements.payment;

					this.each(saleItems, (item) => {
						const { conflicts } = item;

						if (this.isArray(conflicts, 1)) {
							// a tier-change was detected, notify the user
							let message = `Items in your cart may have changed, please verify ${item.name}`;
							if (this.includes(conflicts, `NAME`)) {
								if (this.includes(conflicts, `PRICE`)) {
									message += ` name and price`;
								}
								message += `.`;
							} else {
								message += ` price.`;
							}

							App.notifyUser({
								type: `warning`,
								message,
								duration: 0, // don't auto-dismiss
							});
						}
					});

					if (!this.isUndefined(saleItems)) {
						cart.saleItems = saleItems;
					}

					if (this.isArray(response.add_ons, 1)) {
						Events.normalizeSales({
							sales: response.add_ons,
							event,
						});

						this.each(response.add_ons, (add_on) => {
							var product = this.find(cart.products, {
								id: add_on.id,
							});

							if (!this.isObject(product)) {
								// cart-specific normalizing
								this.initProduct(add_on);
								add_on.productType = `SALE`;

								cart.products.push(add_on);
							}
						});
					}

					if (q_required) {
						switch (
							cart.queue_settings.delivery // check for special shipping settings
						) {
							case `SHIPPING_WILLCALL`:
							case `ALL_WILLCALL`: // override shipping, don't ask user for shipping address
								requirements.shipping_address = false;
								break;
							case `DEFAULT`:
							default:
							// no special handling
						}
					}

					requirements.q_config = q_required;

					if (response.paymentPlanRequired === true) {
						requirements.payment_plan = true;
						features.payment_plan_optional = false;
					}

					if (!requirements.payment_plan && urlParams.ppr === `1`) {
						requirements.payment_plan = true;
						features.payment_plan_optional = true;
					}

					if (this.isArray(custom_form_assignments)) {
						data.custom_forms = [];

						const path = App.getPath();
						const formIds = this.map(custom_form_assignments, `customFormId`);
						const uniqueFormIds = this.unique(formIds);
						const formPromises = this.map(uniqueFormIds, (formId) => XHR.ajax({ url: `/api/custom-forms/${formId}` }));
						const sortedAssignments = Utils.sortBy(custom_form_assignments, `priority`);
						const forms = [];

						promise = this.when({
							promises: formPromises,
							then: (formResponses) => {
								this.each(sortedAssignments, (details) => {
									// iterate over each form
									const { customFormId: formId, saleOrderLocalIds } = details;
									const form = this.find(formResponses, { id: formId });

									if (this.isArray(saleOrderLocalIds)) {
										// this is a per-ticket form, store details about which ticket(s) to target
										this.each(saleOrderLocalIds, ({ holdId, priceLevelOptionId, priceListId, saleId, tierId }) => {
											// iterate over each set of values the form applies to
											const formClone = this.clone(form); // shield the original response object, in case of re-use
											this.assign(formClone, { holdId, priceLevelOptionId, priceListId, saleId, tierId }); // make future lookups easier
											forms.push(formClone);
										});
									} else {
										const formClone = this.clone(form); // shield the original response object, in case of re-use
										forms.push(formClone);
									}
								});

								// all requested forms are ready
								this.each(forms, (form) => {
									const { collection_type, id, title } = form;
									const questions = Utils.sortBy(form.questions, ({ ordinal }) => ordinal);
									const friendlyUrl = App.friendlyUrl(`${id}-${title}`);

									form.trackingUrl = `${path}/forms/${friendlyUrl}`;
									form.trackingTitle = `Form ${id}: ${title}`;
									form.questions = questions;

									switch (collection_type) {
										case `TICKET`: // a per-ticket form, expand into multiple form pages
											{
												const { holdId, priceLevelOptionId, priceListId, saleId, tierId } = form;
												const saleItemQuery = { holdId, priceLevelOptionId, priceListId, saleId, tierId };
												const saleItem = this.find(cart.saleItems, saleItemQuery);
												const { name, quantity } = saleItem;
												// Check cart.products (standard sales) and data.postTransactionAddOns (post-purchase add-ons)
												// Note: cart.add_ons gets overwritten by syncCart, so we use data.postTransactionAddOns
												const sale =
													this.find(cart.products, { id: saleId, productType: `SALE` }) ||
													this.find(data.postTransactionAddOns, { id: saleId });
												const { category } = sale;
												const iconName = Config.SALE_CATEGORY_ICONS[category];
												const indices = Utils.range(0, quantity); // e.g. quantity 3 => [0, 1, 2]

												this.each(indices, (i) => {
													const { initial_blurb } = form;
													const internalId = Utils.uniqueId();
													const formQuestions = this.clone(questions);
													let blurb;
													let icon;

													// one page per ticket of this type
													blurb = this.isString(initial_blurb, 1) ? `${initial_blurb}<br />` : ``;
													icon = Templates.getIcon(iconName);
													if (this.isString(icon, 1)) {
														blurb += icon;
													}
													blurb += quantity > 1 ? `${name} #` + (i + 1) : name;

													const newForm = this.assign({ internalId }, form, {
														// copy all form properties over, then extend on top
														initial_blurb: blurb,
													});

													this.extract(formQuestions, (question) => {
														// iterate over the form questions
														if (question.type === `LEGAL`) {
															// legal questions should be split into their own forms
															const formClone = this.clone(newForm);
															formClone.questions = [question];
															data.custom_forms.push(formClone);
															return true; // remove from formQuestions array
														}
													});

													if (formQuestions.length > 0) {
														// there are non-LEGAL questions left over
														newForm.questions = formQuestions;
														data.custom_forms.push(newForm);
													}
												});
											}
											break;

										default: {
											// not a special kind of form, pass through as-is
											const formQuestions = this.clone(questions);

											form.internalId = Utils.uniqueId();

											this.extract(formQuestions, (question) => {
												// iterate over the form questions
												if (question.type === `LEGAL`) {
													// legal questions should be split into their own forms
													const formClone = this.clone(form);
													formClone.questions = [question];
													data.custom_forms.push(formClone);
													return true; // remove from formQuestions array
												}
											});

											if (formQuestions.length > 0) {
												// there are non-LEGAL questions left over
												form.questions = formQuestions;
												data.custom_forms.push(form);
											}
										}
									}
								});
							},
						});
					}

					this.setPersistentCheckout();

					cart.isUsingFullCredits = cart.creditsApplicability === `FULL` && cart.creditsApplied > 0;
					cart.isUsingPartialCredits = cart.creditsApplicability === `PARTIAL` && cart.creditsApplied > 0;

					return this.when({
						promise,
						then: () => this.clone(cart), // ultimate resolve with the cart
						fin: this.reconcile,
					});
				},
			},

			/**
			 * Returns the properties to copy when syncing products
			 * This is used to ensure products are kept in sync with the event's products
			 * The properties are grouped because different usages of this function may require different sets
			 * of properties to be copied.
			 * @method getSyncPropertiesToCopy
			 * @returns {Object} - an object with product types as keys and arrays of properties
			 */
			getSyncPropertiesToCopy: {
				value() {
					return {
						SHARED: [
							`advertisedPrice`,
							`formattedPrice`,
							`formattedPricePerUnit`,
							`formattedPriceTotal`,
							`isPriceCodeEditable`,
							`maxPrice`,
							`metadataCollection`,
							`minPrice`,
							`name`,
							`price`,
							`priceCode`,
							`priceLabel`,
							`priceLevels`,
							`priceLevelOptions`,
							`priceListId`,
							`priceRegulationBreakout`,
							`priceVariants`,
							`state`,
							`tiers`,
						],
						SALE: [],
						HOLD: [
							`products`, // ga holds have a products array instead of sales, but otherwise mostly the same properties to copy as sales, so include it in the HOLD-specific properties to copy
							`saleAssignments`,
						],
					};
				},
			},

			/**
			 * When the price code is changed, all price related fields must be synced to the cart's products array
			 * The logic was imported from original code update to sync events.
			 * @method syncAgentPriceCodeProducts
			 */
			syncAgentPriceCodeProducts: {
				async: true,
				async value() {
					const { Events, data } = this;
					const { cart, event: existingEvent } = data;
					const { id } = existingEvent;
					const agentHandle = this.App.getCookie(`agentHandle`);
					const params = { agent: agentHandle };
					const event = await Events.get({ block: false, by: `id`, force: true, id, params });
					const { products } = cart;
					const propertiesToCopy = this.getSyncPropertiesToCopy();
					const updates = [];

					this.each(products, (product) => {
						const { displayState, id, productType, localState, selections, state } = product;
						const sameDisplayState = state === displayState;
						const sameLocalState = state === localState;
						let sourceProduct;
						let combinedPropertiesToCopy = [...propertiesToCopy.SHARED]; // copy to avoid mutating the SHARED array

						if (productType === `SALE`) {
							sourceProduct = this.find(event.sales, { id });
							combinedPropertiesToCopy.push(...propertiesToCopy.SALE);
						} else if (productType === `HOLD`) {
							sourceProduct = this.find(event.holds, { id });
							combinedPropertiesToCopy.push(...propertiesToCopy.HOLD);
						}

						if (this.isObject(sourceProduct)) {
							// product exists in the event, copy over shared properties
							combinedPropertiesToCopy.forEach((key) => {
								// if the property exists in the source product, copy it over
								if (this.isDefined(sourceProduct[key])) {
									product[key] = sourceProduct[key];
								}
							});

							// clear any stateOverride so the fresh server state isn't overwritten by reconcile
							delete stateOverrides[product.trackingId];

							if (this.isArray(selections, 1)) {
								updates.push({ id, productType, selections });
							}
						}

						if (sameDisplayState) {
							product.displayState = product.state;
						}

						if (sameLocalState) {
							product.localState = product.state;
						}
					});

					data.event = event;
					data.cart.filterConfiguration = event.filterConfiguration;

					if (updates.length > 0) {
						this.updateProducts({ products: updates });
					} else {
						this.reconcile();
					}
				},
			},

			/**
			 * Syncs user entitlements to unlock any sales based on user's entitlement access
			 * This is called when the user logs in to ensure the cart is unlocked based on their entitlements
			 * @method syncUserEntitlements
			 * @returns {void} - if the user is not logged in or the event/cart is not initialized
			 */
			syncUserEntitlements: {
				async: true,
				value: async function () {
					const { Events, data } = this;
					const { cart, checkoutMode, event, user } = data;

					if (checkoutMode !== `USER`) {
						// only check entitlements for Tixr users (i.e. no agents or guests)
						return;
					}

					// Check if we have a logged-in user and an initialized cart/event
					if (!this.isObject(user) || !this.isInt(user.id) || !this.isObject(event) || !this.isObject(cart)) {
						return; // nothing to do
					}

					try {
						const { id: eventId } = event;
						const { id: userId } = user;
						const { saleUnlocks } = cart;

						// Fetch user entitlements
						const entitlements = await Events.getUserEntitlements({ userId, eventId });

						if (this.isObject(entitlements)) {
							if (this.isArray(entitlements.saleLocks)) {
								// Process each sale lock from entitlements
								this.each(entitlements.saleLocks, (saleLock) => {
									const { lockStatus, saleId, status, metadataCollection, benefitBadge } = saleLock;

									// Only process UNLOCKED sales
									if (lockStatus === `UNLOCKED`) {
										const entitlementSaleUnlock = {
											benefitBadge,
											eventId,
											metadataCollection: metadataCollection || [],
											status: lockStatus, // Use lockStatus as the status field
											saleId,
											state: status, // Use status as the state field
											// NOTE: No accessCode property — distinguishes entitlement unlocks from
											// access code unlocks so they can be cleared separately on logout
										};

										const existing = this.find(saleUnlocks, { eventId, saleId });

										if (this.isObject(existing)) {
											// Merge entitlement data onto the existing entry.
											// Since entitlement saleUnlocks have no accessCode property, this.assign
											// will not overwrite an existing accessCode from an access-code unlock.
											this.assign(existing, entitlementSaleUnlock);
										} else {
											saleUnlocks.push(entitlementSaleUnlock);
										}
									}
								});
							}

							if (this.isArray(entitlements.holds)) {
								const { unlockedHolds } = cart;
								const { holds } = entitlements;

								// Normalize holds so derived fields (saleAssignment tierId/deliveryConfiguration,
								// OPEN seating products array, trackingId, etc.) match what reconcile expects —
								// mirrors the normalization done in syncUnlocks for access-code hold unlocks.
								Events.normalizeHolds({ event, holds });

								this.each(holds, (hold) => {
									const { id, state } = hold;

									// only process OPEN holds
									if (state === `OPEN`) {
										const existing = this.find(unlockedHolds, { id });

										if (this.isObject(existing)) {
											// Merge entitlement data onto the existing entry.
											// Since entitlement holds have no accessCode property, this.assign
											// will not overwrite an existing accessCode from an access-code unlock.
											this.assign(existing, hold);
										} else {
											// NOTE: No accessCode property — distinguishes entitlement unlocks from
											// access code unlocks so they can be cleared separately on logout.
											unlockedHolds.push(hold);
										}
									}
								});
							}

							if (this.isObject(entitlements.filterConfiguration)) {
								cart.entitlementFilterConfiguration = entitlements.filterConfiguration;
								if (!this.isObject(cart.filterConfiguration)) {
									cart.filterConfiguration = {};
								}
								this.assign(cart.filterConfiguration, entitlements.filterConfiguration);
							}

							// Trigger a single reconcile to apply all entitlement unlocks
							this.reconcile();
						}
					} catch (error) {
						// Silently fail - don't break the cart if entitlements can't be fetched
						this.log({ type: `warn`, message: `Failed to fetch user entitlements:`, args: [error] });
					}
				},
			},

			/**
			 * Clears user entitlements from the cart when the user logs out
			 * This is called when the user logs out to ensure the cart is locked based on their entitlements
			 * @method clearUserEntitlements
			 * @returns {void} - if the cart is not initialized or the saleUnlocks array is not initialized
			 */
			clearUserEntitlements: {
				value() {
					const { data } = this;
					const { cart } = data;

					if (!this.isObject(cart) || !this.isArray(cart.saleUnlocks)) {
						return; // nothing to do
					}

					const { saleUnlocks, unlockedHolds } = cart;

					this.each(saleUnlocks, ({ saleId: id }) => {
						const product = this.find(cart.products, { id, productType: `SALE` });
						delete product?.benefitBadge;
					});

					this.each(unlockedHolds, ({ id }) => {
						const product = this.find(cart.products, { id, productType: `HOLD` });
						delete product?.benefitBadge;
					});

					// Remove entitlement-based unlocks (those without an accessCode property)
					// Keep access code-based unlocks (those with an accessCode property)
					this.extract(saleUnlocks, (unlock) => {
						return !this.isString(unlock.accessCode, 1); // remove if no accessCode (i.e., it's an entitlement unlock)
					});

					// Remove entitlement-based hold unlocks (those without an accessCode property)
					// Keep access code-based hold unlocks (those with an accessCode property)
					if (this.isArray(unlockedHolds)) {
						this.extract(unlockedHolds, (hold) => {
							return !this.isString(hold.accessCode, 1);
						});
					}

					// Remove entitlement-based filterConfiguration keys, preserving any set by access codes
					if (this.isObject(cart.entitlementFilterConfiguration)) {
						this.each(Object.keys(cart.entitlementFilterConfiguration), (key) => {
							delete cart.filterConfiguration[key];
						});
						delete cart.entitlementFilterConfiguration;
					}

					// Trigger reconcile to re-lock the sales and holds
					this.reconcile();
				},
			},

			syncUnlocks: {
				value() {
					const { Events, data } = this;
					const { cart, event, eventUnlocks } = data;

					if (this.isObject(event) && this.isObject(cart)) {
						const { id: eventId } = event;
						const { saleUnlocks, unlockedHolds } = cart;
						const matchingUnlocks = this.filter(eventUnlocks, { eventId });

						saleUnlocks.length = 0; // reset the sale unlocks
						saleUnlocks.length = 0; // reset the hold unlocks

						this.each(matchingUnlocks, (unlock) => {
							const { accessCode, saleLocks, holds, filterConfiguration } = unlock; // unlock is from the server when the user entered an access code

							this.each(saleLocks, (saleLock) => {
								// iterate through each new lock
								const { saleId } = saleLock;
								// NOTE: BE should be clearer, overriding sale.state
								const status = saleLock.lockStatus;
								const state = saleLock.status;
								// filterConfiguration and metadataCollections may be updated by BE
								const metadataCollection = saleLock.metadataCollection;
								const { benefitBadge } = saleLock;
								const saleUnlock = { accessCode, benefitBadge, eventId, metadataCollection, status, saleId, state };

								this.extract(saleUnlocks, { eventId, saleId }); // remove any existing unlocks from this sale
								saleUnlocks.push(saleUnlock); // add the custom reference to this 'unlock'
							});

							Events.normalizeHolds({ event, holds });

							this.syncUnlocksForAgentPriceCode(event, holds);

							// iterate through each server unlocked hold
							this.each(holds, (hold) => {
								const existing = this.find(unlockedHolds, { id: hold.id }); // look for an existing unlocked hold in cart

								// attach the accessCode for easy lookup later, matching saleLock format
								hold.accessCode = accessCode;

								if (this.isObject(existing)) {
									this.assign(existing, hold);
								} else {
									unlockedHolds.push(hold);
								}
							});

							if (this.isObject(filterConfiguration)) {
								cart.filterConfiguration = filterConfiguration;
							}
						});

						this.reconcile();
					}
				},
			},

			/**
			 * If agent price code is enabled, we need to sync unlocked hold properties from the server with the hold information from the agent selected price code
			 * This is necessary because the agent price code may change the pricing and state of the unlocked holds
			 * NOTE: This is an in-place modification of the holds array passed in
			 * @method syncUnlocksForAgentPriceCode
			 * @param {Array} holds - the array of holds returned from the server when unlocking
			 */
			syncUnlocksForAgentPriceCode: {
				value(event, holds) {
					if (this.AgentPriceCode.isEnabled()) {
						this.each(holds, (hold) => {
							const sourceHold = this.find(event.holds, { id: hold.id });

							if (this.isObject(sourceHold)) {
								// copy over the sync properties from the event hold to the unlocked hold
								const propertiesToCopy = this.getSyncPropertiesToCopy();
								const combinedPropertiesToCopy = [...propertiesToCopy.SHARED, ...propertiesToCopy.HOLD];
								combinedPropertiesToCopy.forEach((key) => {
									if (key === `state`) {
										return; // skip state, needs special handling below
									}

									if (this.isDefined(sourceHold[key])) {
										hold[key] = sourceHold[key];
									}
								});

								// special handler for DO_NOT_SELL because an agent price code may change the state of the unlocked hold from the server response
								if (sourceHold.state === `DO_NOT_SELL`) {
									// cachedUnlockState stores the original state from original unlocked hold and will be used to restore the state if the sourceHold is no longer DO_NOT_SELL
									// Create it if it doesn't exist yet
									if (!this.isObject(hold.cachedUnlockState)) {
										hold.cachedUnlockState = {
											state: hold.state,
											displayState: hold.displayState,
											localState: hold.localState,
										};
									}

									// Now override the state to match the sourceHold
									this.assign(hold, {
										state: sourceHold.state,
										displayState: sourceHold.state,
										localState: sourceHold.state,
									});
								} else if (this.isObject(hold.cachedUnlockState)) {
									this.assign(hold, hold.cachedUnlockState); // restore the original state if it was overridden
								}
							}
						});
					}
				},
			},

			/*
			 * PRICE LEVEL CONSTRAINT HELPERS
			 * ===============================
			 * These functions implement minTicketsAllowed, maxTicketsAllowed, and ticketMultiple
			 * constraints at the individual price level layer.
			 *
			 * Key distinction:
			 *   - isLevelAddable: can add via "+" button (considers assigned selections only)
			 *   - isLevelSelectable: can select in overlay/chart (considers assigned + unassigned)
			 *
			 * This distinction handles the case where a user adds a selection without specifying
			 * which price level (priceLevelOptionId: null). The SelectPriceLevels overlay then
			 * uses isSelectable to show which levels can receive the unassigned selection.
			 *
			 * See: docs/price-level-max-and-multiple-constraints.md
			 */

			/**
			 * Calculate constraint flags for a SALE product's price level
			 * @private
			 * @param {Object} options
			 * @param {Object} options.priceLevel - The price level with constraints
			 * @param {number} options.levelQuantity - Current quantity for this level
			 * @param {number} options.unselectedQuantity - Unassigned quantity (for reserved seating)
			 * @param {number} options.productQuantity - Total product quantity across all levels
			 * @param {number|null} options.productMaxTicketsAllowed - Product-level max constraint
			 * @param {number} options.cartQuantity - Total cart quantity
			 * @param {number|null} options.cartMaxTicketsAllowed - Event-level max constraint
			 * @returns {Object} { quantityFloor, isLevelAddable, isLevelRemovable, isLevelSelectable, minimumViolated, maximumViolated, multipleViolated, atLevelMaximum }
			 */
			calculateSalePriceLevelConstraints: {
				private: true,
				value(options) {
					const {
						priceLevel,
						levelQuantity,
						unselectedQuantity,
						productQuantity,
						productMaxTicketsAllowed,
						cartQuantity,
						cartMaxTicketsAllowed,
					} = options;
					const { maxTicketsAllowed, minBehavior, minTicketsAllowed, ticketMultiple } = priceLevel;

					// Only price level constraints apply here; sale-level multiple is ignored
					// (sale constraints are enforced by checkout validation, not by +/- buttons)
					const effectiveTicketMultiple = this.isInt(ticketMultiple, 1) ? ticketMultiple : undefined;

					let quantityFloor = 0;
					let quantityCeiling = Infinity;
					let isLevelAddable = true;
					let isLevelSelectable = true;
					let minimumViolated = false;
					let maximumViolated = false;
					let multipleViolated = false;
					let atLevelMaximum = false;

					// Detect impossible constraint configurations
					const hasMin = this.isInt(minTicketsAllowed, 1);
					const hasPriceLevelMax = this.isInt(maxTicketsAllowed, 0);
					const hasProductMax = this.isInt(productMaxTicketsAllowed, 0);
					const hasMultiple = this.isInt(effectiveTicketMultiple, 2);

					// Determine effective max across all levels (price code, product, cart)
					let effectiveMax = Infinity;
					if (hasPriceLevelMax) {
						effectiveMax = Math.min(effectiveMax, maxTicketsAllowed);
					}
					if (hasProductMax) {
						effectiveMax = Math.min(effectiveMax, productMaxTicketsAllowed - productQuantity);
					}
					if (this.isInt(cartMaxTicketsAllowed, 0)) {
						effectiveMax = Math.min(effectiveMax, cartMaxTicketsAllowed - cartQuantity);
					}

					// Check if min > max (impossible)
					if (hasMin && hasPriceLevelMax && minTicketsAllowed > maxTicketsAllowed) {
						window.console.warn(
							`Price level has impossible constraints: min (${minTicketsAllowed}) > max (${maxTicketsAllowed}). Level disabled.`,
							priceLevel
						);
						return {
							quantityFloor: 0,
							quantityCeiling: 0,
							isLevelAddable: false,
							isLevelRemovable: levelQuantity > 0,
							isLevelSelectable: false,
							minimumViolated: false,
							maximumViolated: false,
							multipleViolated: false,
							atLevelMaximum: false,
						};
					}

					// Check if multiple is incompatible with min-max range (considering ALL max constraints)
					if (hasMultiple) {
						const effectiveMin = hasMin ? minTicketsAllowed : effectiveTicketMultiple;
						const firstValidQuantity = Math.ceil(effectiveMin / effectiveTicketMultiple) * effectiveTicketMultiple;

						if (firstValidQuantity > effectiveMax) {
							window.console.warn(
								`Price level has impossible constraints: no valid multiple (${effectiveTicketMultiple}) exists with effective max (${effectiveMax}). Level disabled.`,
								priceLevel
							);
							return {
								quantityFloor: 0,
								quantityCeiling: 0,
								isLevelAddable: false,
								isLevelRemovable: levelQuantity > 0,
								isLevelSelectable: false,
								minimumViolated: false,
								maximumViolated: false,
								multipleViolated: false,
								atLevelMaximum: false,
							};
						}
					}

					// Handle minTicketsAllowed constraint
					if (this.isInt(minTicketsAllowed, 1)) {
						if (minBehavior === `ENFORCE`) {
							quantityFloor = minTicketsAllowed;

							if (levelQuantity < minTicketsAllowed) {
								// zero is not allowed, purchase is required
								minimumViolated = true;
							}
						} else {
							// minBehavior = SET_AS_DEFAULT or null
							if (levelQuantity < minTicketsAllowed && levelQuantity > 0) {
								// zero is allowed, since the purchase is optional
								minimumViolated = true;
							}
						}

						if (levelQuantity < minTicketsAllowed) {
							// level quantity is below the minimum, check whether incrementing would exceed any limits
							const difference = minTicketsAllowed - levelQuantity;

							// Calculate actual tickets needed when rounded to multiple
							// Must match calculateSalePriceLevelIncrementAmount logic exactly
							// Example: min=5, multiple=3 → ceil(5/3)*3 = 6, not 5
							const effectiveMultiple = this.isInt(effectiveTicketMultiple, 1) ? effectiveTicketMultiple : 1;
							const actualTicketsNeeded = Math.ceil(difference / effectiveMultiple) * effectiveMultiple;

							// check for a product-level maximum
							if (this.isInt(productMaxTicketsAllowed, 0)) {
								const maxAddableQuantity = productMaxTicketsAllowed - productQuantity;
								const maxSelectableQuantity = maxAddableQuantity + unselectedQuantity;

								if (maxAddableQuantity < actualTicketsNeeded) {
									isLevelAddable = false;
								}

								if (maxSelectableQuantity < actualTicketsNeeded) {
									isLevelSelectable = false;
								}
							}

							// check for an event-level maximum
							if (this.isInt(cartMaxTicketsAllowed, 0)) {
								const maxCartAddableQuantity = cartMaxTicketsAllowed - cartQuantity;
								const maxCartSelectableQuantity = maxCartAddableQuantity + unselectedQuantity;

								if (maxCartAddableQuantity < actualTicketsNeeded) {
									isLevelAddable = false;
								}

								if (maxCartSelectableQuantity < actualTicketsNeeded) {
									isLevelSelectable = false;
								}
							}
						}
					}

					// Handle maxTicketsAllowed constraint (NEW)
					if (this.isInt(maxTicketsAllowed, 0)) {
						quantityCeiling = maxTicketsAllowed;

						if (levelQuantity >= maxTicketsAllowed) {
							// level is at or above maximum
							maximumViolated = true;
							atLevelMaximum = true;
							isLevelAddable = false;
							isLevelSelectable = false;
						} else if (this.isInt(effectiveTicketMultiple, 2)) {
							// Check if we can add at least one full multiple before hitting maximum
							// Example: max=10, qty=9, multiple=3 → remaining=1 < 3 → can't add
							// Increment would calculate: floor(1/3)*3 = 0 (adds nothing)
							const maxAddable = maxTicketsAllowed - levelQuantity;
							if (maxAddable < effectiveTicketMultiple) {
								isLevelAddable = false;
							}
						}
					}

					// Check for ticketMultiple violation (NEW)
					if (hasMultiple && levelQuantity > 0) {
						if (levelQuantity % effectiveTicketMultiple !== 0) {
							multipleViolated = true;
						}
					}

					const isLevelRemovable = levelQuantity > quantityFloor;

					return {
						quantityFloor,
						quantityCeiling,
						isLevelAddable,
						isLevelRemovable,
						isLevelSelectable,
						minimumViolated,
						maximumViolated,
						multipleViolated,
						atLevelMaximum,
					};
				},
			},

			/**
			 * Calculate how many items to add when incrementing a SALE product's price level
			 * @private
			 * @param {Object} options
			 * @param {Object} options.priceLevel - The price level being incremented
			 * @param {number} options.levelQuantity - Current quantity at this level
			 * @returns {number} Number of items to add
			 */
			calculateSalePriceLevelIncrementAmount: {
				private: true,
				value(options) {
					const { priceLevel, levelQuantity } = options;
					const { maxTicketsAllowed, minTicketsAllowed, ticketMultiple } = priceLevel;

					// Only price level constraints apply; sale-level multiple/min are ignored so that
					// fans can reach valid sale-total states across multiple price levels (e.g. 2x Adult
					// + 1x Child satisfying a sale min of 3). Checkout validation enforces the sale total.
					const effectiveTicketMultiple = this.isInt(ticketMultiple, 1) ? ticketMultiple : undefined;

					const effectiveMultiple = this.isInt(effectiveTicketMultiple, 1) ? effectiveTicketMultiple : 1;
					let numberToAdd = effectiveMultiple;

					// PRIORITY 1: Correct invalid multiple state before normal increment logic
					// Prevents infinite loop when user picks arbitrary seat count (e.g., 3 with multiple=2)
					// Example: qty=3, multiple=2, remainder=1 → add 1 to reach 4 (not add 2 to reach 5)
					if (this.isInt(effectiveTicketMultiple, 2) && levelQuantity > 0) {
						const remainder = levelQuantity % effectiveTicketMultiple;
						if (remainder !== 0) {
							// Invalid multiple - add partial to complete current multiple
							numberToAdd = effectiveTicketMultiple - remainder;

							// Verify we don't exceed maximum while correcting
							if (this.isInt(maxTicketsAllowed, 0)) {
								const maxAddable = maxTicketsAllowed - levelQuantity;
								if (numberToAdd > maxAddable) {
									// Can't correct without exceeding max - return 0 (no-op)
									return 0;
								}
							}

							// Return early - correction takes absolute priority
							return numberToAdd;
						}
					}

					// PRIORITY 2: Apply price level minimum
					const effectiveMin = this.isInt(minTicketsAllowed, 2) ? this.max([0, minTicketsAllowed - levelQuantity]) : 0;

					if (effectiveMin > 0) {
						numberToAdd = Math.ceil(effectiveMin / effectiveMultiple) * effectiveMultiple;
					}

					// PRIORITY 3: Cap at maximum
					if (this.isInt(maxTicketsAllowed, 0)) {
						const maxAddable = maxTicketsAllowed - levelQuantity;
						if (numberToAdd > maxAddable) {
							// Find largest valid multiple that fits within maximum
							numberToAdd = Math.floor(maxAddable / effectiveMultiple) * effectiveMultiple;
						}
					}

					return numberToAdd;
				},
			},

			/**
			 * Calculate how many items to remove when decrementing a SALE product's price level
			 * @private
			 * @param {Object} options
			 * @param {Object} options.priceLevel - The price level being decremented
			 * @param {number} options.levelQuantity - Current quantity at this level
			 * @returns {number} Number of items to remove
			 */
			calculateSalePriceLevelDecrementAmount: {
				private: true,
				value(options) {
					const { priceLevel, levelQuantity } = options;
					const { minTicketsAllowed, ticketMultiple } = priceLevel;

					// Only price level constraints apply; sale-level multiple is ignored
					const effectiveTicketMultiple = this.isInt(ticketMultiple, 1) ? ticketMultiple : undefined;

					const effectiveMultiple = this.isInt(effectiveTicketMultiple, 1) ? effectiveTicketMultiple : 1;
					let numberToRemove = effectiveMultiple;

					// PRIORITY 1: Correct invalid multiple state before normal decrement logic
					// Prevents infinite loop when user tries to escape invalid state
					// Example: qty=3, multiple=2, remainder=1 → remove 1 to reach 2 (not remove 2 to reach 1)
					if (this.isInt(effectiveTicketMultiple, 2) && levelQuantity > 0) {
						const remainder = levelQuantity % effectiveTicketMultiple;
						if (remainder !== 0) {
							// Invalid multiple - remove partial to reach previous valid multiple
							const correctedQuantity = levelQuantity - remainder;

							// Check if correction would violate minimum (but 0 is okay)
							if (this.isInt(minTicketsAllowed, 1) && correctedQuantity > 0 && correctedQuantity < minTicketsAllowed) {
								// Correcting to previous valid would leave us below minimum
								// Example: qty=5, multiple=3, min=4 → can't reach 3, remove all to 0
								return levelQuantity;
							}

							// Safe to remove just the remainder
							return remainder;
						}
					}

					// PRIORITY 2: Standard decrement logic (only runs if currently valid)
					// If at or below minimum, remove all
					if (this.isInt(minTicketsAllowed, 1) && levelQuantity <= minTicketsAllowed) {
						numberToRemove = levelQuantity;
					} else {
						// Check if removing would leave invalid quantity below minimum
						const remaining = levelQuantity - effectiveMultiple;
						if (this.isInt(minTicketsAllowed, 1) && remaining > 0 && remaining < minTicketsAllowed) {
							// Removing one multiple would leave us below minimum, so remove all instead
							numberToRemove = levelQuantity;
						}
					}

					return numberToRemove;
				},
			},

			/**
			 * Calculate how many selections to add when incrementing a non-levels product.
			 * Same three-priority structure as calculateSalePriceLevelIncrementAmount, applied to
			 * the product-level constraints instead of a price level's.
			 * @public
			 * @param {Object} options
			 * @param {Object} options.product - The product being incremented (ticketMultiple, minTicketsAllowed, maxTicketsAllowed)
			 * @param {number} options.currentQuantity - Effective quantity to increment from
			 * @returns {number} Number of selections to add
			 */
			calculateProductIncrementAmount: {
				public: true,
				value(context, options) {
					const { product, currentQuantity } = options;
					const { maxTicketsAllowed, minTicketsAllowed, ticketMultiple } = product;

					// A null/non-integer ticketMultiple must still define a step, or the modulo
					// checks below evaluate to NaN.
					const effectiveMultiple = this.isInt(ticketMultiple, 1) ? ticketMultiple : 1;

					// PRIORITY 1: Correct invalid multiple state before normal increment logic
					// Prevents staging a quantity that isn't a valid multiple (e.g. a cart restored
					// from storage pre-fix). Example: qty=4, multiple=3, remainder=1 → add 2 to reach 6.
					if (this.isInt(effectiveMultiple, 2) && currentQuantity > 0) {
						const remainder = currentQuantity % effectiveMultiple;
						if (remainder !== 0) {
							const numberToAdd = effectiveMultiple - remainder;

							// Verify we don't exceed maximum while correcting
							if (this.isInt(maxTicketsAllowed, 0)) {
								const maxAddable = maxTicketsAllowed - currentQuantity;
								if (numberToAdd > maxAddable) {
									// Can't correct without exceeding max - return 0 (no-op)
									return 0;
								}
							}

							// Return early - correction takes absolute priority
							return numberToAdd;
						}
					}

					// PRIORITY 2: Apply product minimum
					let numberToAdd = effectiveMultiple;
					const effectiveMin = this.isInt(minTicketsAllowed, 2)
						? this.max([0, minTicketsAllowed - currentQuantity])
						: 0;

					if (effectiveMin > 0) {
						numberToAdd = Math.ceil(effectiveMin / effectiveMultiple) * effectiveMultiple;
					}

					// PRIORITY 3: Cap at maximum
					if (this.isInt(maxTicketsAllowed, 0)) {
						const maxAddable = maxTicketsAllowed - currentQuantity;
						if (numberToAdd > maxAddable) {
							// Find largest valid multiple that fits within maximum
							numberToAdd = Math.floor(maxAddable / effectiveMultiple) * effectiveMultiple;
						}
					}

					return numberToAdd;
				},
			},

			/**
			 * Calculate how many selections to remove when decrementing a non-levels product.
			 * Mirrors the while loop in decrementProduct's non-levels branch, re-expressed as a
			 * count; which selection is actually removed stays with the caller.
			 * @public
			 * @param {Object} options
			 * @param {Object} options.product - The product being decremented (ticketMultiple, minTicketsAllowed)
			 * @param {number} options.currentQuantity - Effective quantity to decrement from
			 * @returns {number} Number of selections to remove
			 */
			calculateProductDecrementAmount: {
				public: true,
				value(context, options) {
					const { product, currentQuantity } = options;
					const { minTicketsAllowed, ticketMultiple } = product;

					const effectiveTicketMultiple = this.isInt(ticketMultiple, 1) ? ticketMultiple : 1;

					let removed = 0;

					while (
						// keep removing while:
						currentQuantity - removed > 0 && // there are still items to remove
						(removed < effectiveTicketMultiple || // fewer than ticketMultiple have been removed
							(currentQuantity - removed) % effectiveTicketMultiple !== 0 || // not a valid ticketMultiple
							currentQuantity - removed < minTicketsAllowed) // jump between 0 <=> minTicketsAllowed
					) {
						removed++;
					}

					return removed;
				},
			},

			/**
			 * Calculate constraint flags for a HOLD product's price level
			 * Traverses saleAssignments to check constraints across related SALE products
			 * @private
			 * @param {Object} options
			 * @param {Object} options.priceLevel - The HOLD product's price level
			 * @param {number} options.levelQuantity - Current quantity for this level on the HOLD
			 * @param {number} options.unselectedQuantity - Unassigned quantity
			 * @param {number} options.productQuantity - Total HOLD product quantity
			 * @param {Array} options.saleAssignments - The HOLD's sale assignments
			 * @param {Array} options.allProducts - All products in the cart (to look up SALEs)
			 * @param {number} options.cartQuantity - Total cart quantity
			 * @param {number|null} options.cartMaxTicketsAllowed - Event-level max constraint
			 * @returns {Object} { isLevelAddable, isLevelRemovable, isLevelSelectable, maximumViolated, multipleViolated, atLevelMaximum }
			 */
			calculateHoldPriceLevelConstraints: {
				private: true,
				value(options) {
					const {
						priceLevel,
						levelQuantity,
						unselectedQuantity,
						productTicketMultiple,
						saleAssignments,
						allProducts,
						cartQuantity,
						cartMaxTicketsAllowed,
					} = options;
					const {
						maxTicketsAllowed: holdLevelMaxTicketsAllowed,
						ticketMultiple,
						value: priceLevelOptionId,
					} = priceLevel;

					// Calculate effective multiple: if both have multiples, use LCM to satisfy both
					let effectiveTicketMultiple;
					if (this.isInt(ticketMultiple, 1) && this.isInt(productTicketMultiple, 1)) {
						const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
						const lcm = (a, b) => (a * b) / gcd(a, b);
						effectiveTicketMultiple = lcm(ticketMultiple, productTicketMultiple);
					} else if (this.isInt(ticketMultiple, 1)) {
						effectiveTicketMultiple = ticketMultiple;
					} else {
						effectiveTicketMultiple = productTicketMultiple;
					}

					const effectiveMultiple = this.isInt(effectiveTicketMultiple, 1) ? effectiveTicketMultiple : 1;

					let isLevelSelectable = true;
					let isLevelRemovable = false;
					let isLevelAddable = true;
					let numberToAdd = effectiveMultiple;
					let holdMaxTicketsAllowed;
					let maximumViolated = false;
					let minimumViolated = false;
					let multipleViolated = false;
					let atLevelMaximum = false;

					// Detect impossible constraint configurations (same validation as SALE levels)
					const hasPriceLevelMax = this.isInt(holdLevelMaxTicketsAllowed, 0);
					const hasMultiple = this.isInt(effectiveTicketMultiple, 2);

					// Determine effective max for HOLD (will be refined by saleAssignment traversal)
					let effectiveMax = Infinity;
					if (hasPriceLevelMax) {
						effectiveMax = Math.min(effectiveMax, holdLevelMaxTicketsAllowed);
					}
					if (this.isInt(cartMaxTicketsAllowed, 0)) {
						effectiveMax = Math.min(effectiveMax, cartMaxTicketsAllowed - cartQuantity);
					}

					// Note: HOLD levels don't use minTicketsAllowed directly; they inherit from saleAssignments
					// Check if multiple is incompatible with max constraints (will check sale maxes below)
					if (hasMultiple && effectiveMax !== Infinity) {
						// For HOLDs, the first valid quantity is just the multiple itself (or 0)
						const firstValidQuantity = effectiveTicketMultiple;

						if (firstValidQuantity > effectiveMax) {
							window.console.warn(
								`HOLD price level has impossible constraints: ticketMultiple (${effectiveTicketMultiple}) > effective max (${effectiveMax}). Level disabled.`,
								priceLevel
							);
							return {
								isLevelAddable: false,
								isLevelRemovable: levelQuantity > 0,
								isLevelSelectable: false,
								maximumViolated: false,
								multipleViolated: false,
								atLevelMaximum: false,
							};
						}
					}

					if (levelQuantity > 0) {
						// HOLDs are always reserved, don't need to worry about ADD_ON + minBehavior
						isLevelRemovable = true;
					}

					// Check HOLD level's own maxTicketsAllowed first
					if (this.isInt(holdLevelMaxTicketsAllowed, 0)) {
						if (holdMaxTicketsAllowed === undefined || holdLevelMaxTicketsAllowed < holdMaxTicketsAllowed) {
							holdMaxTicketsAllowed = holdLevelMaxTicketsAllowed;
						}
					}

					// Traverse saleAssignments to find most restrictive max across related SALEs
					this.each(saleAssignments, (assignment) => {
						const sale = this.find(allProducts, { productType: `SALE`, id: assignment.saleId });
						const { maxTicketsAllowed } = sale;
						const salePriceLevel = this.find(sale.priceLevels, { value: priceLevelOptionId }) ?? {}; // this level may not exist for the sale in the case of ffpc so fallback to empty object
						const saleSelections = this.filter(sale.selections, { priceLevelOptionId });
						const { length: saleLevelQuantity } = saleSelections;
						const { minTicketsAllowed } = salePriceLevel;

						if (this.isInt(minTicketsAllowed, 2)) {
							const difference = minTicketsAllowed - saleLevelQuantity - levelQuantity;
							// Round to next valid multiple - must match calculateHoldPriceLevelIncrementAmount
							// Example: holdQty=0, saleQty=0, min=5, multiple=3 → ceil(5/3)*3 = 6
							const saleNumberToAdd = Math.ceil(difference / effectiveMultiple) * effectiveMultiple;

							if (saleNumberToAdd > numberToAdd) {
								numberToAdd = saleNumberToAdd;
							}

							const totalSelected = saleLevelQuantity + levelQuantity;

							if (totalSelected > 0 && totalSelected < minTicketsAllowed) {
								minimumViolated = true;
							}
						}

						if (this.isInt(maxTicketsAllowed)) {
							if (holdMaxTicketsAllowed === undefined || maxTicketsAllowed < holdMaxTicketsAllowed) {
								holdMaxTicketsAllowed = maxTicketsAllowed;
							}
						}
					});

					// Check if current quantity violates maximum
					if (this.isInt(holdMaxTicketsAllowed, 0)) {
						if (levelQuantity >= holdMaxTicketsAllowed) {
							maximumViolated = true;
							atLevelMaximum = true;
							isLevelAddable = false;
							isLevelSelectable = false;
						} else if (this.isInt(effectiveTicketMultiple, 2)) {
							// Check if we can add at least one full multiple before hitting maximum
							const maxAddable = holdMaxTicketsAllowed - levelQuantity;
							if (maxAddable < effectiveTicketMultiple) {
								isLevelAddable = false;
							}
						}
					}

					// Check for ticketMultiple violation (NEW)
					if (hasMultiple && levelQuantity > 0) {
						if (levelQuantity % effectiveTicketMultiple !== 0) {
							multipleViolated = true;
						}
					}

					if (levelQuantity + numberToAdd > holdMaxTicketsAllowed) {
						// incrementing would exceed this price variant's maxTicketsAllowed
						isLevelAddable = false;
					}

					if (levelQuantity - unselectedQuantity + numberToAdd > holdMaxTicketsAllowed) {
						// incrementing would exceed this price variant's maxTicketsAllowed
						isLevelSelectable = false;
					}

					if (this.isInt(cartMaxTicketsAllowed)) {
						if (cartQuantity + numberToAdd > cartMaxTicketsAllowed) {
							// incrementing would exceed event maxTicketsAllowed
							isLevelAddable = false;
						}

						if (cartQuantity - unselectedQuantity + numberToAdd > cartMaxTicketsAllowed) {
							// incrementing would exceed event maxTicketsAllowed
							isLevelSelectable = false;
						}
					}

					return {
						isLevelAddable,
						isLevelRemovable,
						isLevelSelectable,
						maximumViolated,
						minimumViolated,
						multipleViolated,
						atLevelMaximum,
					};
				},
			},

			reconcile: {
				value() {
					const { Events, Products, Templates, data } = this;
					const { cart, event, user } = data;
					const saleCategories = [];
					const printNumber = Templates.getHelper(`printNumber`);
					let minimumNotSelected = false; // used to detect the minimum not selected violation
					let invalidMultiple = false; // used to detect invalid ticket quanties
					let recalculate = false;
					let countedTicketTypesPerUser;
					let countedTicketTypesPerOrder;
					let countedSaleTypes;
					let primaryQuantityCount;
					let waitlistQuantityCount = 0;
					let singleProductType; // used in cases of "queueBehavior === SINGLE_SALE"
					let singleProductId; // used in cases of "queueBehavior === SINGLE_SALE"
					let queueRequired;
					let cartQuantity;
					let isCounted;
					let subtotal;
					let purchasedPrimaryCategoriesBySaleId = {};
					let purchasedWaitlistCategoriesBySaleId = {};
					let purchasedCombinedCategoriesBySaleId = {};

					if (!this.isObject(event) || !this.isObject(cart)) {
						// not initialized
						return; // nothing to do
					}

					const { checkoutMode } = data;
					const {
						addOnPacks,
						guestCheckoutBehavior,
						holds: eventHolds,
						products,
						saleItems,
						unlockedHolds,
						token,
						violations,
					} = cart;
					const checkoutInProgress = this.isString(token, 1); // used to avoid making changes once an order has been reserved from the server
					const { combinePrimaryWaitlistCounts: limitBehavior, mostco, mostca, mostcou, mostcau } = event;
					/**
					 * mostco: maxOnlineSaleTypeCountPerOrder
					 * mostca: maxOnlineSaleTypeCategoriesPerOrder
					 * mostcou: maxOnlineSaleTypeCountPerUser
					 * mostcau: maxOnlineSaleTypeCategoriesPerUser
					 */
					const hasAddOnPacks = this.isArray(addOnPacks, 1);
					let { agentCheckoutPaymentOptions: tenderTypeOptions } = cart;

					if (!this.isArray(tenderTypeOptions, 1)) {
						tenderTypeOptions = [`CREDIT_CARD`];
					}

					// reset selected add-on pack counts
					if (hasAddOnPacks) {
						this.each(addOnPacks, (pack) => {
							pack.selectedSaleTypes = new window.Set();
							pack.selectedQuantity = 0;
						});
					}

					countedSaleTypes = event.maxTicketsAllowedMetadata; // ADD_ON | FIXED | PRIVATE
					countedSaleTypes = this.isString(countedSaleTypes, 1) ? countedSaleTypes.split(`,`) : null;
					if (this.isNull(countedSaleTypes)) {
						isCounted = () => true;
					} else {
						isCounted = (saleType) => this.includes(countedSaleTypes, saleType);
					}

					primaryQuantityCount = 0; // includes past purchases
					this.each(products, (product) => {
						const { state } = product;

						// reset counts and state overrides
						product.totalQuantity = 0;
						product.displayState = state;
						product.localState = state;
					});

					this.each(unlockedHolds, (hold) => {
						const { id } = hold;
						const productType = `HOLD`;
						const query = { id, productType };
						const match = this.find(products, query);

						if (this.isObject(match)) {
							const { state } = match;
							const { state: newState } = hold;

							this.assign(match, hold); // overwrite all properties present on the unlocked version
							// store the up-to-date state in displayState, original in state
							match.displayState = newState;
							match.localState = newState;
							match.state = state; // preserve original state for comparison
							match.isUnlocked = true; // mark as unlocked
						} else {
							const clone = this.clone(hold);
							this.initProduct(clone);
							clone.state = `LOCKED`; // NOTE: important for detecting that the hold was unlocked via access code
							products.push(clone);
						}
					});

					// Remove any holds that were dynamically added to products (not from the original event holds)
					// but are no longer in unlockedHolds. This prevents stale holds from lingering in the product
					// list after entitlement unlocks are cleared (e.g. on logout).
					this.extract(products, (product) => {
						if (product.productType !== `HOLD`) return false;
						const isOriginalHold = this.isArray(eventHolds) && this.find(eventHolds, { id: product.id });
						if (isOriginalHold) return false;
						const isStillUnlocked = this.find(unlockedHolds, { id: product.id });
						return !isStillUnlocked;
					});

					if (checkoutMode === `AGENT`) {
						if (this.isString(guestCheckoutBehavior)) {
							// specific checkout behavior was returned from server
							const isAgentCheckoutAllowed = this.includes([`ALL`, `AGENT_ONLY`], guestCheckoutBehavior);
							cart.checkoutMode = isAgentCheckoutAllowed ? `AGENT` : `USER`;
						} else {
							// still shopping, judge using event settings
							const { guestCheckoutSetting } = event;
							const isAgentCheckoutAllowed = this.includes(guestCheckoutSetting, `AGENT`);
							cart.checkoutMode = isAgentCheckoutAllowed ? `AGENT` : `USER`;
						}
					} else {
						cart.checkoutMode = checkoutMode;
					}

					if (this.isObject(user)) {
						const { purchasedHoldQuantityMap, purchasedSaleQuantityMap } = user;
						const { id: eventId, saleTypeMap } = event;
						const eventKey = `${eventId}`;

						// TODO: Get purchasedHoldQuantityMap and purchasedSaleQuantityMap from new endpoints once BE has added them (required before release to prod)
						const holdMap = purchasedHoldQuantityMap ? purchasedHoldQuantityMap[eventKey] : [];
						const saleMap = purchasedSaleQuantityMap ? purchasedSaleQuantityMap[eventKey] : [];

						this.each(saleMap, (saleQuantities, saleKey) => {
							const productType = `SALE`;
							const saleId = this.parseInt(saleKey);
							const product = this.find(products, { id: saleId, productType });
							const saleType = saleTypeMap[saleId];
							const primaryCount = this.parseInt(saleQuantities.pc);
							const waitlistCount = this.parseInt(saleQuantities.wc);
							const combinedCount = primaryCount + waitlistCount;

							let countTowardsEventMax = true;
							waitlistQuantityCount += waitlistCount;

							// not guaranteed to be present for ADD_ONs, etc.
							if (this.isObject(product)) {
								if (primaryCount > 0) {
									purchasedPrimaryCategoriesBySaleId[saleId] = product.category;
								}
								if (waitlistCount > 0) {
									purchasedWaitlistCategoriesBySaleId[saleId] = product.category;
								}
								if (combinedCount > 0) {
									purchasedCombinedCategoriesBySaleId[saleId] = product.category;
								}

								if (product.ignorePurchasedQuantity) {
									// past purchases should not counted toward any limits
									countTowardsEventMax = false;
								} else {
									// past purchases count for sale max limit
									if (limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.combine) {
										product.totalQuantity += combinedCount;
									} else if (limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.separate) {
										product.totalQuantity += primaryCount;
									}
								}
							}

							if (countTowardsEventMax && isCounted(saleType)) {
								// past purchases count toward the event total max limit
								if (limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.combine) {
									primaryQuantityCount += combinedCount;
								} else if (limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.separate) {
									primaryQuantityCount += primaryCount;
								}
							}
						});

						this.each(holdMap, (saleQuantity, holdKey) => {
							const productType = `HOLD`;
							const holdId = parseInt(holdKey, 10);
							const product = this.find(products, { id: holdId, productType });

							if (this.isObject(product)) {
								product.totalQuantity += saleQuantity;
							}

							// NOTE: hold orders are counted in the sale map, don't double-increment event total
						});
					}

					if (this.isInt(event.maxTicketsAllowed)) {
						cart.maxTicketsAllowed = event.maxTicketsAllowed - primaryQuantityCount;
					}

					countedTicketTypesPerUser = 0; // used for event.mostcou validation
					countedTicketTypesPerOrder = 0; // used for event.mostco validation
					cartQuantity = 0; // current cart listing, ignore past purchases and other rules
					subtotal = 0; // local calculation of base order cost
					queueRequired = false;

					const stateOptionValueByName = {};
					this.each(cart.filterConfiguration, (collectionFilterConfig) => {
						const availabilityFilter = this.find(collectionFilterConfig?.filters, { key: `-2` });
						if (this.isObject(availabilityFilter)) {
							this.each(availabilityFilter.options, (option) => {
								stateOptionValueByName[option.name] = option.value;
							});
						}
					});

					this.each(products, (product) => {
						const productQuantity = product.quantity;
						const { productType, selections, state, totalQuantity, trackingId } = product;
						const stateOverride = stateOverrides[trackingId];

						// declare variables that will be derived and attached to the product
						let isStateOverride = false; // whether the product's state has been overridden

						// keep track of the count from the purchase history
						product.purchasedQuantity = totalQuantity;

						if ((state === `LOCKED` || state === `HIDE`) && productType === `SALE`) {
							// check for unlocks that may augment this product
							const saleId = product.id;
							const unlock = this.find(cart.saleUnlocks, { saleId });

							if (this.isObject(unlock)) {
								if (unlock.status === `NO_LOCK` || unlock.status === `UNLOCKED`) {
									// the sale was unlocked
									const { state: newState, metadataCollection } = unlock;

									this.assign(product, {
										displayState: newState,
										localState: newState,
										metadataCollection,
									});
								}
							}
						}

						// benefit badge gets bundled with the entitlements call; need it regardless of lock status
						this.each(cart.saleUnlocks, ({ saleId, benefitBadge }) => {
							if (benefitBadge) {
								const product = this.find(cart.products, { id: saleId });

								if (this.isObject(product)) {
									this.assign(product, { benefitBadge });
								}
							}
						});

						if (this.isString(stateOverride, 1)) {
							// a manual override has been set
							this.assign(product, {
								displayState: stateOverride,
								localState: stateOverride,
							});

							isStateOverride = true;
						}

						// calculate more derived properties
						const isSelectable = Products.checkSelectable(product);
						const unlockableStates = [`LOCKED`, `HIDE`];
						const isUnlockable = unlockableStates.includes(state);
						const stateChanged = state !== product.displayState;
						const isUnlocked = isUnlockable && stateChanged;

						this.assign(product, { isSelectable, isStateOverride, isUnlocked });

						// handle here, so displayState has already incorporated unlocks, etc.
						if (product.displayState === `SOLD_OUT` && product.resaleState === `AVAILABLE`) {
							// TODO: stop using this strategy, remove this FE-only state
							this.assign(product, { displayState: `RESALE`, isStateOverride: true, localState: `RESALE` });
						}

						if (productType === `SALE` && this.isArray(product.metadataCollection)) {
							const stateEntry = this.find(product.metadataCollection, { metadataId: -2 });

							if (this.isObject(stateEntry)) {
								const { displayState, resaleState } = product;
								let metadataStateKey;

								if (
									displayState === `RESALE` || // this is an FE-only pseudo-state
									(displayState === `QUEUED` && resaleState === `AVAILABLE`) // prioritize resale being available over waitlist
								) {
									// treat as OPEN, to include in the "Available" filter
									metadataStateKey = `OPEN`;
								} else {
									metadataStateKey = displayState;
								}

								const numericValue = stateOptionValueByName[metadataStateKey] ?? `0`;

								stateEntry.metadataOptionId = numericValue;
								stateEntry.metadataOptionValue = metadataStateKey;
							}
						}

						if (productQuantity > 0) {
							const seatSelections = this.filter(selections, ({ seatLabel, state }) => {
								return state === `ACTIVE` && this.isString(seatLabel);
							});
							const isQueued = product.displayState === `QUEUED`;

							if (isQueued || !product.isReserved || seatSelections.length === productQuantity) {
								cartQuantity += productQuantity; // plain tally
							}

							product.totalQuantity += productQuantity; // total count of this sale purchased, to check against limits
							if (isCounted(product.type)) {
								primaryQuantityCount += productQuantity; // total event count, to check against limits
							}

							if (isQueued) {
								queueRequired = true;

								if (data.queueBehavior === `SINGLE_SALE`) {
									singleProductType = product.productType;
									singleProductId = product.id;
								}
							}

							this.each(selections, ({ price }) => (subtotal += price));

							if (productType === `SALE`) {
								saleCategories.push(product.category);
							} else if (productType === `HOLD`) {
								this.each(selections, (selection) => {
									const { saleId } = selection;

									if (this.isInt(saleId, 0)) {
										// an underlying sale is associated, use its category
										const sale = this.find(products, { productType: `SALE`, id: saleId });
										saleCategories.push(sale.category);
									} else {
										// no underlying sale yet, use default category
										saleCategories.push(product.category);
									}
								});
							}

							const mostcouIsSet = this.isInt(mostcou, 1);
							const mostcoIsSet = this.isInt(mostco, 1);
							const mostcauIsSet = this.isArray(mostcau, 1);
							const mostcaIsSet = this.isArray(mostca, 1);
							const mostcouReached = mostcouIsSet && countedTicketTypesPerUser >= mostcou;
							const mostcoReached = mostcoIsSet && countedTicketTypesPerOrder >= mostco;
							const productCategoryIncludedInMOSTCAU = mostcauIsSet && this.includes(mostcau, product.category);
							const productCategoryIncludedInMOSTCA = mostcaIsSet && this.includes(mostca, product.category);

							let includedInMostco = mostcoIsSet ? !product.isAddOn : false;
							let includedInMostcou = mostcouIsSet ? !product.isAddOn : false;

							if (mostcouIsSet && mostcauIsSet) {
								includedInMostcou = !product.isAddOn && productCategoryIncludedInMOSTCAU;
							}

							if (mostcoIsSet && mostcaIsSet) {
								includedInMostco = !product.isAddOn && productCategoryIncludedInMOSTCA;
							}

							// if mostcouIsSet and mostcoIsSet, enforce the lower of the two limits
							if ((includedInMostcou && mostcouReached) || (includedInMostco && mostcoReached)) {
								product.quantity = 0;
								product.selections = [];
								recalculate = true;
							} else {
								if (includedInMostcou) {
									countedTicketTypesPerUser++;
								}
								if (includedInMostco) {
									countedTicketTypesPerOrder++;
								}
							}

							if (hasAddOnPacks && product.isAddOn) {
								this.each(product.addOnPackIndexes, (index) => {
									addOnPacks[index].selectedSaleTypes.add(product.id);
									addOnPacks[index].selectedQuantity += productQuantity;
								});
							}
						} else {
							selections.length = 0; // clear out any selections
						}
					});

					if (!this.isString(cart.token)) {
						cart.requirements.q_config = queueRequired;
					}

					if (subtotal === 0) {
						const allCategories = this.unique(saleCategories);

						if (this.isEqual(allCategories, [`GUEST`])) {
							cart.checkoutLabel = `Add to list`;
						} else if (cart.requirements.q_config) {
							cart.checkoutLabel = `Join Waitlist`;
						} else {
							cart.checkoutLabel = `Checkout`;
						}

						cart.checkoutAmount = `Free`;
					} else if (cart.requirements.q_config) {
						cart.checkoutLabel = `Join Waitlist`;
						cart.checkoutAmount = printNumber(subtotal, `currencyCompact`, cart.currency);
					} else {
						cart.checkoutLabel = `Checkout`;
						cart.checkoutAmount = printNumber(subtotal, `currencyCompact`, cart.currency);
					}

					if (this.isArray(saleItems, 1)) {
						// a saleItems array was received from the server, recalculate summary
						// NOTE: important for cases like ticket reservations, where products[i] selections can't be relied on
						cartQuantity = 0;
						subtotal = 0;
						this.each(saleItems, (item) => {
							cartQuantity += item.quantity;
							subtotal += item.quantity * item.price;
						});
					}

					const isTerminalConnected = this.isObject(cart.stripeTerminalReader);
					cart.requirements.payment_terminal =
						cart.checkoutMode === `AGENT` && cart.requirements.payment && isTerminalConnected;

					this.assign(cart, {
						quantity: cartQuantity,
						subtotal,
						tenderTypeOptions,
					});

					this.each(products, (product) => {
						// NOTE: separate loop from above, to get accurate quantity counts first
						const { displayState, isLevels, maxTicketsAllowed, quantity, selections, type } = product;
						const canUpdate = !checkoutInProgress || type === `ADD_ON`;
						const inCart = quantity > 0;

						let { localState, ticketMultiple } = product;

						if (isLevels) {
							// override ticketMultiple behavior if price levels are enabled
							ticketMultiple = 1;
						}

						switch (displayState) {
							case `QUEUED`:
							case `OPEN`: {
								// Ticket is active for purchase
								const isQueued = displayState === `QUEUED`;

								localState = displayState; // default for non-QUEUED

								if (this.isInt(maxTicketsAllowed, 1)) {
									let increased = product.totalQuantity + ticketMultiple;

									// check if open hold has selected sale
									this.each(products, (potentialHoldProduct) => {
										if (potentialHoldProduct.productType === `HOLD`) {
											this.each(potentialHoldProduct.selections, (selection) => {
												if (selection.saleId === product.id) {
													increased += 1;
												}
											});
										}
									});

									if (increased > maxTicketsAllowed) {
										localState = `AT_LIMIT`; // max limit hit for sale

										if (canUpdate && inCart) {
											if (product.totalQuantity > product.maxTicketsAllowed) {
												// more than are allowed
												const difference = product.totalQuantity - product.maxTicketsAllowed;

												if (difference > product.quantity) {
													product.quantity = 0;
												} else {
													let updatedQuantity = product.quantity - difference;

													while (updatedQuantity > 0 && updatedQuantity % product.ticketMultiple !== 0) {
														selections.pop();
														updatedQuantity--;
													}

													product.quantity = updatedQuantity;
												}

												recalculate = true;
											}
										}
									}
								}

								if (this.isInt(event.maxTicketsAllowed, 0) && isCounted(product.type)) {
									// this check only applies to counted types
									// NOTE: Use explicit ticketMultiple references, local variable may have been overidden to 1
									let numberToAdd;
									let increasedTotalQuantity;

									if (this.isInt(product.minTicketsAllowed, 0) && product.quantity < product.minTicketsAllowed) {
										const { quantity: saleQuantity } = product;

										numberToAdd = 0;
										while (saleQuantity + numberToAdd < product.minTicketsAllowed) {
											numberToAdd += product.ticketMultiple;
										}
									} else {
										numberToAdd = product.ticketMultiple;
									}

									if (isQueued && limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.separate) {
										increasedTotalQuantity = waitlistQuantityCount + product.quantity + numberToAdd;
									} else {
										increasedTotalQuantity = primaryQuantityCount + numberToAdd;
									}

									if (increasedTotalQuantity > event.maxTicketsAllowed) {
										// can't increase without going over, event max hit
										localState = `AT_LIMIT`;
									}
								}

								if (data.queueBehavior === `SINGLE_SALE`) {
									if (this.isInt(singleProductId)) {
										if (product.id !== singleProductId || product.productType !== singleProductType) {
											// a QUEUED ticket is in cart, all other tickets are disabled
											localState = `AT_Q_LIMIT`;
										}
									}

									if (displayState === `QUEUED` && cartQuantity > product.quantity) {
										// a non-QUEUED ticket is in cart, all QUEUED are disabled
										if (product.quantity > 0) {
											product.selections = [];
											product.quantity = 0;
											recalculate = true;
										}

										localState = `AT_Q_LIMIT`;
									}
								}

								const mostcouIsSet = this.isInt(mostcou, 1);
								const mostcoIsSet = this.isInt(mostco, 1);
								const mostcauIsSet = this.isArray(mostcau, 1);
								const mostcaIsSet = this.isArray(mostca, 1);
								const productCategoryIncludedInMOSTCAU = mostcauIsSet && this.includes(mostcau, product?.category);
								const productCategoryIncludedInMOSTCA = mostcaIsSet && this.includes(mostca, product?.category);
								const productIdString = product?.id?.toString();

								let productIdPurchasedBefore = false;
								let countedWaitlistTicketTypes = 0;
								let countedPrimaryTicketTypes = 0;
								let countedCombinedTicketTypes = 0;
								let mostcouReached = false;

								// past purchases should count toward per-user category max for limited categories
								if (mostcouIsSet) {
									let purchasedProductIds = [];
									let purchasedCategories = [];

									if (limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.separate) {
										if (isQueued && this.isObject(purchasedWaitlistCategoriesBySaleId)) {
											// this is a waitlist ticket type and limit behavior is separate
											// check if the user has purchased a waitlist ticket in the same category
											purchasedProductIds = Object.keys(purchasedWaitlistCategoriesBySaleId);
											purchasedCategories = Object.values(purchasedWaitlistCategoriesBySaleId);
											productIdPurchasedBefore =
												this.isArray(purchasedProductIds, 1) && this.includes(purchasedProductIds, productIdString);

											if (
												this.isArray(purchasedCategories, 1) &&
												this.includes(purchasedCategories, product?.category)
											) {
												if (!mostcauIsSet || (mostcauIsSet && productCategoryIncludedInMOSTCAU)) {
													countedWaitlistTicketTypes++;
												}
											}
										} else if (this.isObject(purchasedPrimaryCategoriesBySaleId)) {
											// this is a primary ticket type (non-waitlist) and limit behavior is separate
											// check if the user has purchased a primary ticket in the same category
											purchasedProductIds = Object.keys(purchasedPrimaryCategoriesBySaleId);
											purchasedCategories = Object.values(purchasedPrimaryCategoriesBySaleId);
											productIdPurchasedBefore =
												this.isArray(purchasedProductIds, 1) && this.includes(purchasedProductIds, productIdString);

											if (
												this.isArray(purchasedCategories, 1) &&
												this.includes(purchasedCategories, product?.category)
											) {
												if (!mostcauIsSet || (mostcauIsSet && productCategoryIncludedInMOSTCAU)) {
													countedPrimaryTicketTypes++;
												}
											}
										}
									} else {
										// limit behavior is combined, check if the user has purchased a ticket of waitlist OR primary in the same category
										purchasedProductIds = Object.keys(purchasedCombinedCategoriesBySaleId);
										purchasedCategories = Object.values(purchasedCombinedCategoriesBySaleId);
										productIdPurchasedBefore =
											this.isArray(purchasedProductIds, 1) && this.includes(purchasedProductIds, productIdString);

										if (this.isArray(purchasedCategories, 1) && this.includes(purchasedCategories, product?.category)) {
											if (!mostcauIsSet || (mostcauIsSet && productCategoryIncludedInMOSTCAU)) {
												countedCombinedTicketTypes++;
											}
										}
									}

									// set user ticket type limit reached flag (mostcouReached)
									// based on limit behavior and waitlist/primary ticket type counts
									if (limitBehavior === COMBINE_PRIMARY_WAITLIST_COUNTS.separate) {
										mostcouReached =
											countedTicketTypesPerUser + countedWaitlistTicketTypes >= mostcou ||
											countedTicketTypesPerUser + countedPrimaryTicketTypes >= mostcou;
									} else {
										mostcouReached = countedTicketTypesPerUser + countedCombinedTicketTypes >= mostcou;
									}
								}

								const mostcoReached = mostcoIsSet && countedTicketTypesPerOrder >= mostco;

								let includedInMostco = false;
								let includedInMostcou = false;

								// per-user category limits shouldn't prevent a user from carting more of the same saleId they've purchased before
								if (product.quantity === 0 && !product.isAddOn) {
									if (mostcouIsSet && !productIdPurchasedBefore) {
										includedInMostcou = mostcouReached;

										if (mostcouReached && mostcauIsSet) {
											includedInMostcou = productCategoryIncludedInMOSTCAU;
										}
									}

									if (mostcoIsSet) {
										includedInMostco = mostcoReached;

										if (mostcoReached && mostcaIsSet) {
											includedInMostco = productCategoryIncludedInMOSTCA;
										}
									}
								}

								// if mostcouIsSet and mostcoIsSet, enforce the lower of the two limits
								if (includedInMostco || includedInMostcou) {
									// max ticket type limit has been hit, but sale isn't already in cart, disable
									localState = isQueued ? `AT_Q_LIMIT` : `AT_LIMIT`;
								}

								if (hasAddOnPacks && product.isAddOn) {
									const packAtMaxLimit = product.addOnPackIndexes.find((index) => {
										const pack = addOnPacks[index];
										// can add more of already selected add-ons, but can't add new types if max types is hit
										return (
											!this.isNull(pack.maxNumberOfSaleTypes) &&
											pack.maxNumberOfSaleTypes <= pack.selectedSaleTypes?.size &&
											!pack.selectedSaleTypes.has(product.id)
										);
									});

									if (this.isInt(packAtMaxLimit)) {
										localState = `AT_LIMIT`;
									}

									const packAtQuantityCap = product.addOnPackIndexes.find((index) => {
										const pack = addOnPacks[index];
										const quantityMatchActive = Boolean(pack.matchQuantityEnabled);
										const maxQuantitySet = this.isInt(pack.maxQuantity) && pack.maxQuantity > 0;
										const atOrOverMaxQuantity = pack.selectedQuantity >= pack.maxQuantity;

										return quantityMatchActive && maxQuantitySet && atOrOverMaxQuantity;
									});

									if (this.isInt(packAtQuantityCap)) {
										localState = `AT_LIMIT`;
									}
								}
								break;
							}

							case `LOCKED`: // Needs to be unlocked
							case `CLOSED`: // Sale ended check
							case `PRE`: // Sale hasn't started
							case `HIDE`: // Sale being manually hidden
							case `RESALE`: // resale state, sold via listings
							case `SOLD_OUT`: // sold out check
							case `AVAIL_AT_DOOR`: // manual override state
							case `AVAIL_SOON`: // manual override state
							case `FREE`: // used for non-tickets
								if (canUpdate && inCart) {
									product.selections = [];
									product.quantity = 0; // reset in case it transitioned from an available to non-available state
									recalculate = true;
								}
								break;

							case `DO_NOT_SELL`: {
								// A product that switched to DO_NOT_SELL does not affect the current cart, it's simply not longer visible
								// This allows agents to switch between price codes without losing the cart state
								break;
							}

							default:
								// unknown state
								this.log({
									type: `warning`,
									message: `Unrecognized sale state: ${product.state}`,
								});
						}

						if (product.localState !== localState) {
							product.localState = localState;
						}

						if (canUpdate && this.isInt(singleProductId) && inCart) {
							if (product.id !== singleProductId || product.productType !== singleProductType) {
								product.selections = [];
								product.quantity = 0;
								recalculate = true;
							}
						}
					});

					const allHolds = this.filter(products, { productType: `HOLD` });

					this.each(products, (product) => {
						const { isLevels, priceRegulationBreakout, productType, quantity, ticketMultiple } = product;
						let isProductRemovable = quantity > 0; // sensible default
						let isProductAddable; // decided using state as a last step

						if (productType === `SALE`) {
							const potentialHolds = this.filter(allHolds, ({ saleAssignments }) => {
								const assignment = this.find(saleAssignments, { saleId: product.id });
								return this.isObject(assignment);
							});

							// used for checking for valid quantity selections, across this sale and any related holds
							let totalQuantity = quantity;

							this.each(potentialHolds, (hold) => {
								this.each(hold.selections, (selection) => {
									if (selection.saleId === product.id || selection.saleId === null) {
										// current sale is selected, or unselected for F.B.A. and should potentially count
										totalQuantity++;
									}
								});
							});

							if (isLevels) {
								// price levels enabled
								const { priceLevels, selections } = product;
								let removableLevelCount = 0;

								// traverse the individual levels for minBehavior
								this.each(priceLevels, (priceLevel) => {
									const { value: priceLevelOptionId } = priceLevel;
									const levelSelections = this.filter(selections, { priceLevelOptionId });
									const unselected = this.filter(selections, { priceLevelOptionId: null });
									const { length: levelQuantity } = levelSelections;
									const { length: unselectedQuantity } = unselected;

									const constraints = this.calculateSalePriceLevelConstraints({
										priceLevel,
										levelQuantity,
										unselectedQuantity,
										productQuantity: quantity,
										productMaxTicketsAllowed: product.maxTicketsAllowed,
										cartQuantity: cart.quantity,
										cartMaxTicketsAllowed: cart.maxTicketsAllowed,
									});

									const {
										isLevelAddable,
										isLevelRemovable,
										isLevelSelectable,
										maximumViolated,
										minimumViolated,
										multipleViolated,
										atLevelMaximum,
									} = constraints;

									if (minimumViolated) {
										minimumNotSelected = true;
									}

									if (maximumViolated) {
										// track maximum violations if needed for UI feedback
									}

									// Track multipleViolated from constraints (already calculated)
									if (multipleViolated) {
										invalidMultiple = true;
									}

									if (isLevelRemovable) {
										removableLevelCount++;
									}

									this.assign(priceLevel, {
										isAddable: isLevelAddable,
										isRemovable: isLevelRemovable,
										isSelectable: isLevelSelectable,
										atMaximum: atLevelMaximum,
										invalidMultiple: multipleViolated,
									});
								});

								if (removableLevelCount > 0) {
									isProductRemovable = true;
								} else {
									// NOTE: handles cases where FBA selections are in cart, but no levels are selected
									this.find(selections, ({ state: selectionState }) => {
										if (selectionState === `FBA`) {
											isProductRemovable = true;
											return true; // break loop
										}
									});
								}

								if (this.isInt(product.minTicketsAllowed, 1)) {
									// ignore sale-level minBehavior, always treat as null/default

									if (totalQuantity < product.minTicketsAllowed && totalQuantity > 0) {
										minimumNotSelected = true;
									}
								}
							} else {
								// standard pricing, minBehavior is at the sale level
								const { minBehavior, minTicketsAllowed, ticketMultiple } = product;
								let quantityFloor = 0;

								if (this.isInt(minTicketsAllowed, 1)) {
									if (minBehavior === `ENFORCE`) {
										quantityFloor = minTicketsAllowed;

										if (totalQuantity < minTicketsAllowed) {
											// zero is not allowed, purchase is required
											minimumNotSelected = true;
										}
									} else {
										// minBehavior === `SET_AS_DEFAULT` | null
										if (totalQuantity > 0 && totalQuantity < minTicketsAllowed) {
											// zero is allowed, since the purchase is optional
											minimumNotSelected = true;
										}
									}
								}

								if (quantity - ticketMultiple < quantityFloor) {
									isProductRemovable = false;
								}
							}

							if (totalQuantity % ticketMultiple !== 0) {
								invalidMultiple = true;
							}
						} else if (productType === `HOLD`) {
							const { isReserved, saleAssignments, selections, totalQuantity } = product;
							const allDisplayStates = []; // used for deciding whether to override state based on assignments

							let { ticketMultiple } = product;
							let selectedMaxQuantity = 0; // limit if accumulating selected tickets from the chart
							let fbaMaxQuantity; // limit if using + button for Find Best Available functionality

							// HOLDs are always reserved, don't need to worry about ADD_ON + minBehavior ENFORCE
							isProductRemovable = quantity > 0;

							if (isLevels) {
								const { holdType, priceLevels, productType, saleAssignments } = product;
								let removableLevelCount = 0; // used as an extra check for the overall product removable
								let addableLevelCount = 0; // used as an extra check for the overall product addable

								// check if hold type is open
								if (productType === `HOLD` && holdType === `OPEN`) {
									// check saleassignment for the sale selected
									this.each(selections, (selection) => {
										const { saleId } = selection;
										const saleAssignment = this.find(saleAssignments, { saleId });
										if (!this.isArray(saleAssignment?.priceLevelOptionAssignments, 1)) {
											removableLevelCount++;
										}
									});
								}

								// override ticketMultiple behavior if price levels are enabled
								ticketMultiple = 1;

								this.each(priceLevels, (priceLevel) => {
									const { value: priceLevelOptionId } = priceLevel;
									const levelSelections = this.filter(product.selections, { priceLevelOptionId });
									const unselected = this.filter(selections, { priceLevelOptionId: null });
									const { length: levelQuantity } = levelSelections;
									const { length: unselectedQuantity } = unselected;

									const constraints = this.calculateHoldPriceLevelConstraints({
										priceLevel,
										levelQuantity,
										unselectedQuantity,
										productQuantity: quantity,
										productTicketMultiple: product.ticketMultiple,
										saleAssignments,
										allProducts: products,
										cartQuantity: cart.quantity,
										cartMaxTicketsAllowed: cart.maxTicketsAllowed,
									});

									const {
										isLevelAddable,
										isLevelRemovable,
										isLevelSelectable,
										maximumViolated,
										minimumViolated,
										multipleViolated,
										atLevelMaximum,
									} = constraints;

									if (maximumViolated) {
										// track maximum violations if needed for UI feedback
									}

									if (minimumViolated) {
										minimumNotSelected = true;
									}

									// Track multipleViolated from constraints (already calculated)
									if (multipleViolated) {
										invalidMultiple = true;
									}

									if (isLevelAddable) {
										addableLevelCount++;
									}

									if (isLevelRemovable) {
										removableLevelCount++;
									}

									this.assign(priceLevel, {
										isAddable: isLevelAddable,
										isRemovable: isLevelRemovable,
										isSelectable: isLevelSelectable,
										atMaximum: atLevelMaximum,
										invalidMultiple: multipleViolated,
									});
								});

								isProductAddable = addableLevelCount.length > 0;
								isProductRemovable = removableLevelCount > 0;
							} else {
								// standard pricing, use sale-level minBehavior

								const { minBehavior, minTicketsAllowed, ticketMultiple } = product;
								const enforceMinimum = this.isInt(minTicketsAllowed, 1) && minBehavior === `ENFORCE`;
								const quantityFloor = enforceMinimum ? minTicketsAllowed : 0;

								if (quantity - ticketMultiple >= quantityFloor) {
									isProductRemovable = true;
								}
							}

							this.each(saleAssignments, ({ saleId: id }) => {
								const sale = this.find(products, { id, productType: `SALE` });
								const { displayState, maxTicketsAllowed } = sale;

								allDisplayStates.push(displayState);

								if (this.isInt(maxTicketsAllowed)) {
									// sale has a limit
									if (this.includes([`OPEN`, `HIDE`], displayState)) {
										// sale is available for seating chart selection
										const { purchasedQuantity, quantity: saleQuantity } = sale;
										const userMaxTicketsAllowed = maxTicketsAllowed - purchasedQuantity;
										const adjustedMaxTicketsAllowed = userMaxTicketsAllowed - saleQuantity;

										if (selectedMaxQuantity !== null) {
											selectedMaxQuantity += userMaxTicketsAllowed;
										}

										if (this.isUndefined(fbaMaxQuantity) || adjustedMaxTicketsAllowed < fbaMaxQuantity) {
											fbaMaxQuantity = adjustedMaxTicketsAllowed;
										}
									}
								} else {
									selectedMaxQuantity = null;
								}
							});

							const isHoldUnlocked = this.isString(product.seatsIoConfiguration?.channelId, 1);

							if (
								!isHoldUnlocked && // unlocked holds can determine if they have selectable seats from the chart
								product.displayState === `OPEN` &&
								allDisplayStates.length > 0
							) {
								const hasOpenSale = this.includes(allDisplayStates, `OPEN`);
								const hasHiddenSale =
									this.includes(allDisplayStates, `HIDE`) || this.includes(allDisplayStates, `DO_NOT_SELL`);

								if (!hasOpenSale && !hasHiddenSale) {
									// should override OPEN displayState, to disallow Find Best Available, etc.
									let [overrideState] = allDisplayStates;

									if (overrideState === `QUEUED`) {
										// NOTE: holds do not support waitlist functionality
										// keep the hold in open state, since it's possible seats to be released and become available, but sales don't toggle back from QUEUED
										overrideState = `OPEN`;
									}

									if (product.displayState === product.localState) {
										// Avoid overriding situations like AT_LIMIT
										product.localState = overrideState;
									}

									product.displayState = overrideState;
								}
							}

							if (product.displayState === `OPEN` || product.displayState === `QUEUED`) {
								// it's possible for items to be in cart for this product

								if (isReserved) {
									// NOTE: non-reserved holds are broken out into each holdId + saleId combination, no product-wide overrides
									const selectedSales = this.filter(selections, ({ seatLabel }) => this.isString(seatLabel));
									// it no specific seat is chosen yet, F.B.A. needs to consider ALL assigned sales
									const quantityToCheck = selectedSales.length > 0 ? selectedMaxQuantity : fbaMaxQuantity;

									if (this.isInt(quantityToCheck, 0)) {
										if (totalQuantity + ticketMultiple > quantityToCheck) {
											product.localState = `AT_LIMIT`;
										}
									}
								}
							}

							product.fbaMaxQuantity = fbaMaxQuantity;
						}

						isProductAddable = this.includes([`OPEN`, `QUEUED`], product.localState);

						let summary;
						let priceBreakoutMessage;
						switch (product.displayState) {
							case `OPEN`:
							case `QUEUED`: {
								summary = product.formattedPrice;
								// price breakout message should only show for open, queued, or resale sales
								if (product.productType === `HOLD`) {
									if (!product.isLevels) {
										// holds with price levels display breakdown at individual level
										priceBreakoutMessage = Events.getPriceBreakdownForHold({ hold: product, currency: cart.currency });
									}
								} else {
									const priceBreakoutResult = Events.getPriceBreakoutMessage({
										priceRegulationBreakout,
										currency: cart.currency,
									});
									priceBreakoutMessage = priceBreakoutResult.message;
								}
								break;
							}
							case `RESALE`: {
								summary = product.formattedPriceResale;
								const isRange = product.minPriceResale !== product.maxPriceResale;
								const { message: resaleBreakoutMessage } = Events.getPriceBreakoutMessage({
									priceRegulationBreakout: product.resalePriceBreakout?.maxPriceRegulationBreakout,
									currency: cart.currency,
									isRange,
								});
								priceBreakoutMessage = resaleBreakoutMessage;
								break;
							}
							case `AVAIL_SOON`:
								if (this.includes(event.ticketPickerPriceDisplay, `PRICE_ON_AVAIL_SOON`)) {
									summary = `${product.formattedPrice}`;
								}
								break;
							case `FREE`:
								summary = `Free`;
								break;
							case `LOCKED`:
								if (this.includes(event.ticketPickerPriceDisplay, `PRICE_ON_LOCKED`)) {
									if (this.isString(product.formattedPriceTotal)) {
										summary = `${product.formattedPriceTotal}`;
									} else {
										summary = `${product.formattedPrice}`;
									}
								}
								break;
							case `SOLD_OUT`:
								if (this.isString(product.optionalSaleRedirectUrl, 1)) {
									if (this.includes([`CABANA`, `TABLE_SERVICE`], product.category)) {
										summary = `Reservations`;
									} else {
										summary = `Sold elsewhere`;
									}
								}
								break;
							default:
								summary = ``;
						}

						this.assign(product, {
							isAddable: isProductAddable,
							isRemovable: isProductRemovable,
							priceBreakoutMessage,
							summary,
						});
					});

					// update open hold product representations
					this.each(products, (product) => {
						if (product.productType === `HOLD`) {
							this.each(product.products, (assignedProduct) => {
								const saleProduct = this.find(products, { id: assignedProduct.id, productType: `SALE` });
								const holdSelectionsAssignedSale = this.filter(product.selections, (selection) => {
									return selection.saleId === assignedProduct.id;
								});

								assignedProduct.quantity = holdSelectionsAssignedSale.length;

								if (saleProduct.localState === `AT_LIMIT`) {
									assignedProduct.localState = `AT_LIMIT`;
								} else {
									assignedProduct.localState = assignedProduct.displayState;
								}
							});
						}
					});

					if (minimumNotSelected) {
						violations.push({
							type: `minimumNotSelected`,
							message: `The minimum number of items was not met. Please select more items.`,
						});
					} else {
						this.extract(violations, { type: `minimumNotSelected` });
					}

					if (invalidMultiple) {
						violations.push({
							type: `multipleNotValid`,
							message: `An invalid number of items was selected. Please adjust your selection to continue.`,
						});
					} else {
						this.extract(violations, { type: `multipleNotValid` });
					}

					cart.requireSavedPayment = cart.usePaymentPlan || cart.requirements.q_config;

					cart.requirePaymentMethod =
						cart.requirements.payment &&
						((cart.requirements.q_config ? cart.total : cart.totalDueNow) > 0 || cart.requireSavedPayment);

					if (this.isArray(cart.upgrades, 1) && this.syncUpgradesWithSeatSelections()) {
						recalculate = true;
					}

					if (recalculate) {
						// skip notifying subscribers, will trigger after re-running
						return this.reconcile();
					} else {
						const { subscriptions } = this;
						const { cachedCart } = data;

						this.App.unblock(upgradesFindBestBlockOptions);

						if (!this.isEqual(cart, cachedCart)) {
							// keep a cached copy of the published version for future comparison
							data.cachedCart = this.clone(cart);

							this.each(subscriptions, (subscription) => {
								const { derive, handler, owner } = subscription;

								if (!owner.alive) {
									return;
								}

								const hasDerive = this.isFunction(derive);
								let handlerResult;

								if (hasDerive) {
									const protectedValue = this.clone(cart);

									// calculate a quick lookup map for products, for consumers plucking out a specific entry
									const { products } = protectedValue;
									protectedValue.productsMap = this.calculateProductsMap(products);

									const deriveResult = derive(protectedValue);

									if (this.isEqual(deriveResult, subscription.deriveResult)) {
										return;
									}

									subscription.deriveResult = this.clone(deriveResult);
									handlerResult = deriveResult;
								}

								this.defer({
									done: true,
									then: () => {
										if (!hasDerive) {
											// create a protected copy of the cart for the handler, so it can't modify internal state
											handlerResult = this.clone(cart);

											// calculate a quick lookup map for products, for consumers plucking out a specific entry
											const { products } = handlerResult;
											const productsMap = this.calculateProductsMap(products); // generate quick-lookup map
											handlerResult.productsMap = productsMap;
										}

										handler.call(owner, handlerResult);
									},
								});
							});
						}
					}
				},
			},

			calculateProductsMap: {
				value(products) {
					const productsMap = {};

					// generate a map for faster lookups by consumers of cart products
					this.each(products, (product) => {
						const { id, productType } = product;
						const key = `${productType}-${id}`;

						productsMap[key] = product;
					});

					return productsMap;
				},
			},

			setViolations: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart, ignoreConsecutiveSeatingViolations } = data;
					const { violations } = options;

					if (ignoreConsecutiveSeatingViolations) {
						this.extract(violations, { type: `consecutiveSeats` });
					}

					cart.violations = violations;
					this.reconcile();
				},
			},

			getPaymentIntent: {
				public: true,
				async: true,
				value(context, options) {
					const { data, XHR } = this;
					const { cart, event, user } = data;
					const { checkoutMode, paymentIntent } = cart;

					if (!this.isString(cart.token, 1)) {
						throw new Error(`ShoppingCart.getPaymentIntent() called without a valid cart.`);
					}

					if (!this.isObject(event) || event.paymentProcessorType !== `STRIPE`) {
						throw new Error(`ShoppingCart.getPaymentIntent() called without a valid Stripe event.`);
					}

					if (!this.isObject(user) && !cart.skipUserForBoxOffice) {
						throw new Error(`ShoppingCart.getPaymentIntent() called without a valid user.`);
					}

					// Waitlist / partial cart payloads may include paymentIntent metadata without a client_secret.
					// Stripe Elements (e.g. saved-card CVC) requires a real secret — refresh via GET/set-user instead of no-op.
					if (this.isObject(paymentIntent) && this.isString(paymentIntent.clientSecret, 1)) {
						return paymentIntent;
					}

					if (this.isNull(setUserPromise)) {
						if (checkoutMode === `USER`) {
							setUserPromise = XHR.ajax({
								// cache a promise, in case it's called again quickly in succession
								// associate user with cart, ensure to get a paymentIntent if necessary
								url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=set-user`,
								type: `PUT`,
								REST: true,
								data: {},
								then: (response) =>
									this.syncCart({
										response,
										then: () => response.paymentIntent,
									}),
								fail: (reason) => {
									this.init(); // unrecoverable, reset the cart
									throw reason; // throw for further handling
								},
								fin: () => {
									setUserPromise = null; // reset the cached promise
								},
							});
						} else {
							// use new endpoint for guest checkout, ?action=set-user won't work for guests
							setUserPromise = XHR.ajax({
								// cache a promise, in case it's called again quickly in succession
								// associate user with cart, ensure to get a paymentIntent if necessary
								url: `/api/events/${event.id}/shoppingcarts/${cart.token}`,
								type: `GET`,
								then: (response) =>
									this.syncCart({
										response,
										then: () => response.paymentIntent,
									}),
								fail: (reason) => {
									this.init(); // unrecoverable, reset the cart
									throw reason; // throw for further handling
								},
								fin: () => {
									setUserPromise = null; // reset the cached promise
								},
							});
						}
					} else {
						this.log({
							message: `ShoppingCart.getPaymentIntent() tried calling ?action=get-user while a request was still active`,
						});
					}

					return setUserPromise;
				},
			},

			setTenderType: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token } = cart;
					const { tenderType } = options;
					const url = `/api/events/${eventId}/shoppingcarts/${token}?action=update-payment-method`;
					const body = { tenderType };
					const type = `PUT`;
					const REST = true;
					const promise = XHR.ajax({ body, REST, type, url });

					return this.when({
						promise,
						then: (response) =>
							this.syncCart({
								response,
								then: this.reconcile,
							}),
					});
				},
			},

			/**
			 * Set transaction metadata for the current cart
			 * @param {Object} options
			 * @param {Object} options.transactionMetadata Key/value pairs to set as transaction metadata
			 * @returns {Promise}
			 */
			setTransactionMetadata: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token } = cart;
					const { transactionMetadata } = options;
					const url = `/api/events/${eventId}/shoppingcarts/${token}?action=update-transaction-metadata`;
					const body = { transactionMetadata };
					const type = `PUT`;
					const REST = true;
					return XHR.ajax({ body, REST, type, url });
				},
			},

			addPromo: {
				async: true,
				public: true,
				defaults: {
					ignoreErrors: false,
					force: false,
				},
				value(context, options) {
					const { XHR, data } = this;
					const code = options.code.toUpperCase();
					const { cart, event } = data;
					const { force } = options;

					if (buyRequestPlaced && !force) {
						// too late to try a promo
						return;
					}

					return XHR.ajax({
						type: `PUT`,
						REST: true,
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=apply-promo`,
						ignoreErrors: options.ignoreErrors,
						body: { promoCode: code },
						then: (response) => {
							cart.promoCode = code;

							return this.syncCart({
								response,
								then: () => {
									this.reconcile();
									return response;
								},
							});
						},
						fail: (reason) => {
							var docType, json;

							if (this.isObject(reason.responseJSON)) {
								json = reason.responseJSON;
								docType = json.docType;
							}

							switch (docType) {
								case `STANDARD_ERROR`:
									switch (json.code) {
										case 1029: // max number of promo code attempts
											this.init();
											break;
									}
									break;

								default: // Unrecognized docType
							}

							throw reason; // re-throw for the caller to detect
						},
					});
				},
			},

			removePromo: {
				async: true,
				public: true,
				value(context, options) {
					return this.addPromo({
						code: ``,
					});
				},
			},

			setIncompletePromo: {
				public: true,
				value(context, value) {
					// used to track whether the user might have a dangling coupon code
					this.data.incomplete_promo = value;
				},
			},

			updateUserMarketingOptIn: {
				async: true,
				public: true,
				value(context, options) {
					const { eventId, token } = this.data.cart;
					const { userMarketingOptIn } = options;

					return this.XHR.ajax({
						type: `PUT`,
						REST: true,
						url: `/api/events/${eventId}/shoppingcarts/${token}?action=user-marketing-opt-in`,
						data: { userMarketingOptIn },
					});
				},
			},

			applyAddOns: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart } = data;
					const { products } = cart;
					const addOns = this.filter(products, ({ quantity, type }) => type === `ADD_ON` && quantity > 0);
					const saleItems = [];

					if ([`next-add-on-pack`, `previous-add-on-pack`].includes(options.action)) {
						return this.reconcile();
					}

					this.each(addOns, (product) => {
						const { deliveryConfiguration, id, productType, selections } = product;
						const holdId = productType === `HOLD` ? id : null;
						let additionalDeliveryType;
						let deliveryType;

						if (this.isObject(deliveryConfiguration)) {
							deliveryType = deliveryConfiguration.deliveryType;
							additionalDeliveryType = deliveryConfiguration.additionalDeliveryType;
						} else {
							deliveryType = null;
							additionalDeliveryType = null;
						}

						const denormalizedSelections = this.PriceLevels.denormalizeProductSelections({
							product,
							selections,
						});

						this.each(denormalizedSelections, (selection) => {
							const { priceLevelOptionId, priceListId, saleId, tierId } = selection;
							const saleItem = this.find(saleItems, { holdId, priceLevelOptionId, priceListId, saleId });

							if (this.isObject(saleItem)) {
								saleItem.quantity++;
							} else {
								saleItems.push({
									additionalDeliveryType,
									deliveryType,
									holdId,
									priceLevelOptionId,
									priceListId,
									quantity: 1,
									saleId,
									tierId,
								});
							}
						});
					});

					if (options.action === `decline-add-ons`) {
						return XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${cart.eventId}/shoppingcarts/${cart.token}?action=decline-add-ons`,
							body: {},
							then: () => {
								cart.progress.add_ons = true;
								this.reconcile();
							},
						});
					}

					if (saleItems.length > 0) {
						return XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${cart.eventId}/shoppingcarts/${cart.token}?action=apply-add-ons`,
							body: { saleItems },
							then: (response) => {
								const { reconcile: then } = this;
								cart.progress.add_ons = true;

								return this.syncCart({ response, then });
							},
						});
					} else {
						cart.progress.add_ons = true;
						this.reconcile();
					}
				},
			},

			enablePaymentPlan: {
				async: true,
				public: true,
				defaults: {
					block: true,
				},
				value(context, options) {
					const { App, data } = this;
					const { cart } = data;
					const { block } = options;

					if (cart.usePaymentPlan !== true) {
						return this.XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${data.event.id}/shoppingcarts/${cart.token}?action=apply-payment-plan`,
							data: {
								usePaymentPlan: true,
							},
							block,
							then: (response) => {
								// success
								cart.usePaymentPlan = true;
								const paymentPlanFee = this.isObject(cart) && this.isInt(cart.paymentPlanFee) ? cart.paymentPlanFee : 0;

								// fire the "cart-add" event for payment plan
								App.captureInsight({
									eventType: `cart-add`,
									purchaseFlowItem: `Payment Plan`,
									item: {
										id: `payment_plan`,
										name: `Payment Plan`,
										price: paymentPlanFee,
									},
									productList: {},
								});

								return this.syncCart({
									response,
									then: this.reconcile,
								});
							},
						});
					}
				},
			},

			addOrderProtection: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { quote, token } = options;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { orderProtectionConfiguration, token: cartToken } = cart;
					const { offeringId, offeringName } = orderProtectionConfiguration;
					const isProtected = true;
					const orderProtection = { quote, token, offeringId, offeringName, isProtected };
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=order-protection-opt-in`;
					const body = { orderProtection };
					const type = `PUT`;
					const REST = true;

					const response = await XHR.ajax({ body, REST, type, url });
					cart.orderProtectionAdded = true;
					await this.syncCart({ response });
				},
			},

			confirmOrderProtection: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the insurance step complete
					progress.order_protection = true;
					this.reconcile();
				},
			},

			revisitOrderProtection: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the insurance step incomplete
					progress.order_protection = false;
					this.reconcile();
				},
			},

			removeOrderProtection: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=order-protection-opt-out`;
					const REST = true;
					const body = {};
					const type = `PUT`;
					const response = await XHR.ajax({ body, REST, type, url });

					cart.orderProtectionAdded = false;

					await this.syncCart({ response });
				},
			},

			disableOrderProtection: {
				public: true,
				value(context) {
					const { data } = this;
					const { cart } = data;
					const { orderProtection } = cart;

					cart.requirements.order_protection = false;
					cart.progress.order_protection = false;
					data.skipOrderProtection = true;

					if (this.isObject(orderProtection)) {
						this.removeOrderProtection();
					} else {
						this.reconcile();
					}
				},
			},

			applyAvailableCredits: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=apply-available-credits`;
					const type = `PUT`;
					const REST = true;
					const body = {};

					const response = await XHR.ajax({ body, REST, type, url });
					await this.syncCart({ response });
				},
			},

			removeAppliedCredits: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=remove-applied-credits`;
					const type = `PUT`;
					const REST = true;
					const body = {};

					const response = await XHR.ajax({ body, REST, type, url });
					await this.syncCart({ response });
				},
			},

			confirmCredits: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the credits step complete
					progress.credits = true;
					this.reconcile();
				},
			},

			revisitCredits: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the credits step incomplete
					progress.credits = false;
					this.reconcile();
				},
			},

			confirmRepresentatives: {
				public: true,
				async: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { representatives } = options;
					const { progress, token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=apply-representatives`;
					const type = `PUT`;
					const body = {
						representatives,
					};
					const REST = true;

					await XHR.ajax({ body, REST, type, url });

					// mark the representatives step complete
					progress.representatives = true;
					this.reconcile();
				},
			},

			revisitRepresentatives: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the representatives step incomplete
					progress.representatives = false;
					this.reconcile();
				},
			},

			addAutoRenewal: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=season-auto-renewal-opt-in`;
					const type = `PUT`;
					const body = {
						autoRenewalState: `OPT_IN`,
					};
					const REST = true;

					const response = await XHR.ajax({ body, REST, type, url });
					cart.autoRenewalAdded = true;
					await this.syncCart({ response });
				},
			},

			removeAutoRenewal: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=season-auto-renewal-opt-in`;
					const type = `PUT`;
					const body = {
						autoRenewalState: `OPT_OUT`,
					};
					const REST = true;

					const response = await XHR.ajax({ body, REST, type, url });
					cart.autoRenewalAdded = false;
					await this.syncCart({ response });
				},
			},

			confirmAutoRenewal: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the auto renewal step complete
					progress.auto_renewal = true;
					this.reconcile();
				},
			},

			revisitAutoRenewal: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the auto renewal step incomplete
					progress.auto_renewal = false;
					this.reconcile();
				},
			},

			confirmPhone: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the phone step complete
					progress.phone = true;
					this.reconcile();
				},
			},

			revisitPhone: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the phone step incomplete
					progress.phone = false;
					this.reconcile();
				},
			},

			confirmPaymentTerminal: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the payment terminal step complete
					progress.payment_terminal = true;
					this.reconcile();
				},
			},

			revisitPaymentTerminal: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the payment terminal step incomplete
					progress.payment_terminal = false;
					this.reconcile();
				},
			},

			isValidSelection: {
				value({ product, selection }) {
					const { isLevels, isReserved } = product;

					if (isReserved) {
						// seatLabel value required for every selection
						if (!this.isString(selection.seatLabel)) {
							return false;
						}
					}

					if (isLevels) {
						// price level required for every selection
						if (!this.isInt(selection.priceLevelOptionId)) {
							return false;
						}
					}

					return true;
				},
			},

			updateProducts: {
				async: true,
				public: true,
				defaults: {
					removeSeats: true,
				},
				async value(context, options) {
					const { App, Utils, data, promises } = this;
					const { removeSeats } = options;
					const { cart: cartPromise } = promises;

					const cart = await cartPromise; // ensure the cart is ready before proceeding

					const { isUpgrades, purchaseFlow } = data;
					const { products } = cart;
					let { products: updates } = options;
					let launchPriceLevelsOverlay = false;

					if (!this.isArray(updates)) {
						// undefined, null, etc.
						updates = this.isObject(updates) ? [updates] : []; // default to empty array for undefined, null, etc.
					}

					this.each(updates, (update) => {
						const {
							color,
							formattedPriceResale: updatedFormattedPriceResale,
							id,
							productType,
							resalePriceBreakout,
							resaleState,
							state,
						} = update;
						const product = this.find(products, { id, productType });
						const holdId = productType === `HOLD` ? id : null;
						const holdName = productType === `HOLD` ? product.name : null;
						let { quantity, selections } = update;

						if (!this.isObject(product)) {
							// no matching sale found
							return; // skip this update
						}

						if (this.isArray(selections)) {
							if (this.isInt(quantity)) {
								if (quantity !== selections.length) {
									throw new Error(`quantity ${quantity} doesn't match selections.length ${selections.length}`);
								}
							} else {
								quantity = selections.length;
							}

							// prevent possible reference issues, e.g. the same object being pushed into selections multiple times
							selections = this.map(selections, (selection) => this.clone(selection));
						} else if (quantity === 0) {
							if (this.isArray(selections, 1)) {
								throw new Error(`quantity ${quantity} doesn't match selections.length ${selections.length}`);
							} else {
								selections = [];
							}
						}

						this.each(selections, (selection) => this.assign(selection, { holdId, holdName }));

						const { category, displayState, isLevels, isReserved, maxTicketsAllowed } = product;
						const currentSelections = this.clone(product.selections);
						const isQueued = displayState === `QUEUED`;

						if (this.isInt(quantity, 0)) {
							// NOTE: updates can also be updated state, color, etc.

							// populated with newly encountered selections
							const newSelections = [];
							// calculated by chipping away at the list as selections are encountered
							const removedSelections = this.clone(currentSelections);
							// save a quick-reference list of any pending selections
							const pendingSelections = this.filter(removedSelections, { state: `PENDING` });

							// iterate over the updated selections
							this.each(selections, (selection) => {
								let existingSelection;

								if (Number.isSafeInteger(selection.id)) {
									// can look up by id
									existingSelection = this.find(currentSelections, { id: selection.id });
								} else if (pendingSelections.length > 0) {
									// new selection, use any pending ones first
									existingSelection = pendingSelections.shift();
								}

								if (this.isObject(existingSelection)) {
									const allValues = this.assign({}, existingSelection, selection);
									this.assign(selection, allValues);
								} else {
									// brand new selection, assign it the default values
									this.makeProductSelection(selection);
								}

								const { id: selectionId } = selection;

								if (productType === `SALE`) {
									selection.saleId = product.id;
									selection.tierId = product.tiers[0].id;
								} else if (productType === `HOLD`) {
									if (this.isInt(selection.saleId)) {
										const sale = this.find(products, { id: selection.saleId, productType: `SALE` });
										const { tiers } = sale;

										selection.tierId = tiers[0].id;
									} else {
										selection.saleId = null;
										selection.tierId = null;
									}
								}

								if (this.isObject(existingSelection)) {
									if (
										// previously invalid, now valid
										!this.isValidSelection({ product, selection: existingSelection }) &&
										this.isValidSelection({ product, selection })
									) {
										// counts as newly inserted
										newSelections.push(selection);
									}

									// exclude from the list of removed items
									this.extractWhere(removedSelections, { id: selectionId });
								} else {
									// newly added selection
									if (this.isValidSelection({ product, selection })) {
										// should be counted for analytics
										newSelections.push(selection);
									}
								}

								// seat-related calculation
								if (isReserved && !isQueued) {
									const { state: selectionState } = selection;

									if (selectionState === `ACTIVE`) {
										if (typeof selection.seatLabel !== `string`) {
											// no seat provided, "find best available" situation
											selection.state = `FBA`;
										}
									} else if (selectionState === `PENDING`) {
										if (this.isString(selection.seatLabel, 1)) {
											// a specific seat is reserved
											selection.state = `ACTIVE`;
										}
									}
								}
							});

							// price calculation
							this.each(selections, (selection) => {
								const { priceLevelOptionId, saleId, state: selectionState } = selection;
								const extraDetails = [];
								let priceLevelOptionName = null;
								let holdName = null;
								let holdId = null;
								let trackingId;
								let price;
								let name;

								// If product's priceListId is different from the selection's priceListId, skip the price re-calculation.
								// Newly added selections should have matching priceListId so the price calculation will be run for them.
								// This allows tickets in cart with a specific price code to keep the same price when the agent changes price codes.
								if (product.priceListId !== selection.priceListId) {
									return; // skip this selection since it's not for the current price list
								}

								if (productType === `SALE`) {
									holdId = null;
									name = product.name;
									trackingId = product.trackingId;

									if (isLevels) {
										if (this.isInt(priceLevelOptionId)) {
											const tier = product.tiers[0];
											const priceLevel = this.find(tier.priceLevels, { priceLevelOptionId });

											price = priceLevel.price;
											priceLevelOptionName = priceLevel.priceLevelOptionName;
											extraDetails.push(priceLevelOptionName);
										} else {
											price = product.advertisedPrice;

											if (selectionState === `ACTIVE` || selectionState === `FBA`) {
												launchPriceLevelsOverlay = true;
											}
										}
									} else {
										price = product.price;
									}
								} else if (productType === `HOLD`) {
									holdId = product.id;
									holdName = product.name;

									if (this.isInt(saleId)) {
										const saleAssignment = this.find(product.saleAssignments, { saleId });
										const sale = this.find(products, { id: saleId, productType: `SALE` });

										name = sale.name;
										trackingId = sale.trackingId;

										if (this.isString(product.name, 1)) {
											extraDetails.push(product.name);
										}

										if (isLevels) {
											if (this.isInt(priceLevelOptionId)) {
												// look up from the hold's own price levels first (consistent with isLevels source);
												// the sale's tier is a fallback for the traditional price level override case
												const holdPriceLevel = this.find(product.priceLevels, { value: priceLevelOptionId });
												const salePriceLevel = this.find(sale.tiers[0].priceLevels, { priceLevelOptionId });

												if (this.isObject(holdPriceLevel)) {
													// matching price level found in the hold's options
													priceLevelOptionName = holdPriceLevel.label;
													// price may be null when the hold defers to the sale's tier price for this level
													price = holdPriceLevel.price != null ? holdPriceLevel.price : salePriceLevel?.price;
												} else {
													// no match found, fall back to the sale's price level options
													priceLevelOptionName = salePriceLevel?.priceLevelOptionName;
													price = salePriceLevel?.price;
												}

												if (this.isString(priceLevelOptionName, 1)) {
													extraDetails.push(priceLevelOptionName);
												}
											} else {
												if (selectionState === `ACTIVE` || selectionState === `FBA`) {
													launchPriceLevelsOverlay = true;
												}
											}
										}

										if (this.isUndefined(price)) {
											// didn't obtain from a selected price level
											if (this.isFinite(saleAssignment.price, 0)) {
												// the sale assignment has a price override
												price = saleAssignment.price;
											} else {
												// inherit the selected sale's price
												price = sale.advertisedPrice;
											}
										}
									} else {
										// no sale selected, use default for the hold
										name = product.name;
										price = product.advertisedPrice;

										if (!this.isInt(priceLevelOptionId)) {
											// check to see if any sale assignments have priceLevelOptionAssignments
											const saleAssignmentWithLevels = this.find(product.saleAssignments, (saleAssignment) => {
												const { priceLevelOptionAssignments } = saleAssignment;
												return this.isArray(priceLevelOptionAssignments, 1);
											});
											if (saleAssignmentWithLevels) {
												launchPriceLevelsOverlay = true;
											}
										}
									}
								}

								if (extraDetails.length > 0) {
									const detailsString = extraDetails.join(`, `);
									name += ` [${detailsString}]`;
								}

								this.assign(selection, {
									category,
									holdName,
									holdId,
									name,
									price,
									priceLevelOptionName,
									trackingId,
								});
							});

							if (this.isInt(maxTicketsAllowed)) {
								if (selections.length > maxTicketsAllowed) {
									while (selections.length > maxTicketsAllowed) {
										selections.pop();
									}

									App.notifyUser({
										message: `The maximum number of selections has been reached`,
										type: `warning`,
									});
								}
							}

							if (isUpgrades && !this.isArray(selections, 1) && this.isUpgradesReleasingSeats()) {
								selections = currentSelections;
							}

							product.selections = selections;

							const stateFilter = ({ state }) => state !== `REMOVING`;
							const visibleSelections = this.filter(selections, stateFilter);
							product.quantity = visibleSelections.length; // don't include removed selections

							if (launchPriceLevelsOverlay && !isUpgrades) {
								this.requestMissingPriceLevels();
							}

							if (newSelections.length > 0) {
								const groupedSelections = Utils.groupBy(newSelections, (diff) => {
									const { priceLevelOptionId, priceListId, saleId } = diff;
									return `${saleId}_${holdId}_${priceLevelOptionId}_${priceListId}`;
								});

								const eventType = `cart-add`;
								this.each(groupedSelections, (grouped) => {
									App.captureInsight({ eventType, productId: id, productType, selections: grouped, purchaseFlow });
								});
							}

							if (removedSelections.length > 0) {
								// existing selections were removed
								// release temporarily held seats
								if (removeSeats) {
									const filterBySeatLabel = ({ seatLabel }) => this.isString(seatLabel, 1);
									const removedSeatLabelSelections = this.filter(removedSelections, filterBySeatLabel);

									if (removedSeatLabelSelections.length > 0) {
										// removed selections had seat labels
										const seats = [];

										this.each(removedSeatLabelSelections, (removedSelection) => {
											// keep track of the seat info for deselection
											const { seatLabel, priceLevelOptionId: ticketType } = removedSelection;
											seats.push({ seatLabel, ticketType });

											// keep in the product selections array for a small buffer period
											selections.push(removedSelection);

											// remove from the list after a short buffer delay
											this.queueSelectionRemoval({
												productId: id,
												productType,
												selection: removedSelection,
											});
										});

										// Deselect from the chart instead of calling the REST API directly.
										// The chart handles partial GA quantity, orphan seat validation, etc.
										// The chart is always present when reserved removal is enabled.
										this.ReservedSeating.deselectSeats({ seats });
									}
								}

								// analytics tracking for item removal
								const isValidFilter = (selection) => this.isValidSelection({ product, selection });
								const validRemovedSelections = this.filter(removedSelections, isValidFilter);
								const groupedSelections = Utils.groupBy(validRemovedSelections, (diff) => {
									const { priceLevelOptionId, priceListId, saleId } = diff;
									return `${saleId}_${holdId}_${priceLevelOptionId}_${priceListId}`;
								});

								const eventType = `cart-remove`;

								this.each(groupedSelections, (grouped) => {
									App.captureInsight({ eventType, productId: id, productType, selections: grouped, purchaseFlow });
								});
							}
						}

						if (this.isDefined(color)) {
							// could be null
							product.color = color;
						}

						if (this.isString(state, 1)) {
							// a state override was passed
							stateOverrides[product.trackingId] = state;
						} else if (state === null) {
							// null was passed, remove any overrides
							const override = stateOverrides[product.trackingId];

							if (this.isString(override, 1)) {
								delete stateOverrides[product.trackingId];
							}
						}

						if (this.isString(resaleState, 1)) {
							// a resale state override was passed (e.g. seating chart update)
							product.resaleState = resaleState;
						} else if (resaleState === null) {
							// null was passed, remove any overrides
							product.resaleState = product.originalResaleState;
						}

						if (this.isObject(resalePriceBreakout)) {
							// price range from resale listings (e.g. seating chart update for RESERVED products)
							product.resalePriceBreakout = resalePriceBreakout;
						} else if (resalePriceBreakout === null) {
							// null was passed, remove any overrides
							product.resalePriceBreakout = null;
						}

						if (this.isString(updatedFormattedPriceResale, 1)) {
							// formatted resale price computed from actual listing data (overrides stale server value)
							product.formattedPriceResale = updatedFormattedPriceResale;
						}
					});

					this.reconcile();
				},
			},

			requestMissingPriceLevels: {
				async: true,
				value() {
					const { App, Events, ReservedSeating, data } = this;
					const { cart, event } = data;
					const { products, upgrades } = cart;
					const { currency, theme } = event;
					const unselectedPriceLevels = [];

					this.each(products, (product) => {
						const { displayState, isLevels, quantity, selections } = product;

						if (!isLevels || quantity === 0) {
							// not using price levels, or selections to be made
							return;
						}

						const selectableStates = [`OPEN`, `QUEUED`];
						if (!selectableStates.includes(displayState)) {
							// not in a state where selections can be made
							return;
						}

						const { id: productId, name, priceLevels, productType } = product;
						const relevantStates = [`ACTIVE`, `FBA`];
						const relevantSelectionFilter = ({ state }) => relevantStates.includes(state);
						const relevantSelections = this.filter(selections, relevantSelectionFilter);

						this.each(relevantSelections, (selection) => {
							const { id: selectionId, priceLevelOptionId, seatLabel } = selection;

							// check if hold type is open
							if (productType === `HOLD` && product.holdType === `OPEN`) {
								// check saleassignment for the sale selected
								const saleAssignment = this.find(product.saleAssignments, { saleId: selection.saleId });
								if (!this.isArray(saleAssignment?.priceLevelOptionAssignments, 1)) {
									return; // skip this selection
								}
							}

							if (!this.isInt(priceLevelOptionId)) {
								const seatSummary = ReservedSeating.makeSeatSummary(selection);

								if (this.isArray(upgrades, 1)) {
									const upgrade = this.find(upgrades, (upgrade) => {
										return this.isEqual(upgrade.selection?.id, selectionId);
									});

									if (this.isObject(upgrade)) {
										const upgradesPriceLevels = priceLevels
											.map((priceLevel) => {
												const option = this.find(upgrade.targetOptions, {
													saleId: selection.saleId,
													priceLevelOptionId: priceLevel.value,
												});

												if (this.isObject(option)) {
													const formattedPriceDifference = Events.formatPriceDifference({
														currency,
														freeLabel: `Equal Value`,
														priceDifference: option.priceDifference,
													});

													return { ...priceLevel, formattedPriceDifference };
												}
											})
											.filter((priceLevel) => this.isObject(priceLevel));

										unselectedPriceLevels.push({
											name,
											priceLevels: upgradesPriceLevels,
											productId,
											productType,
											seatLabel,
											seatSummary,
											selectionId,
										});
									}
								} else {
									unselectedPriceLevels.push({
										name,
										priceLevels,
										productId,
										productType,
										seatLabel,
										seatSummary,
										selectionId,
									});
								}
							}
						});
					});

					if (unselectedPriceLevels.length === 0) {
						return;
					}

					let succeeded = false;

					// fires when the overlay is closed
					const onDie = () => {
						if (!succeeded) {
							const unselectedIds = this.map(unselectedPriceLevels, `selectionId`);
							this.removeProductSelections({ ids: unselectedIds });
						}
					};

					// fires when the overlay obtains the price level selections
					const onSuccess = (priceLevelOptionIds) => {
						// a list of price level selections was returned
						const updates = [];
						const selectionIds = [];
						this.each(unselectedPriceLevels, (unselected, i) => {
							const priceLevelOptionId = priceLevelOptionIds[i];
							const { productId, productType, selectionId } = unselected;
							const product = this.find(cart.products, { id: productId, productType });
							const { selections } = product;
							const selection = this.find(selections, { id: selectionId });
							const existing = this.find(updates, { id: productId, productType });

							selectionIds.push(selectionId);

							this.assign(selection, { priceLevelOptionId });

							if (this.isObject(existing)) {
								const quantity = selections.length;
								this.assign(existing, { quantity, selections });
							} else {
								// add to the list of updates
								const id = productId;
								updates.push({ id, productType, selections });
							}
						});

						this.updateProducts({ products: updates });
						succeeded = true;
					};

					App.loadOverlay({
						type: `SelectPriceLevels`,
						setup: {
							callbacks: {
								die: onDie,
								success: onSuccess,
							},
							theme,
							unselectedPriceLevels,
						},
					});
				},
			},

			/**
			 * Calculate the increment amount for a HOLD product's price level
			 * Traverses saleAssignments to find the largest minimum constraint across related SALEs
			 * @private
			 * @param {Object} options
			 * @param {Object} options.priceLevel - The HOLD product's price level
			 * @param {number} options.levelQuantity - Current quantity for this level on the HOLD
			 * @param {number} options.priceLevelOptionId - The price level option ID
			 * @param {Array} options.saleAssignments - The HOLD's sale assignments
			 * @param {Array} options.allProducts - All products in the cart (to look up SALEs)
			 * @returns {number} Number of tickets to add
			 */
			calculateHoldPriceLevelIncrementAmount: {
				private: true,
				value(options) {
					const { priceLevel, levelQuantity, priceLevelOptionId, productTicketMultiple, saleAssignments, allProducts } =
						options;
					const { maxTicketsAllowed: levelMaxTicketsAllowed, ticketMultiple: levelTicketMultiple } = priceLevel;

					// Calculate effective multiple: if both have multiples, use LCM to satisfy both
					let effectiveTicketMultiple;
					if (this.isInt(levelTicketMultiple, 1) && this.isInt(productTicketMultiple, 1)) {
						const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
						const lcm = (a, b) => (a * b) / gcd(a, b);
						effectiveTicketMultiple = lcm(levelTicketMultiple, productTicketMultiple);
					} else if (this.isInt(levelTicketMultiple, 1)) {
						effectiveTicketMultiple = levelTicketMultiple;
					} else {
						effectiveTicketMultiple = productTicketMultiple;
					}

					const effectiveMultiple = this.isInt(effectiveTicketMultiple, 1) ? effectiveTicketMultiple : 1;
					let numberToAdd = effectiveMultiple;

					// PRIORITY 1: Correct invalid multiple state before normal increment logic
					// Prevents infinite loop when user picks arbitrary seat count
					if (this.isInt(effectiveTicketMultiple, 2) && levelQuantity > 0) {
						const remainder = levelQuantity % effectiveTicketMultiple;
						if (remainder !== 0) {
							// Invalid multiple - add partial to complete current multiple
							numberToAdd = effectiveTicketMultiple - remainder;

							// Verify we don't exceed maximum while correcting
							if (this.isInt(levelMaxTicketsAllowed, 0)) {
								const maxAddable = levelMaxTicketsAllowed - levelQuantity;
								if (numberToAdd > maxAddable) {
									// Can't correct without exceeding max - return 0 (no-op)
									return 0;
								}
							}

							// Return early - correction takes absolute priority
							return numberToAdd;
						}
					}

					// PRIORITY 2: Handle sale assignment minimums (only if currently valid or at 0)
					this.each(saleAssignments, (assignment) => {
						// traverse all sales, looking for any price levels with minimums
						const sale = this.find(allProducts, { id: assignment.saleId, productType: `SALE` });
						const { priceLevels: salePriceLevels } = sale;
						const salePriceLevel = this.find(salePriceLevels, { value: priceLevelOptionId }) ?? {}; // this level may not exist for the sale in the case of ffpc so fallback to empty object
						const saleSelections = this.filter(sale.selections, { priceLevelOptionId });
						const { length: saleLevelQuantity } = saleSelections;
						const { minTicketsAllowed } = salePriceLevel;

						if (this.isInt(minTicketsAllowed, 2)) {
							const difference = minTicketsAllowed - saleLevelQuantity - levelQuantity;
							// Round up to next valid multiple that reaches or exceeds minimum
							const saleNumberToAdd = Math.ceil(difference / effectiveMultiple) * effectiveMultiple;

							if (saleNumberToAdd > numberToAdd) {
								numberToAdd = saleNumberToAdd;
							}
						}
					});

					// Cap at level maximum if present
					if (this.isInt(levelMaxTicketsAllowed, 0)) {
						const maxAddable = levelMaxTicketsAllowed - levelQuantity;
						if (numberToAdd > maxAddable) {
							// Find largest valid multiple that fits within maximum
							numberToAdd = Math.floor(maxAddable / effectiveMultiple) * effectiveMultiple;
						}
					}

					return numberToAdd;
				},
			},

			/**
			 * Calculate the decrement amount for a HOLD product's price level
			 * Traverses saleAssignments to determine if removing quantity would violate SALE minimums
			 * @private
			 * @param {Object} options
			 * @param {Object} options.priceLevel - The price level object
			 * @param {number} options.levelQuantity - Current quantity for this level on the HOLD
			 * @param {number} options.priceLevelOptionId - The price level option ID
			 * @param {string} options.priceListId - The price list ID for filtering selections
			 * @param {Array} options.saleAssignments - The HOLD's sale assignments
			 * @param {Array} options.allProducts - All products in the cart (to look up SALEs)
			 * @returns {number} Number of tickets to remove
			 */
			calculateHoldPriceLevelDecrementAmount: {
				private: true,
				value(options) {
					const {
						priceLevel,
						levelQuantity,
						priceLevelOptionId,
						priceListId,
						productTicketMultiple,
						saleAssignments,
						allProducts,
					} = options;
					const { ticketMultiple: levelTicketMultiple } = priceLevel;

					// Calculate effective multiple: if both have multiples, use LCM to satisfy both
					let effectiveTicketMultiple;
					if (this.isInt(levelTicketMultiple, 1) && this.isInt(productTicketMultiple, 1)) {
						const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
						const lcm = (a, b) => (a * b) / gcd(a, b);
						effectiveTicketMultiple = lcm(levelTicketMultiple, productTicketMultiple);
					} else if (this.isInt(levelTicketMultiple, 1)) {
						effectiveTicketMultiple = levelTicketMultiple;
					} else {
						effectiveTicketMultiple = productTicketMultiple;
					}

					const effectiveMultiple = this.isInt(effectiveTicketMultiple, 1) ? effectiveTicketMultiple : 1;
					let numberToRemove = effectiveMultiple;

					// PRIORITY 1: Correct invalid multiple state before normal decrement logic
					// Prevents infinite loop when user tries to escape invalid state
					if (this.isInt(effectiveTicketMultiple, 2) && levelQuantity > 0) {
						const remainder = levelQuantity % effectiveTicketMultiple;
						if (remainder !== 0) {
							// Invalid multiple - remove partial to reach previous valid multiple
							numberToRemove = remainder;
							const correctedQuantity = levelQuantity - remainder;

							// Check if correction violates sale minimums (HOLD-specific complexity)
							this.each(saleAssignments, (assignment) => {
								const sale = this.find(allProducts, { id: assignment.saleId, productType: `SALE` });
								const { priceLevels: salePriceLevels } = sale;
								const salePriceLevel = this.find(salePriceLevels, { value: priceLevelOptionId }) ?? {};
								const saleSelections = this.filter(sale.selections, { priceLevelOptionId, priceListId });
								const { length: saleLevelQuantity } = saleSelections;
								const { minTicketsAllowed } = salePriceLevel;
								const totalAfterCorrection = correctedQuantity + saleLevelQuantity;

								if (
									this.isInt(minTicketsAllowed, 1) &&
									totalAfterCorrection > 0 &&
									totalAfterCorrection < minTicketsAllowed
								) {
									// Correcting would violate sale minimum - remove all instead
									numberToRemove = levelQuantity;
								}
							});

							// Return early - correction takes absolute priority
							return numberToRemove;
						}
					}

					// PRIORITY 2: Standard decrement logic (only runs if currently valid)
					this.each(saleAssignments, (assignment) => {
						// traverse all sales, looking for any price levels with minimums
						const sale = this.find(allProducts, { id: assignment.saleId, productType: `SALE` });
						const { priceLevels: salePriceLevels } = sale;
						const salePriceLevel = this.find(salePriceLevels, { value: priceLevelOptionId }) ?? {}; // this level may not exist for the sale in the case of ffpc so fallback to empty object
						const saleSelections = this.filter(sale.selections, { priceLevelOptionId, priceListId });
						const { length: saleLevelQuantity } = saleSelections;
						const { minTicketsAllowed } = salePriceLevel;
						const totalQuantity = levelQuantity + saleLevelQuantity;

						if (totalQuantity <= minTicketsAllowed) {
							const saleNumberToRemove = levelQuantity;

							if (saleNumberToRemove > numberToRemove) {
								numberToRemove = saleNumberToRemove;
							}
						}
					});

					// Check if removing would leave invalid quantity
					const remaining = levelQuantity - numberToRemove;
					if (remaining > 0 && remaining % effectiveMultiple !== 0) {
						// Remaining quantity would violate ticketMultiple, so remove all instead
						numberToRemove = levelQuantity;
					}

					return numberToRemove;
				},
			},

			incrementProduct: {
				public: true,
				defaults: {
					priceLevelOptionId: null,
					saleId: null,
				},
				value(context, options) {
					const { Utils, data } = this;
					const { holdId, id, priceLevelOptionId, productType: optionsProductType } = options;
					const { cart } = data;
					const { products } = cart;
					let productType = optionsProductType;
					let productQuery = { id, productType };
					const saleId = productType === `SALE` ? id : options.saleId;

					if (this.isInt(holdId)) {
						productType = `HOLD`;
						productQuery = { id: holdId, productType: `HOLD` };
					}

					const product = this.find(products, productQuery);
					const { isLevels, selections: productSelections, priceListId, priceCode } = product;
					const selections = this.clone(productSelections);
					const { length: quantity } = selections;
					let selection = { priceLevelOptionId, priceListId, priceCode, saleId };

					if (this.isInt(holdId)) {
						const holdProduct = this.find(products, { id: holdId, productType: `HOLD` });
						const holdName = holdProduct?.name ?? null;
						selection = this.assign(selection, { holdId, holdName });
					}

					if (isLevels) {
						// price levels in use
						// constraints can involve splitting between 1 Adult + 1 Child = 2 tickets, etc.

						if (this.isInt(priceLevelOptionId)) {
							// price level was specified
							const priceLevel = this.find(product.priceLevels, { value: priceLevelOptionId });
							const levelSelections = this.filter(selections, { priceLevelOptionId });
							const { length: levelQuantity } = levelSelections;
							let numberToAdd = 1;

							if (productType === `SALE`) {
								numberToAdd = this.calculateSalePriceLevelIncrementAmount({
									priceLevel,
									levelQuantity,
								});

								const indices = Utils.range(0, numberToAdd);
								this.each(indices, () => selections.push(selection));
							} else if (productType === `HOLD`) {
								// NOTE: this flow is used to insert "find best available" quantity into cart for reserved products
								// As of now, all HOLD products are reserved

								numberToAdd = this.calculateHoldPriceLevelIncrementAmount({
									priceLevel,
									levelQuantity,
									priceLevelOptionId,
									productTicketMultiple: product.ticketMultiple,
									saleAssignments: product.saleAssignments,
									allProducts: products,
								});

								const indices = Utils.range(0, numberToAdd);
								this.each(indices, () => selections.push(selection));
							}
						}
					}

					if (!(isLevels && this.isInt(priceLevelOptionId))) {
						const added = this.calculateProductIncrementAmount({ product, currentQuantity: quantity });
						const indices = Utils.range(0, added);
						this.each(indices, () => selections.push(selection));
					}

					this.updateProducts({
						products: [{ id: this.isInt(holdId) ? holdId : id, productType, selections }],
					});
				},
			},

			decrementProduct: {
				public: true,
				defaults: {
					priceLevelOptionId: null,
					saleId: null,
				},
				value(context, options) {
					const { Utils, data } = this;
					const { holdId, id, priceLevelOptionId, productType: optionsProductType, saleId } = options;
					const { cart } = data;
					const { products } = cart;
					let productType = optionsProductType;
					let productQuery = { id, productType };

					if (this.isInt(holdId)) {
						productType = `HOLD`;
						productQuery = { id: holdId, productType: `HOLD` };
					}

					const product = this.find(products, productQuery);
					const { isLevels, selections: productSelections, priceListId } = product;
					const selections = this.clone(productSelections);

					if (isLevels) {
						if (this.isInt(priceLevelOptionId)) {
							// price level provided

							const priceLevel = this.find(product.priceLevels, { value: priceLevelOptionId });
							const matchingSelections = this.filter(selections, { priceLevelOptionId, priceListId });
							const levelQuantity = matchingSelections.length;
							let numberToRemove = 1;

							if (productType === `SALE`) {
								numberToRemove = this.calculateSalePriceLevelDecrementAmount({
									priceLevel,
									levelQuantity,
								});
							} else if (productType === `HOLD`) {
								// NOTE: this flow is used to remove "find best available" quantity from cart for reserved products
								// As of now, all HOLD products are reserved

								numberToRemove = this.calculateHoldPriceLevelDecrementAmount({
									priceLevel,
									levelQuantity,
									priceLevelOptionId,
									priceListId,
									productTicketMultiple: product.ticketMultiple,
									saleAssignments: product.saleAssignments,
									allProducts: products,
								});
							}

							const indices = Utils.range(0, numberToRemove);
							this.each(indices, () => this.extractWhere(selections, { priceLevelOptionId, priceListId }));
						}
					}

					if (!(isLevels && this.isInt(priceLevelOptionId))) {
						const { length: quantity } = selections;
						const numberToRemove = this.calculateProductDecrementAmount({ product, currentQuantity: quantity });
						const indices = Utils.range(0, numberToRemove);

						this.each(indices, () => {
							const query = this.isInt(holdId) ? { priceListId, saleId } : { priceListId };
							const removedSelection = this.extractWhere(selections, query);

							if (!removedSelection) {
								if (this.isInt(holdId)) {
									this.log({
										type: `debug`,
										message: `No selection with matching saleId ${saleId} and priceList id ${priceListId} found to remove for this hold`,
									});
								} else {
									this.log({
										type: `debug`,
										message: `No selection with matching priceList id ${priceListId} found to remove, removing the next available selection`,
									});

									selections.pop();
								}
							}
						});
					}

					this.updateProducts({
						products: [{ id: this.isInt(holdId) ? holdId : id, productType, selections }],
					});
				},
			},

			removeProductSelections: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const products = this.clone(cart.products);
					const ids = this.isArray(options.ids) ? options.ids : [options.id];
					const updates = [];

					this.each(products, (product) => {
						const { id, productType, quantity, selections } = product;
						this.extract(selections, ({ id: selectionId }) => this.includes(ids, selectionId));

						if (quantity !== selections.length) {
							updates.push({ id, productType, selections });
						}
					});

					this.updateProducts({ products: updates });
				},
			},

			queueSelectionRemoval: {
				async value(options) {
					const { data } = this;
					const { cart } = data;
					const { products } = cart;
					const { productId, productType, selection } = options;
					const { id: selectionId } = selection;
					const product = this.find(products, { id: productId, productType });

					selection.state = `REMOVING`;

					await this.defer({ duration: 2000 });

					const { selections } = product;
					this.extract(selections, { id: selectionId });

					this.reconcile();
				},
			},

			/**
			 * Change the price code for a product selection
			 * @param {Object} options
			 * @param {number} options.selectionId - The selection id to change
			 * @param {number} options.priceListId - The new price list id
			 * @param {string} options.priceCode - The new price code
			 *
			 */
			changeProductSelectionPriceCode: {
				public: true,
				async value(_, options) {
					const { data } = this;
					const { cart } = data;
					const products = this.clone(cart.products);
					const { selectionId, priceListId, priceCode } = options;
					const updates = [];

					for (const product of products) {
						const { selections } = product;
						const selection = this.find(product.selections, { id: selectionId });

						if (selection) {
							const { id, productType } = product;
							const { saleId } = selection;

							// get the price of the new price list id
							const priceListEntry = await this.AgentPriceCode.getPriceListEntry({ saleId, priceListId });

							if (!this.isObject(priceListEntry)) {
								throw new Error(`Unable to update to price code ${priceCode}`);
							}

							selection.priceListId = priceListId;
							selection.priceCode = priceCode;
							selection.price = priceListEntry.price;

							updates.push({ id, productType, selections });

							break; // break the for loop
						}
					}

					if (updates.length === 0) {
						throw new Error(`No selection found with id ${selectionId}`);
					}

					await this.updateProducts({ products: updates });
				},
			},

			releaseSeats: {
				async: true,
				async value(options) {
					const { ReservedSeating, data } = this;
					const { seatLabels } = options;

					try {
						await ReservedSeating.releaseSeats({ seatLabels });
					} catch (error) {
						// assume success, swallow error
					}

					data.releasingSeats = false;
					this.reconcile();
				},
			},

			fetch: {
				async: true,
				defaults: {
					ignoreErrors: true,
				},
				value(options) {
					const { XHR } = this;
					const { eventId, ignoreErrors, token } = options;
					const url = `/api/events/${eventId}/shoppingcarts/${token}`;
					const method = `GET`;

					return XHR.ajax({ ignoreErrors, method, url });
				},
			},

			get: {
				async: false, // needs raw access to async options below
				public: true,
				defaults: {
					derive: null,
					subscribe: false,
				},
				value(context, options) {
					const { data } = this;
					const { derive } = options;

					if (options.subscribe) {
						// keep track of the handler functions if this is a subscription
						this.subscribe({
							owner: context,
							then: options.then,
							fail: options.fail,
							derive,
						});
					}

					const cart = this.clone(data.cart);
					if (this.isObject(cart)) {
						cart.productsMap = this.calculateProductsMap(cart.products);
					}
					options.owner = context;
					options.promise = this.isFunction(derive) ? derive(cart) : cart;
					return this.when(options);
				},
			},

			subscribe: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						owner: context,
						derive: null,
					});

					this.subscriptions.push({
						handler: options.then,
						errorHandler: options.fail,
						owner: options.owner,
						derive: options.derive,
						deriveResult: null,
					});
				},
			},

			checkout: {
				clone: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { App, Groups, data } = this;
					const { cart, event, groupSaleId, groupSaleUrlParam, isUpgrades, purchaseFlow } = data;
					const { id: eventId } = event;
					const { trt, saleItems, postTransactionAddOns } = options;
					const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
					const orderProtectionFee =
						this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;

					let promise;

					this.defaults(options, {
						owner: context,
					});

					if (data.checkoutActive) {
						// a checkout is already in flight, e.g. the fan clicked the button more than once
						return;
					}

					data.checkoutActive = true;

					if (this.isArray(postTransactionAddOns, 1)) {
						// Store in data so it persists after syncCart overwrites cart.add_ons
						data.postTransactionAddOns = postTransactionAddOns;
						cart.add_ons = postTransactionAddOns;
						cart.showAddOns = false;
					}

					if (event.captcha) {
						promise = this.require({
							deps: [`Captcha`],
							then: () => {
								const { Captcha } = this;
								const path = App.getPath();

								App.captureInsight({
									eventId,
									eventType: `page-view`,
									pageType: `Checkout`,
									path: `${path}/captcha`,
									title: `Event Page, Captcha (${event.id}: ${event.name})`,
									cart,
									purchaseFlow,
									paymentPlanFee,
									orderProtectionFee,
								});

								return Captcha.getResponseToken();
							},
						});
					} else {
						promise = DUMMY_RECAPTCHA;
					}

					promise = this.when({
						promise,
						then: (recaptchaResponse) => {
							const { cart } = this.data;
							const reserveOptions = { recaptchaResponse };

							if (this.isArray(cart.saleItems, 1) && !cart.completed) {
								// saved cart; return without reserving
								return cart;
							}

							if (this.isString(trt)) {
								reserveOptions.trt = trt;
							} else if (this.isArray(saleItems, 1)) {
								reserveOptions.saleItems = saleItems;
							}

							return this.reserve(reserveOptions);
						},
					});

					promise = this.when({
						promise,
						then: () => {
							const hasAddOns =
								this.isArray(cart.add_ons, 1) || this.isObject(this.find(cart.products, { type: `ADD_ON` }));

							if (hasAddOns && cart.showAddOns !== false) {
								const deferred = this.Deferred();

								App.loadOverlay({
									type: `AddOnsPicker`,
									setup: {
										theme: event.theme,
										eventId: cart.eventId,
										callbacks: {
											success: deferred.resolve, // skipped or added tickets, continue
											die: () => {
												deferred.reject({
													message: `ShoppingCart: User exited AddOnsPicker`,
													suppress: true,
												});
											},
										},
									},
									fail: deferred.reject,
								});

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise,
						then: () => {
							// add-ons stage complete
							var deferred;

							if (cart.requirements.q_config && cart.showWaitlist !== false) {
								// this is a 'queue' order
								deferred = this.Deferred();

								let modalName = `waitlist`;

								if (
									this.isObject(data.event.paymentPlanConfig) &&
									data.event.paymentPlanConfig.paymentPlanRequired &&
									data.event.paymentPlanConfig.waitlistEnabled
								) {
									modalName = `waitlist-and-payment-plan-required`;

									// if payment plan is required and the user is already on a payment plan, skip this step
									if (cart.usePaymentPlan) {
										return deferred.resolve();
									}
								}

								App.loadOverlay({
									// launch the overlay
									type: `CheckoutQueueDetails`,
									defer: false, // show immediately
									replace: false, // keep the current overlay active
									setup: {
										addBack: true,
										modalName,
										callbacks: {
											success: deferred.resolve,
											die: () => {
												deferred.reject({
													message: `ShoppingCart: User exited CheckoutQueueDetails`,
													suppress: true,
												});
											},
										},
									},
									then: () => {
										const path = App.getPath();

										App.captureInsight({
											// log the 'pageview' of the disclaimer overlay
											eventId,
											eventType: `page-view`,
											pageType: `Checkout`,
											path: `${path}/queue/agreement`,
											title: `Queue, agreement (${event.id}: ${event.name})`,
											cart,
											purchaseFlow,
											paymentPlanFee,
											orderProtectionFee,
										});
									},
									fail: deferred.reject,
								});

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise,
						then: () => {
							// add-ons complete
							var deferred, agreed;

							if (cart.requirements.payment_plan) {
								deferred = this.Deferred();

								if (this.isObject(cart.paymentSchedule)) {
									App.loadOverlay({
										type: `PaymentSchedule`,
										replace: false, // keep the existing overlay(s) running
										defer: false, // show the overlay immediately
										setup: {
											contentMode: `require`,
											checkoutMode: cart.checkoutMode,
											paymentSchedule: cart.paymentSchedule,
											currency: event.currency,
											timezone: event.venue.timezone,
											eventId: event.id,
											callbacks: {
												success: () => {
													agreed = true;
												},
												die: () => {
													if (agreed === true) {
														this.enablePaymentPlan({
															then: deferred.resolve,
															fail: deferred.reject,
														});
													} else {
														deferred.reject({
															message: `ShoppingCart: User exited required PaymentSchedule`,
															suppress: true,
														});
													}
												},
											},
										},
										fail: deferred.reject,
									});
								} else {
									if (cart.features.payment_plan_optional || cart.usePaymentPlan) {
										// allowed to skip this step
										deferred.resolve();
									} else {
										// no way to proceed, error out
										deferred.reject({
											message: `We're sorry, payment plans are no longer available.`,
										});
									}
								}

								return deferred.promise;
							} else if (cart.useCustomPaymentSchedule) {
								deferred = this.Deferred();

								// Custom payments flow
								const paymentSchedule = {
									installment: this.map(cart.customPaymentSchedule.scheduledPayments, (payment) => {
										return {
											paymentAmount: payment.amount,
											status: payment.status,
											paymentDate: payment.paymentDate ? payment.paymentDate : payment.scheduledDate,
											issuanceConditions: payment.issuanceConditions,
											token: payment.token,
										};
									}),
									currentPayment: cart.currentPayment,
									terms: cart.customPaymentSchedule.terms,
									isCustomPaymentSchedule: true,
								};

								this.App.loadOverlay({
									type: `PaymentSchedule`,
									replace: false, // keep the existing overlay(s) running
									defer: false, // show immediately
									setup: {
										contentMode: `status`,
										checkoutMode: cart.checkoutMode,
										paymentSchedule,
										currency: event.currency,
										timezone: event.venue.timezone,
										eventId: event.id,
										callbacks: {
											success: () => {
												agreed = true;
											},
											die: () => {
												if (agreed === true) {
													deferred.resolve();
												} else {
													if (cart.useCustomPaymentSchedule) {
														window.history.back();
													} else {
														deferred.reject({
															message: `ShoppingCart: User exited PaymentSchedule overview`,
															suppress: true,
														});
													}
												}
											},
										},
									},
									fail: deferred.reject,
								});

								return deferred.promise;
							}
						},
					});

					if (data.checkoutMode === `AGENT`) {
						promise = this.when({
							promise,
							then: () => {
								const promises = [
									Groups.getRepresentativesForGroup({ groupId: data.event.group.id })
										.then((representatives) => {
											cart.groupReps = representatives;
											cart.requirements.representatives = this.isArray(representatives, 1);
										})
										.catch(() => {
											cart.groupReps = [];
											cart.requirements.representatives = false;
										}),
									Groups.getRepresentativeTypeOptionsForGroup({ groupId: data.event.group.id }).then(
										(representativeTypeOptions) => {
											cart.repTypeOptions = representativeTypeOptions;
										}
									),
								];

								return this.when(promises);
							},
						});
					}

					promise = this.when({
						promise, // reserved successfully; NOTE: should only fire once per potential order
						then: () =>
							App.loadOverlay({
								type: `Checkout`,
								replace: true,
								setup: {
									backgroundColor: `#F5F5F5`, // hack for mobile when the form is pulled up a bit so it looks like it's part of the form
									backgroundOpacity: 1,
									isUpgrades,
									purchaseFlow,
									callbacks: {
										die: () => {
											if (this.isString(groupSaleUrlParam, 1) && !cart.completed) {
												this.navigateToOriginalGroupSale({ groupSaleId, groupSaleUrlParam });
											}
										},
									},
								},
							}),
					});

					options.promise = promise;
					promise = this.when(options); // run through any passed handlers

					return this.when({
						promise,
						then: () => {
							this.reconcile();
							return cart;
						},
						fail: (reason) => {
							let json;

							if (this.isString(reason?.responseText)) {
								json = JSON.parse(reason.responseText);
							}

							const preserveSession = json?.docType === `STANDARD_ERROR` && json?.code === 2017;

							this.init({ preserveSession }); // reset the cart if the checkout fails (e.g. from deferred.reject() calls from modals)

							if (!this.isObject(reason) || reason.suppress !== true) {
								throw reason; // re-throw the error
							}
						},
						fin: () => {
							data.checkoutActive = false;
							data.postTransactionAddOns = null; // cleanup
						},
					});
				},
			},

			checkoutUpgrades: {
				clone: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { App, data } = this;
					const { cart, event, isUpgrades, purchaseFlow } = data;
					const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
					const orderProtectionFee =
						this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;
					const { trt } = options;
					let promise;

					this.defaults(options, {
						owner: context,
					});

					if (data.checkoutActive) {
						// a checkout is already in flight, e.g. the fan clicked the button more than once
						return;
					}

					data.checkoutActive = true;

					if (event.captcha) {
						promise = this.require({
							deps: [`Captcha`],
							then: () => {
								const { Captcha } = this;
								const path = App.getPath();

								App.captureInsight({
									eventType: `page-view`,
									pageType: `Checkout`,
									path: `${path}/captcha`,
									title: `Event Page, Captcha (${event.id}: ${event.name})`,
									cart,
									purchaseFlow,
									paymentPlanFee,
									orderProtectionFee,
								});

								return Captcha.getResponseToken();
							},
						});
					} else {
						promise = DUMMY_RECAPTCHA;
					}

					promise = this.when({
						promise,
						then: (recaptchaResponse) => {
							const reserveOptions = { recaptchaResponse };

							if (this.isString(trt)) {
								reserveOptions.trt = trt;
							}

							reserveOptions.upgrades = true;

							return this.reserve(reserveOptions);
						},
					});

					/* removed steps that are not needed for upgrades
					 ** add-ons
					 ** queue
					 */

					promise = this.when({
						promise,
						then: () => {
							var deferred, agreed;

							if (cart.requirements.payment_plan) {
								deferred = this.Deferred();

								if (this.isObject(cart.paymentSchedule)) {
									App.loadOverlay({
										type: `PaymentSchedule`,
										replace: false, // keep the existing overlay(s) running
										defer: false, // show the overlay immediately
										setup: {
											checkoutMode: cart.checkoutMode,
											contentMode: `require`,
											paymentSchedule: cart.paymentSchedule,
											currency: event.currency,
											timezone: event.venue.timezone,
											callbacks: {
												success: () => {
													agreed = true;
												},
												die: () => {
													if (agreed === true) {
														this.enablePaymentPlan({
															then: deferred.resolve,
															fail: deferred.reject,
														});
													} else {
														deferred.reject({
															message: `ShoppingCart: User exited required PaymentSchedule`,
															suppress: true,
														});
													}
												},
											},
										},
										fail: deferred.reject,
									});
								} else {
									if (cart.features.payment_plan_optional) {
										// allowed to skip this step
										deferred.resolve();
									} else {
										// no way to proceed, error out
										deferred.reject({
											message: `We're sorry, payment plans are no longer available.`,
										});
									}
								}

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise, // reserved successfully; NOTE: should only fire once per potential order
						then: () =>
							App.loadOverlay({
								type: `Checkout`,
								replace: true,
								setup: {
									theme: `LIGHT`,
									backgroundColor: `#F5F5F5`, // hack for mobile when the form is pulled up a bit so it looks like it's part of the form
									backgroundOpacity: 1,
									isUpgrades,
									purchaseFlow,
								},
							}),
					});

					options.promise = promise;
					promise = this.when(options); // run through any passed handlers

					return this.when({
						promise,
						then: () => {
							this.reconcile();
							return cart;
						},
						fail: (reason) => {
							this.leaveUpgradesMobileWebview();
							// reset the cart
							this.init();

							if (!this.isObject(reason) || reason.suppress !== true) {
								throw reason; // re-throw the error
							}
						},
						fin: () => {
							data.checkoutActive = false;
							data.postTransactionAddOns = null; // cleanup
						},
					});
				},
			},

			reserve: {
				async: true,
				defaults: {
					recaptchaResponse: null,
				},
				value(options) {
					// make the initial request to reserve a token
					const { App, Locale, ReservedSeating, System, User, Utils, XHR, data } = this;
					const { cart, event, eventUnlocks, isUpgrades, purchaseFlow } = data;
					const { referrer } = document;
					const { recaptchaResponse, trt, upgrades, saleItems } = options;
					const { currency, groupSaleToken, privateLinkToken } = cart;
					const { id: eventId, mode: eventMode } = event;
					const isFanPreview = eventMode === `FAN_PREVIEW`;
					const reservedSeating = ReservedSeating.get();
					const matchingUnlocks = this.filter(eventUnlocks, { eventId });
					const accessCodes = this.map(matchingUnlocks, `accessCode`);
					const system = System.get();
					const localeInfo = Locale.get();
					const { selected: selectedLocale } = localeInfo;
					const { seatingChartHoldId } = reservedSeating;
					const { key: locale } = selectedLocale;
					const { isBrowser } = system;
					let refId = App.getUrlParam(`r`);
					let canceled = false;
					let waitingRoom;
					let onCancel;
					let promise;
					let body;

					body = {
						accessCodes,
						currency,
						recaptchaResponse,
						referrer,
						refId,
						locale,
					};

					if (isUpgrades) {
						body.isExchange = true;
					}

					if (isFanPreview) {
						// let the BE know this a Fan Preview event
						body.fp = true;
					}

					if (this.isString(trt, 1)) {
						// resuming has confused fans when clicking multiple email links in a row
						data.enablePersistentCheckout = false;

						body.trt = trt;
					} else if (this.isArray(saleItems, 1)) {
						body.saleItems = saleItems;
					} else {
						const saleItems = [];
						let hasReserved = false;
						let hasDynamicQueued = false;

						// avoid possible sync issues when embedded in native apps
						data.enablePersistentCheckout = isBrowser;

						body.saleItems = saleItems;
						this.each(cart.products, (product) => {
							const { displayState, id, isStateOverride, isReserved, productType, quantity } = product;
							const isQueued = displayState === `QUEUED`;
							const selectionModifiers = { isReserved, isQueued };
							let isDynamicQueued = false;

							if (quantity > 0) {
								// product will be included in request
								if (isQueued) {
									if (!hasDynamicQueued && isStateOverride) {
										// client-side dynamic waitlist was used, keep track for cart request body
										hasDynamicQueued = true;
										isDynamicQueued = true;
									}
								} else if (isReserved && !hasReserved) {
									// keep track for cart request body
									hasReserved = true;
								}
							}

							selectionModifiers.isDynamicQueued = isDynamicQueued;

							if (productType === `SALE`) {
								const { deliveryConfiguration, quantity, tiers, selections } = product;

								if (quantity > 0) {
									const { id: tierId } = tiers[0];

									const baseSaleItem = {
										holdId: null,
										saleId: id,
										tierId,
									};

									const selectionSaleItems = this.convertProductSelectionsToSaleItems({
										selections,
										baseSaleItem,
										deliveryConfiguration,
										selectionModifiers,
										product,
									});
									saleItems.push(...selectionSaleItems);
								}
							} else if (productType === `HOLD`) {
								const allSaleIds = this.map(product.selections, `saleId`);
								const saleIds = this.unique(allSaleIds);

								this.each(saleIds, (saleId) => {
									const included = this.filter(product.selections, { saleId });
									const quantity = included.length;

									if (quantity > 0) {
										const assignment = this.find(product.saleAssignments, { saleId });
										const { deliveryConfiguration } = assignment;
										const sale = this.find(cart.products, { id: saleId, productType: `SALE` });
										const { id: tierId } = sale.tiers[0];

										const baseSaleItem = {
											holdId: id,
											saleId,
											tierId,
										};

										const selectionSaleItems = this.convertProductSelectionsToSaleItems({
											selections: included,
											baseSaleItem,
											deliveryConfiguration,
											selectionModifiers,
											product,
										});
										saleItems.push(...selectionSaleItems);
									}
								});
							}
						});

						if (hasDynamicQueued) {
							body.q = true;
						}

						if (this.isString(seatingChartHoldId)) {
							if (hasReserved) {
								body.seatingChartHoldId = seatingChartHoldId;
							}
							// If no reserved items, keep the session alive until the cart is confirmed
						} else if (hasReserved) {
							throw new Error(`Seating chart session is required but not found`);
						}
					}

					if (this.isString(privateLinkToken, 1)) {
						body.privateLinkToken = privateLinkToken;
					}

					if (this.isString(groupSaleToken, 1)) {
						body.groupSaleToken = groupSaleToken;
					}

					if (upgrades) {
						body.upgradeInfo = {
							sourceEventId: cart.sourceEventId, // need source event since upgrades can be across events with exchanges
							sourceOrderId: cart.orderId,
						};

						this.each(body.saleItems, (saleItem) => {
							saleItem.upgradeInfo = {
								upgradeSourceItemInfos: [],
							};

							if (this.isArray(saleItem.items, 1)) {
								this.each(saleItem.items, (item) => {
									const { seatLabel } = item;
									const matchingUpgrade = this.find(cart.upgrades, (upgrade) => {
										if (upgrade.selection.seatLabel === seatLabel) {
											const sourceAlreadyAdded = this.find(saleItem.upgradeInfo.upgradeSourceItemInfos, {
												sourceSaleOrderItemId: upgrade.source,
											});

											if (!this.isObject(sourceAlreadyAdded)) {
												return true;
											}
										}

										return false;
									});

									if (this.isObject(matchingUpgrade)) {
										const { source, targetSelected = {} } = matchingUpgrade;
										const { priceListId } = targetSelected;

										saleItem.upgradeInfo.upgradeSourceItemInfos.push({
											sourceSaleOrderItemId: source,
											targetSeatLabel: seatLabel,
										});

										// attach any priceListId that was on the target selection
										item.priceListId = this.isInt(priceListId) ? priceListId : null;
									}
								});
							} else {
								this.each(cart.upgrades, (upgrade) => {
									const { source, targetSelected } = upgrade;
									const { saleId, tierId, /*holdId,*/ priceLevelOptionId, priceListId } = targetSelected;

									if (
										saleId === saleItem.saleId &&
										tierId === saleItem.tierId &&
										priceLevelOptionId == saleItem.priceLevelOptionId
									) {
										saleItem.priceListId = priceListId;
										saleItem.upgradeInfo.upgradeSourceItemInfos.push({ sourceSaleOrderItemId: source });
									}
								});
							}
						});

						// TODO: replace with something better integrated
						const updatedSaleItems = [];
						this.each(body.saleItems, (saleItem) => {
							if (this.isArray(saleItem.items, 1)) {
								const { items, upgradeInfo } = saleItem;
								const { upgradeSourceItemInfos } = upgradeInfo;
								const groupedUpdatedSourceItemInfos = {};
								let i = 0; // NOTE: groupBy() doesn't provide a second index argument, manually define and increment
								const groupedItemLists = Utils.groupBy(items, (item) => {
									const { priceListId } = item; // will be used as the key for grouping
									const matchingSourceItemInfo = upgradeSourceItemInfos[i];
									const priceListIdString = `${priceListId}`;

									// group the associated itemInfo values by the same key
									if (this.isArray(groupedUpdatedSourceItemInfos[priceListIdString])) {
										// array already exists, push into it
										groupedUpdatedSourceItemInfos[priceListIdString].push(matchingSourceItemInfo);
									} else {
										// create new array to hold the itemInfo values for this key
										groupedUpdatedSourceItemInfos[priceListIdString] = [matchingSourceItemInfo];
									}

									i++; // increment the counter

									// group the items by the key
									return priceListIdString;
								});

								this.each(groupedItemLists, (groupedItems, priceListIdString) => {
									// parse the priceListId from the group's key
									const priceListIdNumber = this.parseInt(priceListIdString);
									const priceListId = this.isInt(priceListIdNumber) ? priceListIdNumber : null;

									// clone the existing saleItem, to augment and add to updatedSaleItems
									const saleItemCopy = this.clone(saleItem);
									// fetch the array of upgradeSourceItemInfos that was accumulated earlier
									const matchingSourceItemInfo = groupedUpdatedSourceItemInfos[priceListIdString];

									// set the priceListId at the saleItem
									saleItemCopy.priceListId = priceListId;

									// clear the priceLevelId at the item level
									this.each(groupedItems, (groupedItem) => delete groupedItem.priceListId);

									// use the subset of items grouped by key
									saleItemCopy.items = groupedItems;
									// use the subset of upgradeSourceItemInfos grouped by key
									saleItemCopy.upgradeInfo.upgradeSourceItemInfos = matchingSourceItemInfo;
									// push into the new array of saleItems
									updatedSaleItems.push(saleItemCopy);
								});
							} else {
								// no items array to potentially split, copy over as-is
								updatedSaleItems.push(saleItem);
							}
						});

						body.saleItems = updatedSaleItems;
					}

					promise = XHR.ajax({
						block: false,
						type: `POST`,
						REST: true,
						url: `/api/events/${event.id}/shoppingcarts`,
						body,
						then: (reservation) => {
							// initial response received
							let requestId;

							function placeRequest() {
								// used in the case of a deferred call, to ping again for status
								body.requestId = requestId;
								body.recaptchaResponse = DUMMY_RECAPTCHA;

								if (!canceled) {
									return XHR.ajax({
										// repeat the same call, with requestId attached
										block: false,
										type: `POST`,
										REST: true,
										url: `/api/events/${event.id}/shoppingcarts`,
										body,
										then: onResponse,
									});
								}
							}

							function onResponse(response) {
								// used every time a response is received
								if (this.isString(response.token, 1)) {
									if (canceled) return; // if the user has exited the waiting room, do not proceed

									// a valid token was received, reservation successful
									if (this.isString(seatingChartHoldId) && !body.seatingChartHoldId) {
										// checkout is launching with no reserved items — end the seats.io session now that we have a confirmed cart
										ReservedSeating.endSession({ deleteHold: true });
									}

									if (response.authenticated) {
										if (response.guestCheckoutBehavior === `AGENT_ONLY`) {
											// forced guest checkout, log
											User.logOut();
										}
									} else {
										// user isn't authenticated
										User.set({
											// log them out locally
											value: null,
										});
									}

									cart.token = response.token;
									cart.upgradeSubTotal = response.upgradeSubTotal;

									return this.syncCart({
										response,
										then: (result) => {
											App.captureInsight({ eventType: `checkout-init`, purchaseFlow });
										},
									});
								} else if (this.isObject(response.delay)) {
									// this is an async request
									const { delay } = response;
									const { cb, warning: message } = delay;
									const duration = this.isInt(cb) ? cb * 1000 : 1000; // use callback delay from server if available

									requestId = delay.requestId;

									if (this.isString(message, 1)) {
										if (this.isObject(waitingRoom) && this.isFunction(waitingRoom.update)) {
											waitingRoom.update();
										}
									}

									return this.defer({
										duration, // wait the requested amount of time
										then: placeRequest, // poll for status
									});
								} else if (response.alert === true) {
									App.loadOverlay({
										type: `Message`,
										setup: {
											type: `alert`,
											message: response.warning,
										},
									});

									throw {
										message: response.warning,
										suppress: true,
									};
								} else {
									this.log({
										type: `warning`,
										message: `No token or requestId present on ticket reservation`,
									});

									throw {
										message: `There was an error reserving tickets`,
									};
								}
							}

							return onResponse.call(this, reservation);
						},
					});

					// callback if the user exits the waiting room
					onCancel = () => (canceled = true);

					promise = this.when({
						promise,
						fail: (reason) => {
							// Cart creation failed — keep the seating chart session alive so the overlay stays open
							throw reason;
						},
					});

					waitingRoom = App.useWaitingRoom({
						theme: event.theme,
						onCancel,
						promise,
					});

					return waitingRoom.promise;
				},
			},

			/**
			 * Sale items should be grouped by saleId, holdId, priceLevelOptionId and priceListId (tierId is included but all sales have the same tierId)
			 * The products array is already grouped by saleId and holdId so we only need to group by priceLevelOptionId and priceListId for the selections.
			 * @method convertProductSelectionsToSaleItems
			 * @param {Array} selections - The product selections to group
			 * @param {Object} baseSaleItem - The base sale item to use as a template
			 * @param {Object} deliveryConfiguration - The delivery configuration for the sale item
			 * @param {Object} selectionModifiers - Additional options that modify how the selections are processed
			 * @param {Boolean} options.isReserved - Whether the selections are reserved
			 * @param {Boolean} options.isQueued - Whether the selections are queued
			 * @returns {Array} An array of sale items
			 */
			convertProductSelectionsToSaleItems: {
				value({ selections, baseSaleItem, deliveryConfiguration, selectionModifiers = {}, product }) {
					const { Utils } = this;
					const { isReserved, isQueued, isDynamicQueued } = selectionModifiers;
					const deliveryConfig = {
						deliveryType: deliveryConfiguration?.deliveryType || null,
						additionalDeliveryType: deliveryConfiguration?.additionalDeliveryType || null,
					};
					const modifiers = {};
					const saleItems = [];

					if (isDynamicQueued) {
						modifiers.q = true;
					}

					// const denormalizedSelections = this.PriceLevels.denormalizeProductSelections({
					// 	product,
					// 	selections,
					// });
					const groupedSelections = Utils.groupBy(
						selections,
						({ priceLevelOptionId, priceListId }) => `${priceLevelOptionId}_${priceListId}`
					);

					this.each(groupedSelections, (selections) => {
						const selectionQuantity = selections.length;
						const { priceLevelOptionId, priceListId } = selections[0]; // all selections in this grouping have the same priceLevelOptionId and priceListId
						let items = null;

						if (isReserved && !isQueued) {
							items = this.map(selections, ({ seatLabel }) => ({ seatLabel }));
						}

						const saleItem = this.assign(
							{
								items,
								priceLevelOptionId,
								priceListId,
								quantity: selectionQuantity,
							},
							baseSaleItem,
							deliveryConfig,
							modifiers
						);

						this.PriceLevels.denormalizeProductItem({ product, item: saleItem });

						saleItems.push(saleItem);
					});

					return saleItems;
				},
			},

			updateQueueSettings: {
				async: true,
				public: true,
				defaults: {
					block: true,
				},
				value(context, options) {
					const data = this.data;
					const cart = data.cart;
					const event = data.event;
					const { block } = options;
					const { expiration_unit, expiration_window, payment_plan_settings } = options.data;

					return this.XHR.ajax({
						type: `PUT`,
						REST: true,
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=change-queue-settings`,
						data: {
							queue_settings: {
								expiration_unit,
								expiration_window,
								payment_plan_settings,
							},
						},
						block,
						then: (response) => {
							cart.progress.q_config = true;

							if (options.q_config === false) {
								cart.progress.q_config = false;
							}

							return this.syncCart({
								response,
								then: () => response,
							});
						},
					});
				},
			},

			finishUpdateQueueSettings: {
				async: true,
				public: true,
				value(context, options) {
					const data = this.data;
					const cart = data.cart;

					cart.progress.q_config = true;

					return this.syncCart({
						response: cart,
					});
				},
			},

			showUpdateQueuePaymentPlanModal: {
				async: true,
				public: true,
				value(context, options) {
					const deferred = this.Deferred();
					const { App, data } = this;
					const { cart, event, purchaseFlow } = data;
					const { id: eventId, name: eventName } = event;
					const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
					const orderProtectionFee =
						this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;

					return App.loadOverlay({
						// launch the overlay
						type: `CheckoutQueueDetails`,
						defer: false, // show immediately
						replace: false, // keep the current overlay active
						setup: {
							addBack: true,
							modalName: `payment-plan`,
							callbacks: {
								success: deferred.resolve,
								die: () => {
									deferred.reject({
										message: `ShoppingCart: User exited CheckoutQueueDetails`,
										suppress: true,
									});
								},
							},
						},
						then: () => {
							const path = App.getPath();

							App.captureInsight({
								// log the 'pageview' of the disclaimer overlay
								eventId,
								eventType: `page-view`,
								pageType: `Checkout`,
								path: `${path}/queue/agreement`,
								title: `Queue, agreement (${eventId}: ${eventName})`,
								cart,
								purchaseFlow,
								paymentPlanFee,
								orderProtectionFee,
							});
						},
						fail: deferred.reject,
					});
				},
			},

			updateShippingSettings: {
				async: true,
				public: true,
				async value(context, { shippingAddress }) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const urlPrefix = `/api/events/${eventId}/shopping-carts/${cartToken}`;
					const hasAddress = this.isObject(shippingAddress);
					const urlSuffix = hasAddress ? `set-shipping-address` : `update-shipping-to-will-call`;
					const body = hasAddress ? { shippingAddress } : null;
					const url = `${urlPrefix}/${urlSuffix}`;
					const type = `PUT`;
					const REST = true;

					// place the api request
					const response = await XHR.ajax({ body, REST, type, url });

					await this.syncCart({ response });
				},
			},

			updateLocaleSetting: {
				async: true,
				value() {
					const { data } = this;
					const { cart, event, selectedLocale } = data;

					if (
						this.isString(selectedLocale?.key, 1) &&
						this.isString(cart?.token, 1) &&
						this.isObject(event) &&
						!cart.completed
					) {
						return this.XHR.ajax({
							body: { key: selectedLocale.key },
							REST: true,
							type: `PUT`,
							url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=change-locale`,
						});
					}
				},
			},

			revisitShipping: {
				public: true,
				value(context) {
					const { data } = this;
					const { cart } = data;

					if (!cart.requirements.shipping_address) {
						throw new Error(`Shipping address is not required`);
					}

					cart.progress.shipping_address = false;
					this.reconcile();
				},
			},

			getPaymentScheduleSimulation: {
				async: true,
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart, event } = data;
					const { startDate } = options;

					return this.XHR.ajax({
						type: `GET`,
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}/payment-schedule-simulation`,
						data: {
							startDate: this.Time.format(`%Y-%n-%D`, startDate, event.venue.timezone),
						},
						block: false,
					});
				},
			},

			unsubscribe: {
				public: true,
				defaults: {},
				value(context, options) {
					this.defaults(options, {
						owner: context,
					});

					this.extract(this.subscriptions, options);
				},
			},

			loadForms: {
				public: true,
				async: true,
				defaults: {
					required: true,
				},
				value(context, options) {
					const { App, Deferred, data } = this;
					const { required } = options;
					const { cart, custom_forms, event, purchaseFlow, user, questionsLoaded = false } = data;
					const { expiration, hasBatchSubmissions, saleItems, token } = cart;
					const { id: eventId, theme } = event;
					const forms = this.extract(custom_forms, { required });

					if (this.isObject(user) && forms.length > 0 && !hasBatchSubmissions && !questionsLoaded) {
						const reservedSaleItem = this.find(saleItems, ({ cartedSaleItems }) => {
							return this.find(cartedSaleItems, ({ seatLabel }) => this.isString(seatLabel, 1));
						});
						const cartHasReservedSaleItems = this.isObject(reservedSaleItem);

						let filteredForms = [];

						if (cartHasReservedSaleItems) {
							// cart has at least one seatLabel value, include SEAT_SELECTION questions
							filteredForms = forms;
						} else {
							// no seatLabels present in the cart, filter out SEAT_SELECTION questions
							this.each(forms, (form) => {
								const filteredForm = this.clone(form);
								const { questions } = form;
								const filteredQuestions = this.filter(questions, ({ type }) => type !== `SEAT_SELECTION`);
								filteredForm.questions = filteredQuestions;

								if (filteredQuestions.length > 0) {
									filteredForms.push(filteredForm);
								}
							});

							if (filteredForms.length === 0) {
								// no forms to show
								return;
							}
						}

						// user logged in and forms are present
						const deferred = Deferred();
						const action = required ? `checkout-required` : `checkout-optional`;
						const setup = {
							theme,
							forms: filteredForms,
							action,
							eventId,
							token,
							expiration,
							required,
							callbacks: {
								success: () => {
									const eventType = `checkout-step-complete`;
									const step = `custom-forms`;
									App.captureInsight({ eventType, step, purchaseFlow });

									data.questionsLoaded = false;

									deferred.resolve();
									isDeferredPending = false;
								},
							},
						};
						let isDeferredPending = true;

						if (required) {
							setup.callbacks.die = () => {
								if (isDeferredPending) {
									// still pending or rejected
									App.notifyUser({
										type: `error`,
										message: `Answers are required to continue`,
									});

									this.init(); // error'd out, do a light cart reset

									data.questionsLoaded = false;

									deferred.reject(new Error(`ShoppingCart: Couldn't obtain required form answers`)); // notify the original caller
									isDeferredPending = false;
								}
							};
						} else {
							setup.callbacks.die = () => {
								data.questionsLoaded = false;
								deferred.resolve(); // no success, but not required, resolve
								isDeferredPending = false;
							};
						}

						if (this.isDefined(cart.orderId)) {
							setup.orderId = cart.orderId;
						}

						App.loadOverlay({
							type: `Questions`,
							defer: false, // show the overlay immediately
							replace: false, // keep any existing overlay in the background
							setup,
						});

						data.questionsLoaded = true;

						const eventType = `checkout-step`;
						const step = `custom-forms`;
						App.captureInsight({ eventType, step });

						return deferred.promise;
					}
				},
			},

			/**
			 * Ask the user if they want to apply a promo code that was entered but not applied
			 */
			checkPromoCode: {
				public: true,
				async: true,
				value(context, options) {
					const { data, App } = this;
					const { cart, incomplete_promo } = data;

					if (!this.isString(cart.promoCode, 1) && this.isString(incomplete_promo, 1)) {
						return App.askUser({
							message: `You entered a promo code but did not apply it. Would you like to apply it?`,
							then: (answer) => {
								if (answer) {
									return this.addPromo({
										force: true, // override the fact that submission has started
										code: incomplete_promo,
										then: () => {
											throw {
												docType: `CANCEL`,
											};
										},
										fail: (reason) => {
											throw {
												docType: `CANCEL`,
											};
										},
									});
								}
							},
						});
					}
				},
			},

			submit: {
				public: true,
				async: true,
				defaults: {
					useAsDefault: true,
					savePaymentMethod: true,
				},
				value(context, options) {
					const { App, Config, Payment, Time, data } = this;
					const { EXTERNAL_TENDER_TYPES } = Config;
					const { cart, event, user } = data;
					const { agentCheckoutFanId, expiration, requirements } = cart;
					const fanPaymentUserIdForAgent = this.getAgentCheckoutPaymentUserId(cart);
					const { paymentProcessorType } = event;
					const currentTime = Time.getTime();
					const key = `buy`;
					let { tenderType } = options;
					const isExternalPayment = this.includes(EXTERNAL_TENDER_TYPES, tenderType);
					if (buyRequestPlaced) {
						throw new Error(`ShoppingCart.submit() already running`);
					}
					if (currentTime > expiration) {
						// cart has expired
						return this.expireCart();
					}
					buyRequestPlaced = true;

					cart.useAsDefault = options.useAsDefault;
					cart.savePaymentMethod = options.savePaymentMethod;
					cart.paymentMethodToken = null;
					cart.billingAddress = options.billingAddress;
					cart.tenderType = null;
					cart.nonce = null;

					App.block({ key });

					// Waitlist: `total` reflects whether the order is free; `totalDueNow` can be 0 when nothing is charged until later.
					const dueForPaymentFlow = requirements.q_config ? cart.total : cart.totalDueNow;

					// Save-card waitlist w/ a balance due uses Stripe; $0 (normal or waitlist) uses PUT to shopping cart only
					const isStripeWaitlistSave =
						requirements.q_config &&
						cart.savePaymentMethod &&
						paymentProcessorType === `STRIPE` &&
						!isExternalPayment &&
						dueForPaymentFlow > 0;

					// Master: no `requirements.payment` → `NONE` + callSubmit (PUT `?action=purchase|queue`). $0: same, never `confirmSetupIntent`
					if (!requirements.payment || dueForPaymentFlow <= 0) {
						if (!isStripeWaitlistSave) {
							tenderType = `NONE`;
						}
					}

					// `isStripeWaitlistSave` must use Stripe (setup intent / SCA) before PUT `?action=queue`.
					// If we short-circuited here when `!requirements.payment`, we would callSubmit with no token/nonce → ValidPayment2.
					if (
						!isStripeWaitlistSave &&
						(!requirements.payment || dueForPaymentFlow <= 0 || paymentProcessorType !== `STRIPE` || isExternalPayment)
					) {
						// BRAINTREE, $0 checkout (incl. waitlist `?action=queue`), External Payment
						this.callSubmitCartAction(options);
						this.clearPersistentCheckout();
					} else if (tenderType === `CREDIT_BANK`) {
						this.callSubmitCartAction(options);
						this.clearPersistentCheckout();
					} else {
						// STRIPE CHECKOUT FLOW
						let promise;

						const confirmNewStripePaymentMethodOptions = {
							shippingAddress: cart.shippingAddress,
							addressMatch: cart.requirements.address_match,
							setupIntentUserId: cart.checkoutMode === `AGENT` ? fanPaymentUserIdForAgent : null,
							authenticationContext: {
								eventId: event.id,
								groupId: event.group.id,
								shoppingCartId: cart.token,
								currentPaymentToken: options.token,
							},
							usage: isStripeWaitlistSave ? `off_session` : null,
							confirm: isStripeWaitlistSave ? true : null,
							isWaitlistOrder: !!requirements.q_config,
							// Paid, non-waitlist checkout confirms payment_intent after capture → skip check-3ds.
							...(requirements.payment && !requirements.q_config ? { confirmPaymentIntentAfterCardCapture: true } : {}),
						};

						if (cart.savePaymentMethod && requirements.q_config) {
							// Waitlist
							if (options.token) {
								// existing payment method — same SCA path as new card (GET check-3ds + confirmSetup when required)
								const baseUrl = window.location.href;
								const queryParams = [
									`stripe_redirect_token=${encodeURIComponent(cart.token)}`,
									`eventId=${encodeURIComponent(String(event.id))}`,
								];

								if (this.isString(user?.email, 1)) {
									queryParams.push(`redirect_guest_email=${encodeURIComponent(user.email)}`);
								}

								const querySeparator = baseUrl.includes(`?`) ? `&` : `?`;
								const returnUrl = `${baseUrl}${querySeparator}${queryParams.join(`&`)}`;
								const authUserId =
									cart.checkoutMode === `AGENT` ? fanPaymentUserIdForAgent ?? agentCheckoutFanId : user?.id;

								promise = Payment.authenticateSavedPaymentMethodForOffSession({
									userId: authUserId,
									platformPaymentMethodId: options.token,
									groupId: event.group.id,
									returnUrl,
								}).then(() => {
									options.methodToken = options.token;
									this.callSubmitCartAction(options);
								});
							} else {
								// new payment method
								promise = Payment.confirmSetupIntent(confirmNewStripePaymentMethodOptions).then((setupIntent) => {
									options.methodToken = setupIntent.payment_method;
									this.callSubmitCartAction(options);
								});
							}
						} else {
							if (options.token) {
								// existing payment method
								promise = Promise.resolve({ payment_method: options.token });
							} else {
								// new payment method
								promise = Payment.confirmSetupIntent(confirmNewStripePaymentMethodOptions);
							}

							promise = this.when({
								promise,
								then: ({ payment_method: token }) => {
									let payload = {
										groupId: event.group.id,
										token, //paymentToken,
										then: (result) => result.id, // pass through the translated token id from the response
									};

									// Invoices / agent: guest-user, Studio guestCheckoutDetails, or salesInvoiceFanId
									const paymentOwnerUserId = fanPaymentUserIdForAgent ?? agentCheckoutFanId;
									if (paymentOwnerUserId != null) {
										payload.userId = paymentOwnerUserId;
									}

									return Payment.translateMethod(payload);
								},
							});

							promise = this.when({
								promise,
								then: (paymentToken) => {
									// translated token is ready
									return Payment.confirmIntent({
										isNewCard: !this.isString(options.token, 1),
										payment_method: paymentToken,
										cvcToken: options.cvcToken ? options.cvcToken.id : undefined,
										type: `payment`,
										paymentIntent: cart.paymentIntent,
										userId: cart.checkoutMode === `AGENT` ? fanPaymentUserIdForAgent ?? agentCheckoutFanId : user.id,
										shippingAddress: cart.shippingAddress,
										addressMatch: cart.requirements.address_match,
										setupFutureUsage: `off_session`,
										then: async (stripeResult) => {
											const confirmation = await this.pingForStripeOrderResult();
											this.onOrderComplete({ confirmation });
										},
										fail: (error) => {
											this.resetPaymentMethod({ value: true });
											throw error;
										},
									});
								},
							});
						}

						// Todo - BNPL
						// let paymentIntentConfirmationRequired = false;

						// if (cart.savePaymentMethod) {
						// 	if (requirements.q_config) {
						// 		// waitlist
						// 		if (options.token) {
						// 			// existing payment method
						// 			options.methodToken = options.token;
						// 			this.callSubmitCartAction(options);
						// 		} else {
						// 			// new payment method
						// 			promise = Payment.confirmSetupIntent({
						// 				shippingAddress: cart.shippingAddress,
						// 				addressMatch: cart.requirements.address_match,
						// 			}).then((setupIntent) => {
						// 				options.methodToken = setupIntent.payment_method;
						// 				this.callSubmitCartAction(options);
						// 			});
						// 		}
						// 	} else {
						// 		paymentIntentConfirmationRequired = true;
						// 		// regular purchase, payment plan
						// 		if (options.token) {
						// 			// existing payment method
						// 			let payload = {
						// 				groupId: event.group.id,
						// 				token: options.token, //paymentToken,
						// 				then: (result) => result.id, // pass through the translated token id from the response
						// 			};

						// 			// Invoices
						// 			if (agentCheckoutFanId) {
						// 				payload.userId = agentCheckoutFanId;
						// 			}

						// 			promise = Payment.translateMethod(payload);
						// 		} else {
						// 			// new payment method
						// 			promise = Payment.confirmSetupIntent({
						// 				shippingAddress: cart.shippingAddress,
						// 				addressMatch: cart.requirements.address_match,
						// 				then: (setupIntent) => {
						// 					return setupIntent.payment_method;
						// 				},
						// 			});
						// 		}
						// 	}
						// } else {
						// 	// one time payment, not saved
						// 	paymentIntentConfirmationRequired = true;
						// }

						// if (paymentIntentConfirmationRequired) {
						// 	promise = this.when({
						// 		promise,
						// 		then: (paymentToken) => {
						// 			if (this.isString(paymentToken, 1)) {
						// 				// translated token is ready
						// 				return Payment.confirmIntent({
						// 					payment_method: paymentToken,
						// 					cvcToken: options.cvcToken ? options.cvcToken.id : undefined,
						// 					type: `payment`,
						// 					paymentIntent: cart.paymentIntent,
						// 					userId: user ? user.id : agentCheckoutFanId,
						// 					shippingAddress: cart.shippingAddress,
						// 					addressMatch: cart.requirements.address_match,
						// 					then: async (stripeResult) => {
						// 						const confirmation = await this.pingForStripeOrderResult();
						// 						this.onOrderComplete({ confirmation });
						// 					},
						// 					fail: (error) => {
						// 						this.resetPaymentMethod({ value: true });
						// 						throw error;
						// 					},
						// 				});
						// 			} else {
						// 				// payment elements
						// 				return Payment.confirmPayment({
						// 					addressMatch: cart.requirements.address_match,
						// 					shippingAddress: cart.shippingAddress,
						// 				})
						// 					.then(async (stripeResult) => {
						// 						const confirmation = await this.pingForStripeOrderResult();
						// 						this.onOrderComplete({ confirmation });
						// 					})
						// 					.catch((error) => {
						// 						this.resetPaymentMethod({ value: true });
						// 						throw error;
						// 					});
						// 			}
						// 		},
						// 	});
						// }

						return this.when({
							promise,
							fail: (error) => {
								App.notifyUser({
									type: `error`,
									message: error.message,
								});

								// payment failed, restore persistent checkout values if applicable
								this.setPersistentCheckout();
							},
							fin: () => {
								buyRequestPlaced = false;
								App.unblock({ key });
							},
						});
					}
				},
			},

			submitNewPaymentForSkippedUser: {
				public: true,
				async: true,
				value(context, options) {
					const { App, Payment } = this;
					const { data } = this;
					const { cart } = data;
					const { paymentIntent } = cart;
					const { clientSecret } = options;

					let promise = Payment.confirmPaymentElements({ paymentIntent, clientSecret });

					App.block({ key: `submitNewPaymentForSkippedUser` });

					promise = this.when({
						promise,
						then: () => {
							return this.pingForStripeTerminalOrderResult();
						},
					});

					return this.when({
						promise,
						then: (confirmation) => {
							if (this.isObject(confirmation)) {
								this.onOrderComplete({ confirmation });
							}
						},
						fin: () => {
							App.unblock({ key: `submitNewPaymentForSkippedUser` });
						},
					});
				},
			},

			/**
			 * Reset the payment method form in Checkout steps
			 */
			resetPaymentMethod: {
				public: true,
				value(context, { value }) {
					this.data.cart.resetPaymentMethod = value;
					this.reconcile();
				},
			},

			/**
			 * Ping the BE to see if the order has been completed and launch the OrderComplete overlay. This gets called from Event.js when a token is present in the URL.
			 */
			checkRedirectOrderComplete: {
				public: true,
				value(context, options) {
					const { App, Storage } = this;
					const { data } = this;
					const { purchaseFlow } = data;
					const { eventId, isUpgrades, token } = options;

					App.block({ key: `checkOrderComplete` });

					this.when({
						promise: this.pingForStripeOrderResult({ eventId, token }),
						then: async (confirmation) => {
							if (isUpgrades) {
								await this.init({
									eventId: eventId,
									isUpgrades: true,
								});
							}

							const cart = data.cart;

							if (this.isObject(cart)) {
								let order = {}; // emergency fallback

								if (this.isObject(confirmation)) {
									// order details are present
									const { Orders } = this;
									const { blurb } = confirmation;

									order = confirmation.order;

									order.origin = `PURCHASE`;
									Orders.normalize(order);

									cart.blurb = this.isString(blurb) ? blurb : cart.blurb;
									cart.order = order;
									cart.orderId = order.id;
								}

								cart.completed = true;

								App.loadOverlay({
									type: `OrderComplete`,
									setup: {
										theme: `LIGHT`,
										overlayMode: `modal`,
										style: `ux4`,
									},
									replace: true,
								});

								App.unblock({ key: `checkOrderComplete` });
								App.captureInsight({ eventType: `purchase-redirect`, order, purchaseFlow });

								// remove the token and expiration from local storage
								Storage.remove({ key: `cart_token` });
								Storage.remove({ key: `cart_expiration` });
							}
						},
					});
				},
			},

			/**
			 * Ping BE to see if the Stripe order has been completed
			 */
			pingForStripeOrderResult: {
				value(options = {}) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const token = this.isString(options.token, 1) ? options.token : cart.token;
					let { eventId } = options;

					if (!this.isFinite(eventId, 1)) {
						// use the event id from the data
						eventId = event.id;
					}

					// transaction should already be completed, followup ping for webhook
					const failHandler = (reason) => {
						if (++pingCount < maxPingCount) {
							return this.defer({
								duration,
								then: () => this.XHR.ajax(ajaxOptions),
							});
						} // else resolve undefined
					};
					const ajaxHandler = (confirmation) => {
						if (this.isObject(confirmation) && this.isObject(confirmation.order)) {
							return confirmation;
						} else {
							return failHandler();
						}
					};
					const ajaxOptions = {
						REST: true,
						type: `GET`,
						url: `/api/events/${eventId}/shoppingcarts/${token}/orders`,
						then: ajaxHandler,
						fail: failHandler,
					};
					const duration = 2000;
					let maxPingCount = 4;
					let pingCount = 0;

					return this.defer({
						duration,
						then: () => XHR.ajax(ajaxOptions), // return the status ping flow for Stripe confirmation
					});
				},
			},

			/**
			 * Ping BE to see if the Stripe order has been completed
			 */
			pingForStripeTerminalOrderResult: {
				value(options = {}) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const token = this.isString(options.token, 1) ? options.token : cart.token;
					let { eventId } = options;
					let cancelled = false;

					const onCancel = () => {
						cancelled = true;
					};

					data.onCancelPingForStripeTerminalOrderResult = onCancel;

					if (!this.isFinite(eventId, 1)) {
						// use the event id from the data
						eventId = event.id;
					}

					// transaction should already be completed, followup ping for webhook
					const failHandler = (reason) => {
						if (!cancelled) {
							if (++pingCount < maxPingCount) {
								return this.defer({
									duration,
									then: () => this.XHR.ajax(ajaxOptions),
								});
							} else {
								this.cancelReader();
							}
						} // else resolve undefined
					};
					const ajaxHandler = (confirmation) => {
						if (this.isObject(confirmation) && this.isObject(confirmation.order)) {
							return confirmation;
						} else if (this.isObject(confirmation) && this.isString(confirmation.terminalFailureMessage, 1)) {
							cart.paymentTerminalStatus = `ERROR`;
							cart.paymentTerminalStatusMessage = confirmation.terminalFailureMessage;
							this.reconcile();
						} else {
							return failHandler();
						}
					};
					const ajaxOptions = {
						block: false,
						// REST: true,
						type: `GET`,
						url: `/api/events/${eventId}/shoppingcarts/${token}/orders`,
						then: ajaxHandler,
						fail: failHandler,
					};
					const duration = 2000;
					let maxPingCount = 300; // ping for up to 10 minutes
					let pingCount = 0;

					return this.defer({
						duration,
						then: () => XHR.ajax(ajaxOptions), // return the status ping flow for Stripe confirmation
					});
				},
			},

			/**
			 * Update the Stripe intent with the save payment method option
			 * @param {Object} options.savePaymentMethod - Whether to save the payment method
			 */
			updateStripeIntentWithSavePaymentMethod: {
				public: true,
				async: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { savePaymentMethod } = options;
					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}?action=save_pm`;
					const body = { savePaymentMethod };
					const xhrOptions = {
						url,
						REST: true,
						data: body,
						type: `PUT`,
					};

					return this.when({
						promise: XHR.ajax(xhrOptions),
					});
				},
			},

			/**
			 * Update the Stripe intent with the use as default
			 * @param {Object} options.useAsDefault - Whether to set the payment method as default
			 */
			updateStripeIntentWithUseAsDefault: {
				public: true,
				async: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { useAsDefault } = options;
					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}?action=use_default`;
					const body = { useAsDefault };
					const xhrOptions = {
						url,
						REST: true,
						data: body,
						type: `PUT`,
					};

					return this.when({
						promise: XHR.ajax(xhrOptions),
					});
				},
			},

			// BRAINTREE FLOW, STRIPE WAITLIST, No Payment, External Payment
			callSubmitCartAction: {
				asycn: true,
				value(options) {
					const { App, Payment, XHR, data } = this;
					const { cart, event } = data;
					const { requirements } = cart;
					const { paymentProcessorType } = event;
					const key = `buy`;
					let { tenderType } = options;

					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}`;
					const deviceData = Payment.getDeviceData();
					const payment = { deviceData, tenderType };
					const body = tenderType === `CREDIT_BANK` ? { tenderType } : { payment };
					const xhrOptions = {
						url,
						REST: true,
						data: body,
						type: `PUT`,
						then: handler,
						fail: errorHandler,
					};

					if (requirements.q_config) {
						// waitlist order
						xhrOptions.url += `?action=queue`;
					} else {
						// standard order
						xhrOptions.url += `?action=purchase`;
					}

					if (
						requirements.payment ||
						(requirements.q_config && paymentProcessorType === `STRIPE` && options.savePaymentMethod)
					) {
						const nonce = options.nonce;

						payment.save = options.savePaymentMethod;
						payment.useAsDefault = options.useAsDefault;

						if (paymentProcessorType === `STRIPE`) {
							// transaction already completed
							payment.token = options.methodToken; // TODO: can this be removed?
						} else if (this.isString(options.token, 1)) {
							// saved method
							payment.token = options.token;
						} else if (this.isString(nonce, 1)) {
							// manually passed nonce (card or paypal)
							payment.nonce = nonce;

							if (options.tenderType === `CREDIT_CARD`) {
								// BT credit card transaction, pass billing address
								payment.billingAddress = options.billingAddress;
							}
						}
					}

					function handler(response) {
						// successful checkout, reset the cart
						const { confirmation } = response;

						if (!this.isObject(confirmation)) {
							// NOTE: important to return, to avoid running the code below
							if (this.isUndefined(response.cb) || this.isUndefined(response.requestId)) {
								throw new Error(`Invalid cart response received.`);
							} else {
								const duration = response.cb * 1000;

								if (this.isString(response.warning, 1)) {
									App.notifyUser({
										type: `warning`,
										message: response.warning,
										duration,
									});
								}

								return this.defer({
									duration,
									then: () =>
										XHR.ajax({
											url: `/api/requestauthstatus/${response.requestId}`,
											then: handler,
											fail: errorHandler,
										}),
								});
							}
						}

						return confirmation;
					}

					function errorHandler(reason) {
						let docType;
						let json;

						if (this.isString(reason.responseText)) {
							json = JSON.parse(reason.responseText);
							docType = json.docType;
						}

						switch (docType) {
							case `STANDARD_ERROR`:
								switch (json.code) {
									case 2006: // Payment processor issue
										return this.defer({
											duration: 2000 + 1000 * Math.floor(Math.random() * 5),
											then: () => {
												// Random delay has passed
												buyRequestPlaced = false;
												return this.submit();
											},
										}); // returning, no break

									// Error that can not be recovered from, notify the user and destroy the checkout overlay
									case 1002: // Event not found somehow
									case 1003: // Shopping cart not found
									case 1006: // Invalid shopping cart
									case 1007: // Invalid reserve token
									case 2010: // Maximum number of retries attempted
									case 2011: // Processor down; maximum number of retries attempted
										this.init();
								}
								break;

							case `TPS_ERROR`:
								switch (json.code) {
									case 8009: // escrow non-existent
										this.init();
								}
								break;

							default: // Unrecognized docType
						}

						throw reason; // re-throw for the caller to detect
					}

					return this.when({
						promise: XHR.ajax(xhrOptions),
						then: (confirmation) => {
							this.onOrderComplete({ confirmation });
						},
						fin: () => {
							buyRequestPlaced = false;
							App.unblock({ key });
						},
					});
				},
			},

			onOrderComplete: {
				value(options) {
					const { App, data } = this;
					const { cart, purchaseFlow } = data;
					const { confirmation } = options;

					if (this.isObject(confirmation)) {
						// order details are present
						const { Orders } = this;
						const { order, blurb } = confirmation;

						order.origin = `PURCHASE`;
						Orders.normalize(order);

						cart.blurb = this.isString(blurb) ? blurb : cart.blurb;
						cart.order = order;
						cart.orderId = order.id;
					}

					// CHECKOUT SUCCESSFUL!
					cart.completed = true;

					this.setExpiration(null); // clear any timeout behavior
					this.reconcile(); // notify subscribers

					App.loadOverlay({
						type: `OrderComplete`,
						setup: { theme: `LIGHT`, overlayMode: `modal`, style: `ux4` },
						replace: true,
					});

					App.captureInsight({ eventType: `purchase`, purchaseFlow });
					this.clearPersistentCheckout();
				},
			},

			disablePaymentPlan: {
				async: true,
				public: true,
				async value(context) {
					const { App, data } = this;
					const { cart } = data;

					if (cart.usePaymentPlan === true) {
						// workaround: different cart endpoints use different naming conventions in responses
						const queue_settings = cart.queueCartSettings || cart.queue_settings || cart.queueSettings;

						if (this.isObject(queue_settings)) {
							// reset q_config to false to reset waitlist config step progress
							const payment_plan_settings =
								queue_settings?.payment_plan_settings || queue_settings?.paymentPlanSettings;

							await this.updateQueueSettings({
								data: {
									expiration_window: cart.queue_settings.expiration_window || cart.queue_settings.expirationWindow,
									expiration_unit: cart.queue_settings.expiration_unit || cart.queue_settings.expirationUnit,
									payment_plan_settings,
								},
								q_config: false,
							});
						}

						return this.XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${data.event.id}/shoppingcarts/${cart.token}?action=apply-payment-plan`,
							data: {
								usePaymentPlan: false,
							},
							then: (response) => {
								// success
								cart.usePaymentPlan = false;
								const paymentPlanFee = this.isObject(cart) && this.isInt(cart.paymentPlanFee) ? cart.paymentPlanFee : 0;

								// fire the "cart-remove" event for payment plan
								App.captureInsight({
									eventType: `cart-remove`,
									purchaseFlowItem: `Payment Plan`,
									item: {
										id: `payment_plan`,
										name: `Payment Plan`,
										price: paymentPlanFee,
									},
									productList: {},
								});

								return this.syncCart({
									response,
									then: this.reconcile,
								});
							},
						});
					}
				},
			},

			/**
			 * NOTE: It is NOT safe to assume the user has seats selected when the Promise resolves.
			 * This XHR success implies that Tixr BE talked to seats.io successfully, but the seat
			 * assignment happens when seats.io communicates through its Renderer seating chart embed.
			 */
			getBestAvailable: {
				public: true,
				async: true,
				defaults: {},
				async value(context, options) {
					const { App, ReservedSeating, data } = this;
					const { cart, event, eventUnlocks, isUpgrades } = data;
					const { collectionId, quantity } = options;
					const { groupSaleToken, privateLinkToken, products } = cart;
					const { id: eventId, mode: eventMode } = event;
					const isFanPreview = eventMode === `FAN_PREVIEW`;
					const allSales = this.filter(products, { productType: `SALE` });
					const allHolds = this.filter(products, { productType: `HOLD` });
					const displayStatesToInclude = [`OPEN`, `HIDE`];
					const getAccessibleHoldIdsForSale = (saleId) =>
						this.map(
							this.filter(
								allHolds,
								(hold) =>
									this.includes(displayStatesToInclude, hold.displayState) &&
									this.find(hold.saleAssignments, { saleId })
							),
							`id`
						);
					const matchingUnlocks = this.filter(eventUnlocks, { eventId });
					const accessCodes = this.map(matchingUnlocks, `accessCode`);
					const selectionsToCheck = []; // used to check selection states after a delay
					const updates = []; // used for resetting cart quantities back to zero
					const items = []; // used to build the request body

					if (this.isInt(quantity, 1) && this.isInt(collectionId, 1)) {
						// single quantity, "best available" across a collection
						const { collectionConfiguration } = event;
						const collection = this.find(collectionConfiguration.collections, { id: collectionId });
						const vbas = true; // "validate best available selections" request parameter
						const item = { quantity, vbas };
						const excludedHoldIds = [];
						const saleIds = [];
						const holdIds = [];

						this.each(collection.sales, ({ id }) => {
							const sale = this.find(allSales, { id });
							const { localState, maxTicketsAllowed } = sale;
							let includeSale = localState === `OPEN`;

							if (this.isInt(maxTicketsAllowed, 0)) {
								const { purchasedQuantity } = sale;
								const allowedQuantity = maxTicketsAllowed - purchasedQuantity;

								if (quantity > allowedQuantity) {
									includeSale = false;
								}
							}

							if (includeSale) {
								saleIds.push(id);

								const assignedHoldIds = getAccessibleHoldIdsForSale(id);
								holdIds.push(...assignedHoldIds);
							} else {
								// exclude sale and any assigned holds
								const assignedHolds = this.filter(allHolds, (saleAssignments) => {
									const saleAssignment = this.find(saleAssignments, { saleId: id });
									return this.isObject(saleAssignment);
								});
								this.each(assignedHolds, ({ id: holdId }) => excludedHoldIds.push(holdId));
							}
						});

						item.saleIds = saleIds.length > 0 ? saleIds : null;
						// remove any holdIds that were associated with excluded sales
						this.extract(holdIds, (holdId) => this.includes(excludedHoldIds, holdId));
						item.holdIds = holdIds.length > 0 ? holdIds : null;

						// not possible to use priceLevelOptions across a whole collection
						item.priceLevelOptionIds = null;

						this.addPriceListIdForBestAvailable(item);

						items.push(item);
					} else if (this.isInt(quantity, 1) && this.isString(groupSaleToken, 1)) {
						const vbas = true; // "validate best available selections" request parameter
						const holdIds = this.map(allHolds, (hold) => hold.id);
						const saleIds = [];
						let item = { holdIds, quantity, saleIds, vbas };

						this.addPriceListIdForBestAvailable(item);

						items.push(item);
					} else {
						// specific quantity / quntities selected, map into multiple asks
						const vbas = false; // "validate best available selections"

						this.each(products, (product) => {
							if (product.quantity > 0) {
								// in cart
								if (product.isReserved) {
									// seatLabel selection required
									const { selections: internalSelections } = product;
									const selections = this.clone(internalSelections);
									const fbaSelections = this.filter(selections, { state: `FBA` });
									const { length: fbaQuantity } = fbaSelections;

									if (fbaQuantity > 0) {
										// seats haven't been selected, use
										const { id, productType } = product;
										let priceLevelOptionIds = null;
										let saleIds = null;
										let holdIds = null;

										if (productType === `SALE`) {
											saleIds = [id];
											const assignedHoldIds = getAccessibleHoldIdsForSale(id);
											if (assignedHoldIds.length > 0) {
												holdIds = assignedHoldIds;
											}
										} else if (productType === `HOLD`) {
											holdIds = [id];
										}

										this.each(fbaSelections, (selection) => {
											const { id: selectionId, priceLevelOptionId } = selection;

											selectionsToCheck.push(selectionId);
											selection.state = `PENDING`;

											if (this.isInt(priceLevelOptionId)) {
												if (priceLevelOptionIds === null) {
													priceLevelOptionIds = [priceLevelOptionId];
												} else {
													priceLevelOptionIds.push(priceLevelOptionId);
												}
											}
										});

										const item = { holdIds, priceLevelOptionIds, quantity: fbaQuantity, saleIds, vbas };

										this.addPriceListIdForBestAvailable(item, product);

										this.PriceLevels.denormalizeProductItem({ product, item });

										// set up the items array for ReservedSeating
										items.push(item);

										// publish the updated selection states
										updates.push({ id, productType, selections });
									}
								}
							}
						});
					}

					if (this.isArray(updates, 1)) {
						this.updateProducts({ products: updates });
					}

					const mode = isFanPreview ? `FAN_PREVIEW` : isUpgrades ? `UPGRADE` : `PURCHASE`;
					const response = await ReservedSeating.getBestAvailable({
						accessCodes,
						groupSaleToken,
						items,
						mode,
						privateLinkToken,
					});
					const { nextToEachOther } = response;

					if (nextToEachOther === false) {
						// property is present and specifically false
						if (event.consecutiveSeatSetting === `LENIENT`) {
							data.ignoreConsecutiveSeatingViolations = true;

							App.loadOverlay({
								type: `Message`,
								setup: {
									message: `Due to high demand, the seats selected are not all together. Please review your cart before completing your purchase to ensure you are satisfied with the seat locations.<br /><br />Thank you for your understanding and enjoy the event!`,
									type: `message`,
									overlayMode: `modal`,
									style: `ux4`,
								},
							});
						}
					}

					if (selectionsToCheck.length > 0) {
						// check the status of the selections left in cart
						this.defer({
							duration: 5000,
							then: () => {
								this.each(products, (product) => {
									const { id, productType, selections } = product;
									const remainingSelections = this.filter(selections, ({ id, state }) => {
										// keep selections that were not part of the above request, or aren't still PENDING
										return state !== `PENDING` || selectionsToCheck.includes(id);
									});

									if (remainingSelections.length > 0) {
										const selectionsRemoved = selections.length - remainingSelections.length;

										if (selectionsRemoved > 0) {
											// PENDING selections have timed out, remove them

											this.log({
												type: `warning`,
												message: `Removing ${selectionsRemoved} leftover PENDING selections for ${productType} ${id}`,
												args: [],
											});

											this.updateProducts({ products: [{ id, productType, selections: remainingSelections }] });
										}
									}
								});
							},
						});
					}
				},
			},

			/**
			 * Adds the priceListId to the item for best available selection.
			 * If the product has a priceListId, it uses that; otherwise, it checks
			 * if the agent price code is being used and assigns the current agent price code
			 * Note: This function mutates the item in place
			 * @param {Object} item - The item to which the priceListId will be added
			 * @param {Object} [product] - Optional product object to check for priceListId (this is empty for the collection best available)
			 * @returns {void}
			 */
			addPriceListIdForBestAvailable: {
				value(item, product) {
					let priceListId = null;

					if (product?.priceListId != null) {
						// There is a priceListId already associated with the product
						// eg sale was returned from the BE with a priceListId or a hold has a price code override
						priceListId = product.priceListId;
					} else if (this.AgentPriceCode.isEnabled() && product.priceVariantType === `NONE`) {
						// no priceListId associated with the event, use the current agent price code if enabled and not using price variants
						const priceCode = this.AgentPriceCode.get();

						priceListId = priceCode?.id;
					}

					// This was changed to set priceListIds instead of priceListId to make the api the same as for fan facing price codes
					if (priceListId != null) {
						item.priceListId = priceListId;
					}
				},
			},

			makeProductSelection: {
				value(target) {
					const { Utils } = this;
					const uniqueId = Utils.uniqueId();
					const id = parseInt(uniqueId, 10);

					this.assign(target, { id });

					return this.defaults(target, {
						holdId: null,
						holdName: null,
						priceLevelOptionId: null,
						priceLevelOptionName: null,
						row: null,
						saleId: null,
						seat: null,
						seatLabel: null,
						section: null,
						state: `ACTIVE`, // FBA | PENDING | ACTIVE | REMOVING
						tierId: null,
					});
				},
			},

			onDie: {
				public: true,
				value(context) {
					this.unsubscribe({ owner: context });
				},
			},

			/**
			 * Determines the quantity picker options and state based on product configuration
			 * @public
			 * @param {Object} context - The context object
			 * @param {Object} options - Configuration options
			 * @param {boolean} options.allowsPriceLevelExpansion - Whether price level expansion is allowed
			 * @param {boolean} options.hideAddSubtractButtons - Whether to hide add/subtract buttons
			 * @param {string} options.mediaUrl - URL of associated media
			 * @param {boolean} [options.preferResaleToQueued=false] - Whether to prefer resale behavior over queued
			 * @param {Object} options.product - Product information
			 * @param {string} options.product.displayState - Product display state
			 * @param {boolean} options.product.isLevels - Whether product has price levels
			 * @param {boolean} options.product.isRemovable - Whether product can be removed
			 * @param {string} options.product.localState - Current state of the product
			 * @param {string} options.product.optionalSaleRedirectUrl - Optional redirect URL for sales
			 * @param {number} options.product.quantity - Product quantity
			 * @param {Array} options.product.selections - Selected seats
			 * @param {boolean} options.product.video - Whether product has video
			 * @returns {Object} Quantity picker configuration
			 * @returns {boolean} returns.disabled - Whether the picker is disabled
			 * @returns {string|null} returns.externalLink - External URL if applicable
			 * @returns {boolean} returns.isRemovable - Whether product can be removed
			 * @returns {string} returns.quantityPickerType - Type of quantity picker ('OPEN'|'PRICE_LEVELS'|'ZOOM'|'QUEUED'|'LOCKED'|'RESALE'|'EXTERNAL'|'HIDDEN')
			 */
			getQuantityPickerOptions: {
				public: true,
				value(context, options) {
					const { Config } = this;
					const { ACTIVE_STATES } = Config;
					const {
						allowsPriceLevelExpansion,
						hideAddSubtractButtons,
						mediaUrl,
						preferResaleToQueued = false,
						product,
					} = options;
					const {
						displayState,
						isLevels,
						isRemovable,
						localState,
						optionalSaleRedirectUrl,
						quantity,
						resaleState,
						selections,
						video,
					} = product;
					const isSoldOut = localState === `SOLD_OUT`;
					const isClosed = localState === `CLOSED`;
					const isAtQLimit = localState === `AT_Q_LIMIT`;
					const isMedia = this.isString(mediaUrl, 1);
					const isResale = resaleState === `AVAILABLE`;
					const isAvailableAtDoor = displayState === `AVAIL_AT_DOOR`;
					const hideOpenQuantityPicker = hideAddSubtractButtons && displayState !== `QUEUED`;
					const seatSelections = this.filter(selections, ({ seatLabel }) => this.isString(seatLabel));
					const showZoomButton = seatSelections.length > 0;
					const showUnlockButton = !showZoomButton && !isMedia && localState === `LOCKED`;
					const showResaleButton =
						isResale && // has resale
						!isMedia && // doesn't already have media
						((displayState === `QUEUED` && preferResaleToQueued) || // queued + should be overridden
							displayState === `RESALE` || // local simulated state for resale-only sales
							displayState === `SOLD_OUT`); // will be utilized when the local state is removed
					const showResaleQueued = showResaleButton && displayState === `QUEUED` && preferResaleToQueued;
					let showCartAddButton =
						!showZoomButton && !isMedia && (this.includes(ACTIVE_STATES, localState) || isAtQLimit);
					let disabled = false;
					let hideQuantityPicker = hideOpenQuantityPicker || isMedia || localState === `AVAIL_SOON`;

					let quantityPickerType = `OPEN`;
					let externalLink = null;

					if (localState === `AT_LIMIT`) {
						// Shows badge instead of quantity picker
						if (!video) {
							if (quantity < 1) {
								disabled = true;
							}
						} else {
							if (quantity === 0) {
								disabled = true;
							}
						}
					}

					if (!showUnlockButton && showResaleButton) {
						if (showResaleQueued) {
							quantityPickerType = `RESALE_QUEUED`;
						} else {
							quantityPickerType = `RESALE`;
						}
					} else if (!showUnlockButton && isLevels && allowsPriceLevelExpansion) {
						quantityPickerType = `PRICE_LEVELS`;
						if (isAtQLimit) {
							disabled = true;
						}
					} else if (showZoomButton) {
						quantityPickerType = `ZOOM`;
					} else if (showCartAddButton) {
						if (localState === `QUEUED`) {
							quantityPickerType = `QUEUED`;
						}
						if (isAtQLimit) {
							disabled = true;
						}
					} else if (showUnlockButton) {
						quantityPickerType = `LOCKED`;
					} else if (isSoldOut || isClosed) {
						if (this.isString(optionalSaleRedirectUrl, 1)) {
							quantityPickerType = `EXTERNAL`;
							externalLink = optionalSaleRedirectUrl;
						} else {
							hideQuantityPicker = true;
						}
					} else if (isAvailableAtDoor) {
						hideQuantityPicker = true;
					} else if (quantityPickerType === `OPEN` && !showCartAddButton && localState !== `AT_LIMIT`) {
						// OPEN is the default, but add button flag needs to be true
						hideQuantityPicker = true;
					}

					if (hideQuantityPicker) {
						quantityPickerType = `HIDDEN`;
					}
					return { disabled, externalLink, isRemovable, quantityPickerType };
				},
			},

			navigateToOriginalGroupSale: {
				public: true,
				value(context, options = {}) {
					const { data, Config } = this;
					const { PRIMARY_DOMAIN } = Config;
					const { event, groupSaleAccessCode } = data;
					const { group } = event;
					const { id: groupId } = group;
					const { groupSaleId, groupSaleUrlParam } = options;
					let param = this.isString(groupSaleUrlParam, 1) ? groupSaleUrlParam : data.groupSaleUrlParam;
					const groupSaleIdParam = this.isString(groupSaleId, 1) ? groupSaleId : data.groupSaleId;

					param = `${PRIMARY_DOMAIN}${param}?groupId=${groupId}&gsId=${groupSaleIdParam}`;
					if (this.isString(groupSaleAccessCode, 1)) {
						param += `&ac=${groupSaleAccessCode}`;
					}
					window.location.href = param;
				},
			},

			/**
			 * Upgrades
			 */

			/**
			 * Creates the upgrades object based on the selected source Ids
			 * @param {Object} options - orderId and the sourceSaleOrderItemIds
			 */
			initializeUpgradeCart: {
				public: true,
				async: true,
				async value(context, { eventId, orderId, sourceEventId, sourceSaleOrderItemIds }) {
					const { data, Orders } = this;
					const { cart, user } = data;
					const { guestCheckoutDetails, products } = cart;
					const userId = guestCheckoutDetails?.id || user?.id;

					const _upgrades = await Orders.getExchangesByOrderId({ userId, orderId }); // Todo - cache this api call in Orders.js?
					const order = await Orders.getFanOwnedItemById({ id: orderId, fanId: userId, force: false }); // Yeah, it would be cool to have a cache on Tixr like DataModel in studio
					const isChildOrder = Orders.isChildOrder(order) && order.seasonDetails.seasonEventType === `CHILD`;
					const upgrades = isChildOrder // See: https://tixrinc.atlassian.net/browse/TXP-6133
						? _upgrades.data // Do not normalize upgrades for child orders
						: this.PriceLevels.normalizeUpgrades({ products, upgrades: _upgrades.data });

					if (this.isArray(sourceSaleOrderItemIds, 1)) {
						const selectedUpgrades = this.map(sourceSaleOrderItemIds, (id) => {
							const upgradeSelected = this.find(upgrades, (upgrade) => upgrade.sourceItem.saleOrderItemId === id);
							const upgradeEventSelected = this.find(upgradeSelected.targetEvents, { eventId });

							return {
								source: id,
								sourceItem: upgradeSelected.sourceItem,
								events: upgradeSelected.targetEvents,
								targetOptions: upgradeEventSelected.targetItems,
							};
						});

						if (isChildOrder) {
							this.disableOrderProtection();
						}

						this.assign(cart, {
							orderId,
							sourceEventId,
							upgrades: selectedUpgrades,
						});
					}

					this.reconcile();
				},
			},

			/**
			 * Get the events that are available for all selected upgrade/exchange sources
			 */
			getEventsForUpgradeSourceSelections: {
				public: true,
				async value(context, { isExchanges, orderId, sourceSaleOrderItemIds }) {
					const { data, Orders } = this;
					const { cart, user } = data;
					const userId = cart?.guestCheckoutDetails?.id || user?.id;
					let commonEvents = [];

					const exchanges = await Orders.getExchangesByOrderId({ userId, orderId }); // Todo - cache this api call in Orders.js?

					const selectedExchanges = this.filter(exchanges.data, (exchange) => {
						const selection = this.find(sourceSaleOrderItemIds, (id) => id === exchange.sourceItem.saleOrderItemId);
						return this.isString(selection, 1);
					});

					this.each(selectedExchanges, (exchange, index) => {
						const exchangeEvents = exchange.targetEvents;
						if (index === 0) {
							commonEvents = exchangeEvents;
						} else {
							commonEvents = this.filter(commonEvents, (event) => {
								return this.find(exchangeEvents, { eventId: event.eventId });
							});
						}
					});

					// if upgrades, only allow same event
					if (!isExchanges) {
						commonEvents = this.filter(commonEvents, (event) => {
							return event.eventId === exchanges.sourceEventId;
						});
					}

					return commonEvents;
				},
			},

			/**
			 * Tells the webview to exit the upgrades flow
			 */
			leaveUpgradesMobileWebview: {
				value() {
					const { data, App, System } = this;
					const { cart } = data;
					const { isNativeApp } = System.get();

					if (isNativeApp && cart.leftUpgradesWebview !== true) {
						cart.leftUpgradesWebview = true;
						App.callExternal({
							name: `onAppExitCompleted`,
						});
					}
				},
			},

			/**
			 * Clears the upgrades object, cart, and chart session
			 */
			clearUpgradeSources: {
				public: true,
				value() {
					const { ReservedSeating, data } = this;
					if (this.isObject(data.cart)) {
						data.cart.upgrades = null;
					}
					ReservedSeating.endSession(); // calling this now eliminates some UI flashing when going back to source selection from reserved upgrades
					this.reconcile();
				},
			},

			/**
			 * Clears the upgrade targetOptions
			 */
			clearUpgradeTargets: {
				public: true,
				value() {
					const { ReservedSeating, data } = this;

					this.each(data.cart.upgrades, (upgrade) => {
						upgrade.targetOptions = null;
					});

					ReservedSeating.endSession(); // calling this now eliminates some UI flashing when going back to source selection from reserved upgrades
					this.reconcile();
				},
			},

			/**
			 * Clears the flag for finding best available for upgrades
			 * This is used to know when to zoom in on the seating chart
			 */
			clearFindingBestForUpgradesFlag: {
				public: true,
				value() {
					this.data.cart.findingBestForUpgrades = false;
				},
			},

			/**
			 * Increments the product selection. Upgrade will be added through the sync process
			 */
			incrementUpgrade: {
				public: true,
				defaults: {
					id: null,
					priceLevelOptionId: null,
					productType: null,
					// addOn: null,
					sourceId: null,
				},
				value(context, options) {
					const { data } = this;
					const { id, priceLevelOptionId, productType } = options;
					const { cart } = data;

					const { products } = cart;
					const product = this.find(products, { id, productType });
					const { selections: productSelections } = product;
					const selections = this.clone(productSelections);
					const saleId = id; //productType === `SALE` ? id : options.saleId; // Todo - this is not needed, but may be needed in the future for holds
					const selection = { priceLevelOptionId, saleId };

					selections.push(selection);

					this.updateProducts({
						products: [{ id, productType, selections }],
					});
				},
			},

			/**
			 * Finds the next upgrade that needs a selection
			 */
			getNextUpgradeWithoutSelection: {
				// remove this for the one below or vice versa
				public: true,
				value(context) {
					const { cart } = this.data;
					const { upgrades } = cart;

					const index = this.getNextTargetIndex();

					if (index === -1) {
						return null;
					}

					return upgrades[index];
				},
			},

			/**
			 * Finds the index of the next upgrade that needs a selection
			 */
			getNextTargetIndex: {
				public: true,
				value(context, upgrades = this.data.cart.upgrades) {
					return upgrades.findIndex((upgrade, index) => {
						if (upgrade.releasingSeats) {
							return false;
						}

						if (upgrade.isReserved) {
							return !this.isString(upgrade.selection?.seatLabel, 1);
						}

						return !this.isObject(upgrade.targetSelected);
					});
				},
			},

			isUpgradesReleasingSeats: {
				value() {
					const { upgrades } = this.data.cart;
					const upgradeReleasingSeats = this.find(upgrades, ({ releasingSeats }) => releasingSeats === true);

					return this.isObject(upgradeReleasingSeats);
				},
			},

			/**
			 * Removes the upgrade selection and updates the products
			 */
			deselectUpgradeForSource: {
				public: true,
				value(context, sourceSaleOrderItemId) {
					const { cart } = this.data;
					const { upgrades } = cart;

					const upgrade = this.find(upgrades, (upgrade) => {
						return upgrade.source === sourceSaleOrderItemId;
					});

					if (this.isObject(upgrade?.selection)) {
						const product = this.find(cart.products, (product) => {
							return this.find(product.selections, (selection) => {
								return this.isEqual(selection, upgrade.selection);
							});
						});

						if (this.isObject(product)) {
							const { id, productType } = product;
							let selections = this.clone(product.selections);

							const selection = this.find(selections, (selection) => {
								return this.isEqual(selection, upgrade.selection);
							});

							if (this.isObject(selection)) {
								selections = this.without(selections, selection);
							}

							this.updateProducts({
								products: [{ id, productType, selections }],
							});
						}
					}

					upgrade.selection = null;
					upgrade.targetSelected = null;
					upgrade.isReserved = false;
					upgrade.releasingSeats = false;
				},
			},

			/**
			 * Sync the upgrades with the cart
			 */
			syncUpgradesWithSeatSelections: {
				value() {
					const { ReservedSeating } = this;
					const reservedSeating = ReservedSeating.get();
					const { seatingChartHoldId } = reservedSeating;
					let recalculate = false;

					if (this.checkRemovedUpgrades()) {
						// check to make sure I need to return
						return true; // recalculate
					}

					recalculate = this.findUpgradeForSeatSelection(); // can possibly set limits per sale by categories

					// findBestAvailableForUpgrade
					if (this.isString(seatingChartHoldId)) {
						const needsToReleaseSeats = this.reconcileUpgradesWithoutSeat();
						recalculate = recalculate || needsToReleaseSeats;
					}

					this.calculateUpgradesCheckoutAmount();

					return recalculate;
				},
			},

			/**
			 *	Calculate the checkout amount for upgrades. This must use price difference instead of price.
			 */
			calculateUpgradesCheckoutAmount: {
				value() {
					const { data, Events } = this;
					const { cart } = data;
					const { currency, upgrades } = cart;

					const subtotal = this.reduce(
						upgrades,
						(subtotal, upgrade) => {
							const { targetSelected } = upgrade;

							if (this.isObject(targetSelected)) {
								return subtotal + targetSelected.priceDifference;
							}

							return subtotal;
						},
						0
					);

					cart.upgradesCheckoutAmount = Events.formatPriceDifference({
						currency,
						freeLabel: `Free`,
						priceDifference: subtotal,
					});
				},
			},

			/**
			 * Checks to see if any upgrades have been removed from the cart and removes the upgrade as well
			 */
			checkRemovedUpgrades: {
				value() {
					const { data } = this;
					const { products, upgrades } = data.cart;
					let recalculate = false;
					let failedBestAvailable = false;

					const upgradeMissingSeat = this.find(upgrades, (upgrade) => {
						const seatSelectionForUpgrade = upgrade.selection;

						if (!this.isObject(seatSelectionForUpgrade)) {
							return false;
						} else {
							const { id: upgradeSelectionId } = seatSelectionForUpgrade;
							const productFound = this.find(products, (product) => {
								const matchingSelection = this.find(product.selections, ({ id }) => id === upgradeSelectionId);

								return matchingSelection !== undefined;
							});

							if (upgrade.failedBestAvailable) {
								failedBestAvailable = true;
								return true;
							}

							return !this.isObject(productFound);
						}
					});

					if (this.isObject(upgradeMissingSeat)) {
						if (failedBestAvailable) {
							this.removeProductSelections({ id: upgradeMissingSeat.selection.id });
						}

						upgradeMissingSeat.selection = null;
						upgradeMissingSeat.targetSelected = null;
						upgradeMissingSeat.failedBestAvailable = false;
						upgradeMissingSeat.isReserved = false;

						recalculate = true;
					}

					return recalculate;
				},
			},

			/**
			 * For any product selection without an upgrade, find the next upgrade and select it
			 */
			findUpgradeForSeatSelection: {
				value() {
					const { data, App } = this;
					const { cart } = data;
					const { products, upgrades } = cart;
					let recalculate = false;
					let seats = [];

					this.each(products, (product) => {
						this.each(product.selections, (selection) => {
							const { saleId, priceLevelOptionId } = selection;
							const { isLevels, isReserved } = product;

							if (selection.state === `REMOVING`) {
								return;
							}

							const existingUpgrade = this.find(upgrades, (upgrade) => {
								return this.isEqual(upgrade.selection, selection);
							});

							if (!this.isObject(existingUpgrade)) {
								let selectionFound = false;

								this.each(upgrades, (upgrade) => {
									if (selectionFound) {
										return;
									}

									if (this.isInt(upgrade.selection?.id) && upgrade.selection.id !== selection.id) {
										return;
									}

									const availableOpenUpgrade = !isReserved && !this.isObject(upgrade.selection);
									const availableReservedUpgrade = isReserved && !this.isString(upgrade.selection?.seatLabel);

									if (availableOpenUpgrade || availableReservedUpgrade) {
										const { targetOptions } = upgrade;
										const targetOption = this.find(targetOptions, (targetOption) => {
											const saleIdMatch = targetOption.saleId === saleId;
											const priceLevelPotentialMatch =
												!isLevels ||
												!this.isInt(priceLevelOptionId) ||
												targetOption.priceLevelOptionId === priceLevelOptionId;
											return saleIdMatch && priceLevelPotentialMatch;
										});

										if (this.isObject(targetOption)) {
											if (isLevels && !this.isInt(priceLevelOptionId)) {
												this.requestMissingPriceLevels();
											}

											upgrade.selection = selection;
											upgrade.targetSelected = targetOption;
											upgrade.isReserved = isReserved;
											selectionFound = true;
											recalculate = true;

											if (this.isString(upgrade.selection?.seatLabel)) {
												upgrade.releasingSeats = false;
											}
										}
									}
								});

								if (!selectionFound) {
									const { seatLabel, priceLevelOptionId: ticketType } = selection;
									seats.push({ seatLabel, ticketType });
									this.queueSelectionRemoval({
										productId: product.id,
										productType: product.productType,
										selection,
									});
								}
							}
						});
					});

					if (this.isArray(seats, 1)) {
						this.ReservedSeating.deselectSeats({ seats });

						App.notifyUser({
							type: `warning`,
							message: `No remaining upgrades found for selection${seats.length > 1 ? `s` : ``}.`,
						});
					}

					return recalculate;
				},
			},

			/**
			 * Reconciles upgrades without a seat selection. It will release any seats with the same category before finding the best available
			 */
			reconcileUpgradesWithoutSeat: {
				value() {
					const { data } = this;
					const { cart } = data;
					const { products, upgrades } = cart;

					if (data.findingBest) {
						// already in flight — the getBestAvailable then/fail callback will trigger
						// reconcile() once it resolves, so don't force synchronous re-recursion here
						return false;
					}

					// find an upgrade without a seat if one exists
					const upgradeWithoutSeatIndex = upgrades.findIndex((upgrade) => {
						if (!this.isObject(upgrade.targetSelected) || upgrade.selection?.state === `PENDING`) {
							return false;
						}

						const { saleId, tierId, priceLevelOptionId, seatLabel } = upgrade.selection;
						const productFound = this.find(products, (product) => {
							if (saleId !== product.id) {
								return false;
							}

							const tier = this.find(product.tiers, (tier) => {
								if (tierId !== tier.id) {
									return false;
								}

								if (priceLevelOptionId != null) {
									const priceLevelOption = this.find(tier.priceLevels, (priceLevel) => {
										return priceLevelOptionId === priceLevel.priceLevelOptionId;
									});

									return this.isObject(priceLevelOption);
								}

								return true;
							});

							return this.isObject(tier);
						});

						const lacksASeat =
							productFound?.isReserved && this.isObject(upgrade.selection) && !this.isString(seatLabel, 1);

						if (lacksASeat) {
							this.each(productFound.selections, (selection) => {
								if (selection.state !== `REMOVING`) {
									selection.state = `FBA`;
								}
							});
						}

						return lacksASeat && !upgrade.releasingSeats;
					});
					const upgradeWithoutSeat = upgrades[upgradeWithoutSeatIndex];

					if (this.isObject(upgradeWithoutSeat)) {
						const seatsToRelease = [];
						const { holdId, saleId, tierId } = upgradeWithoutSeat.targetSelected;

						// find seats of the same category to be released
						this.each(upgrades, (upgrade, index) => {
							if (upgradeWithoutSeatIndex !== index) {
								if (this.isObject(upgrade.selection)) {
									const {
										holdId: currentHoldId,
										saleId: currentSaleId,
										tierId: currentTierId,
										seatLabel: currentSeatlabel,
										priceLevelOptionId: currentTicketType,
									} = upgrade.selection;
									if (
										holdId == currentHoldId && // == comparison -> holdId can be null or undefined
										saleId === currentSaleId &&
										tierId === currentTierId &&
										this.isString(currentSeatlabel, 1)
									) {
										seatsToRelease.push({ seatLabel: currentSeatlabel, ticketType: currentTicketType });
										upgrade.releasingSeats = true;

										upgrade.selection.seatLabel = null;
										upgrade.selection.id = null;
									}
								}
							}
						});

						this.App.block(upgradesFindBestBlockOptions);

						if (this.isArray(seatsToRelease, 1) && !data.releaseSeatsBeforeGetBestAvailable) {
							upgradeWithoutSeat.selection.seatLabel = null;
							upgradeWithoutSeat.selection.id = null;
							data.releasingSeats = true;
							data.releaseSeatsBeforeGetBestAvailable = true; // avoiding calling deselectSeats more than once
							this.ReservedSeating.deselectSeats({ seats: seatsToRelease });
							return true; // recalculate
						}

						data.findingBest = true;
						data.releasingSeats = false;
						cart.findingBestForUpgrades = true; // cleared by clearFindingBestForUpgradesFlag(). Used to know when to zoom in on the seating chart
						data.releaseSeatsBeforeGetBestAvailable = false;

						this.getBestAvailable({
							then: (response) => {
								data.findingBest = false;

								this.defer({
									duration: 3000,
									then: () => {
										if (
											this.isObject(this.data.cart.upgrades[upgradeWithoutSeatIndex]?.targetSelected) &&
											!this.isString(this.data.cart.upgrades[upgradeWithoutSeatIndex]?.selection?.seatLabel) &&
											!this.data.cart.upgrades[upgradeWithoutSeatIndex].releasingSeats
										) {
											this.data.cart.upgrades[upgradeWithoutSeatIndex].failedBestAvailable = true;
											this.reconcile();
										}
									},
								});
							},
							fail: (reason) => {
								data.findingBest = false;
								upgradeWithoutSeat.failedBestAvailable = true;
								this.reconcile();
							},
						});
					}

					return false;
				},
			},

			setPersistentCheckout: {
				public: true,
				value(context) {
					const { Storage, data } = this;
					const { cart, enablePersistentCheckout, event } = data;
					const { expiration, token } = cart;

					if (enablePersistentCheckout) {
						const { mode: eventMode } = event;
						const { completed, token: cartToken } = cart;
						const hasToken = this.isString(cartToken, 1);
						const isFanPreview = eventMode === `FAN_PREVIEW`;

						if (!completed && hasToken && !isFanPreview) {
							Storage.set({ key: `cart_token`, value: token });
							Storage.set({ key: `cart_expiration`, value: expiration });
						}
					}
				},
			},

			clearPersistentCheckout: {
				public: true,
				value(context) {
					const { Storage } = this;

					// remove the token and expiration from local storage
					Storage.remove({ key: `cart_token` });
					Storage.remove({ key: `cart_expiration` });
				},
			},

			connectStripeTerminal: {
				public: true,
				async value() {
					const { data } = this;
					const { cart } = data;
					const { requirements, progress } = cart;

					if (this.StripeTerminal == null) {
						const [StripeTerminal] = await this.require({ deps: [`StripeTerminal`] });
						this.log({ message: `StripeTerminal: ${StripeTerminal}` });
						this.StripeTerminal = StripeTerminal;
					}

					try {
						cart.paymentTerminalStatus = `PENDING`;
						cart.paymentTerminalStatusMessage = `Connecting to payment terminal...`;
						this.reconcile();
						await this.initializeBackendClientAndTerminal();
					} catch (error) {
						cart.paymentTerminalStatus = `ERROR`;
						cart.paymentTerminalStatusMessage = `There was an error initializing the payment terminal. Please try again or enter a payment method manually.`;
						this.log({ message: `Error initializing backend client and terminal: ${error}` });
						requirements.payment_terminal = false;
						progress.payment_terminal = false;
						this.reconcile();
					}

					await this.selectReader();
				},
			},

			initializeBackendClientAndTerminal: {
				async: true,
				async value(url) {
					const { data } = this;
					data.terminal = await this.StripeTerminal.create({
						onFetchConnectionToken: () => {
							return this.XHR.ajax({
								type: `POST`,
								url: `/api/groups/${data.event.group.id}/stripe-terminal-connection-tokens`,
								REST: true,
								data: {},
								then: (response) => {
									return response.token;
								},
							});
						},

						onUnexpectedReaderDisconnect: () => {
							window.alert(`Unexpected disconnect from the reader`);
							data.connectionStatus = `not_connected`;
							data.reader = null;
							this.reconcile();
						},
						onConnectionStatusChange: (ev) => {
							data.connectionStatus = ev.status;
							data.reader = null;
							this.reconcile();
						},
					});
				},
			},

			selectReader: {
				async: true,
				async value() {
					const { data } = this;
					const { cart, event } = data;
					const { stripeTerminalReader } = cart;

					cart.paymentTerminalStatus = `PENDING`;
					cart.paymentTerminalStatusMessage = `Payment terminal connected. Please follow the prompts on the reader to complete the payment.`;
					this.reconcile();

					await this.XHR.ajax({
						type: `PUT`,
						REST: true,
						body: {
							readerId: stripeTerminalReader.id,
						},
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=send-to-reader`,
						then: (response) => {
							cart.paymentIntent = response.paymentIntent;
							cart.paymentTerminalStatus = `PENDING`;
							cart.paymentTerminalStatusMessage = `Payment sent to terminal. Please follow the prompts on the reader to complete the payment.`;

							//PING BE for payment response
							this.when({
								promise: this.pingForStripeTerminalOrderResult(),
								then: (confirmation) => {
									if (this.isObject(confirmation)) {
										this.onOrderComplete({ confirmation });
									}
								},
							});
						},
						fail: (error) => {
							cart.paymentTerminalStatus = `ERROR`;
							cart.paymentTerminalStatusMessage = `There was an error sending the payment to the terminal. Please try again or enter a payment method manually.`;
							this.log({ message: `Error sending payment to terminal: ${error}` });
						},
					});

					this.reconcile();
				},
			},

			cancelReader: {
				public: true,
				async: true,
				value(context) {
					const { data } = this;
					const { cart, event } = data;
					const { stripeTerminalReader } = cart;

					if (cart.paymentTerminalStatus === `CANCELED`) {
						return;
					}

					return this.XHR.ajax({
						type: `PUT`,
						REST: true,
						body: { readerId: stripeTerminalReader.id },
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=cancel-reader`,
						then: (response) => {
							cart.paymentTerminalStatus = `CANCELED`;
							cart.paymentTerminalStatusMessage = `Payment terminal transaction canceled. You may try again or enter a payment method manually.`;
							if (this.isFunction(data.onCancelPingForStripeTerminalOrderResult)) {
								data.onCancelPingForStripeTerminalOrderResult();
							}
							this.reconcile();
							return response.token;
						},
					});
				},
			},

			cancelReaderSendBeacon: {
				public: true,
				value(context) {
					const { data } = this;
					const { cart, event } = data;
					const { stripeTerminalReader } = cart;
					const nav = (typeof window !== `undefined` && window.navigator) || null;
					const bodyString = JSON.stringify({ readerId: stripeTerminalReader.id });
					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}?action=cancel-reader`;

					if (cart.paymentTerminalStatus === `CANCELED`) {
						return;
					}

					// Prefer sendBeacon when available (browser)
					try {
						if (typeof nav !== `undefined` && typeof nav.sendBeacon === `function`) {
							// sendBeacon accepts a USVString, no Blob required
							nav.sendBeacon(url, bodyString);
							return;
						}
					} catch (_) {
						this.cancelReader();
						return;
					}
				},
			},

			skipUserForBoxOffice: {
				public: true,
				value(context) {
					const { data } = this;
					const { cart } = data;
					cart.skipUserForBoxOffice = true;
					cart.requirements.representatives = false;

					this.reconcile();

					// CheckoutSteps will continue and automatically call selectReader if necessary
				},
			},

			setAgentCheckoutAutoSendPhone: {
				public: true,
				value(context, options = {}) {
					const { data } = this;
					const { cart } = data;
					const { phoneDisplay = null, phoneE164 = null } = options;

					cart.agentCheckoutAutoSendPhoneDisplay = phoneDisplay;
					cart.agentCheckoutAutoSendPhoneE164 = phoneE164;

					this.reconcile();
				},
			},

			/**
			 * Fan user id for agent checkout payment APIs (`/api/users/{id}/...`).
			 * Prefer POST …/guest-user (`User.getGuestUser()`), else Studio `set-guest-account`
			 * (`cart.guestCheckoutDetails.id`). Callers fall back to `cart.agentCheckoutFanId`
			 * (sales invoice fan) when this returns null.
			 */
			getAgentCheckoutPaymentUserId: {
				public: true,
				value(context, cart) {
					if (!this.isObject(cart) || cart.checkoutMode !== `AGENT`) {
						return null;
					}

					if (this.data.isUpgrades) {
						return this.data.user?.id ?? null;
					}

					const guestUser = this.User.getGuestUser();

					if (guestUser?.id != null) {
						return guestUser.id;
					}

					// Studio postMessage `set-guest-account` only populates guestCheckoutDetails
					return cart.guestCheckoutDetails?.id ?? null;
				},
			},

			/**
			 * Calculates which badges should be displayed for a product based on its state.
			 * @param {Object} options
			 * @param {Object} options.product - The product to calculate badges for
			 * @param {boolean} [options.preferResaleToQueued=true] - Whether to prefer resale over queued display
			 * @returns {Array} Array of badge objects with text, icon, classes, and action properties
			 */
			calculateProductBadges: {
				public: true,
				value(context, options) {
					const { Config } = this;
					const { DISPLAY_RESALE_STATES } = Config;
					const {
						/* allowHoldBadgeClick = false, */
						preferResaleToQueued = true,
						product,
					} = options;
					const { displayState, /* holdName, */ resaleState, optionalSaleRedirectUrl, countdownMessage } = product;
					const badges = [];

					// Hold badge
					// if (this.isString(holdName, 1)) {
					// 	const action = allowHoldBadgeClick ? `viewHold` : null;
					// 	badges.push({ classes: [`hold`], text: holdName, action });
					// }

					// Primary tag - only show on primary row when dual-state is active
					// if (!isResaleVariant && this.shouldRenderDualState(product)) { /* custom handling */ }

					// Resale badge
					if (resaleState === `AVAILABLE`) {
						if (DISPLAY_RESALE_STATES.includes(displayState)) {
							if (displayState !== `QUEUED` || preferResaleToQueued) {
								badges.push({ classes: [`resale`], icon: `resale`, text: `Resale`, action: `viewResaleTag` });
							}
						}
					}

					// State-based badges
					if (displayState === `QUEUED`) {
						badges.push({ icon: `time`, text: `Waitlist`, action: `viewWaitlistTag` });
					} else if (displayState === `SOLD_OUT`) {
						if (!this.isString(optionalSaleRedirectUrl)) {
							badges.push({ text: `Sold Out` });
						}
					} else if (displayState === `CLOSED`) {
						badges.push({ text: `Sale Over` });
					} else if (displayState === `LOCKED`) {
						badges.push({ icon: `lock-3`, text: `Locked` });
					} else if (displayState === `AVAIL_SOON`) {
						badges.push({ text: `Available Soon` });
					} else if (displayState === `AVAIL_AT_DOOR`) {
						badges.push({ text: `Sold at Door` });
					} else if (displayState === `OPEN`) {
						if (this.isString(countdownMessage, 1)) {
							badges.push({ text: countdownMessage });
						}
					}

					return badges;
				},
			},
		},
	});
});

//# sourceMappingURL=ShoppingCart.js.map